/**
 * The person (WORKBOARD E2): the layer-0 soldier at any k_human, as a stand-in against the
 * style sheet (design_sheets/style.html, §10 / §10.2 rules). Ravenfield-style: blocky, the whole
 * body in the team's unit palette (unitPalette.ts — body, dark, flash), a direct-fire handheld
 * (decision 3) held in both hands by two-bone IK at every aim pitch.
 *
 * Built in person units (a person is 1.8 tall, metre-ish proportions) and scaled to the world by
 * `height / 1.8`, so the same model serves the classic profile (1.1 u) and the human one
 * (0.055 u). Faces +Z, feet at y = 0.
 *
 * LOD (§10.2 rule 4): two tiers. `full` is the rigged, animated body; `silhouette` is four boxes
 * in the same three values, one draw call, static — for when a person is a few dozen pixels or
 * less. Below MARKER_PX the host draws a marker instead (the style sheet's 6 px floor).
 *
 * Rig: root (scale) → hips (bob, height) → legs (thigh → shin), torso (lean) → head, arms
 * (IkArm: shoulder → elbow), aim (pitch) → handheld (muzzle). Animation is pure presentation:
 * it reads a PersonAnim context the host fills from state; nothing here writes game state.
 *
 * Handedness (67.21): built left-handed — `armR` is the gun arm and it sits at +x, which is the figure's left (the
 * host's basis puts +x at n × heading) — and a right-handed person is the same model mirrored (`setPersonHand`: the
 * root's x scale negative). Everything held swaps sides with it; the crew anchors swap hands so the strong hand keeps
 * its job. Read `armR` / `armL` as the strong and the support arm.
 *
 * Design reference: design_sheets/standins.html
 */
import * as THREE from 'three';
import { copyModel, markTreeShared } from './modelCopy';
import { bakeStaticParts, markShared } from './meshBake';
import { solveArm, type IkArm } from './playerModel';
import type { UnitPalette } from './unitPalette';
import type { Stance, CoverPose, CoverHides, CoverSide, UnitRole } from '../types';
import { setLawExtended, setBipod, setGl40Open, setGl40Leaf, GL40, WEAPON_LOOK, RIFLE_BUTT, RIFLE_GRIP_L, RIFLE_GRIP_MIN, type Gl40Model, type LawModel, type GroundWeapon, type LongGun, type WeaponModel } from './groundWeapons';
import { buildGrenade, buildMedPouch, buildBandageRoll, setRollLeft, buildDressing, type GrenadeKind, type GrenadeModel } from './fieldKit';
import { BANDAGE, COVER_POSE_SIZE, ACTION_UNIT_RADIUS, PERSON_BODY_HEIGHT, GAIT as FOOT_GAIT, GROUND_WEAPONS, REAL_TIMING } from '../constants';
import { buildAntiDropshipMissile } from './antiDropshipMissileLooks';
import { buildPlayGun, buildPlayLaw, buildPlayGl40, isPartedGun, setGunParts, gunShotPose, gunBoltShotPose, gunReloadPose, kinOf, GUN_AT_REST, type Parted, type GunKind, type HandGunKind, type TubeKind } from './gunLooks';
import type { Hand } from './handedness';
import { dressArmour, armourColours, BALACLAVA, VEST_LOOKS, type HelmetKind, type VestLook, type ArmourWear } from './armourLooks';
import { buildSoldier, clothOf } from './soldierLooks';
import { farRole, type RoleDress } from './roleLooks';
import { personClipsOn, sampleGaitClips, sampleIdleClip, widenStride, clipMean, createClipPose, type PersonClipPose } from './personClips';
import { PERSON_CLIP_JOINTS, type PersonClipData } from './personClipsData';

/** A person in person units (the model's own space). */
export const PERSON_UNITS = 1.8;
/** Below this many pixels tall the host draws a marker (style sheet, §10.1). */
export const MARKER_PX = 6;
/** At or above this many pixels tall the full rig is worth drawing; between the two, the silhouette. */
export const SILHOUETTE_BELOW_PX = 24;

export type PersonTier = 'full' | 'silhouette' | 'marker';
/** 67.16: the far tier's poses (66.7 (a): `duck`, hunched down behind low cover). */
export type SilhouettePose = 'stand' | 'runA' | 'runB' | 'crouch' | 'prone' | 'duck' | 'sit' | 'ride';
/** 'operate': flying a drone (ruling 25: a drone is launched from a person and taken like a seat) — rifle slung, hands on the controller. */
export type PersonState = 'idle' | 'run' | 'airborne' | 'landing' | 'seated' | 'operate';

export interface PersonModel {
  root: THREE.Group;          // scaled to world height; position / orient this
  full: THREE.Group;          // the rigged tier
  silhouette: THREE.Group;    // the far tier: one pose shown at a time, each one mesh after bake
  /** 67.16: the far tier's poses (stand, a two-frame run, crouch, prone); the animation shows the one that matches. */
  silhouettePoses: Record<SilhouettePose, THREE.Group>;
  silhouettePose: SilhouettePose;
  hips: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  thighL: THREE.Group; shinL: THREE.Group; footL: THREE.Group;
  thighR: THREE.Group; shinR: THREE.Group; footR: THREE.Group;
  armL: IkArm; armR: IkArm;
  aim: THREE.Group;           // pitches with the aim; the handheld rides it
  handheld: THREE.Group;
  muzzle: THREE.Object3D;     // where a tracer starts (world position via getWorldPosition)
  gripR: THREE.Object3D;      // right hand target (in aim space)
  gripL: THREE.Object3D;      // left hand target (in aim space)
  controller: THREE.Group;    // the drone controller, shown only while operating (torso space)
  /** 65.5: both weapons; the one in hand rides `aim`, the other waits at the sling (rifle, on the back) or the holster (pistol, right hip) */
  weapons: { rifle: WeaponModel; pistol: WeaponModel; rocket?: LawModel; gl40?: Gl40Model };
  /** 65.31: where the 40 mm rides when it is not in hand — slung across the back, crossing the rifle's sling (`PersonAnim.gl40`) */
  gl40Home: THREE.Group;
  /** S7: where the LAW rides when it is not in hand (strapped upright to the pack's left side), and the spare there (`PersonAnim.lawSpare`) */
  lawHome: THREE.Group;
  sling: THREE.Object3D;      // where the slung rifle hangs (torso space)
  holster: THREE.Object3D;    // where the holstered pistol sits (hips space)
  ctrlR: THREE.Object3D; ctrlL: THREE.Object3D;
  tier: PersonTier;
  /** 67.21: the strong hand (right = the model mirrored) */
  hand: Hand;
  /** 65.17 (b): what the model wears beyond the palette ('' = nothing: today's look) — the far tier's crowd keys its template on it, so an armored figure never draws from a bare one */
  kitKey: string;
  /** 65.12: which long gun rides in the rifle's place (`weapons.rifle`) — 65.43: its kin, for a classic (the fit, the pose) */
  longGun: LongGun;
  /** 65.43: the gun in each slot as drawn (a classic itself, not its kin): its moving parts pose by it (poseGunParts) */
  gunKinds: { rifle: HandGunKind; pistol: HandGunKind; rocket: TubeKind };
  /**
   * 65.12: the field kit's moving parts — the roll in the strong hand and the dressing on the support forearm (a
   * bandage), a grenade in the strong hand (a throw). Built (and baked) the first time they show, so a figure that
   * never bandages or throws keeps today's tree.
   */
  kit: { palette: UnitPalette; roll?: THREE.Group; dressing?: THREE.Group; inHand: Partial<Record<GrenadeKind, GrenadeModel>>; lawSpare?: THREE.Group; tool?: EntrenchingTool };
}

/** What a person is built with beyond the palette and the height. Empty = a right-handed person with today's kit. */
export interface PersonKit {
  /** 67.21: the strong hand (absent = right; the setting and the seed choose it in the game — rendering/handedness.ts) */
  hand?: Hand;
  /** 65.12: the long gun in the rifle's place (absent = the rifle) */
  longGun?: LongGun;
  /** 65.43: a classic long gun in the rifle's place (the Garand, the Springfield, the Thompson, the BAR), fitted as its kin (`longGun` follows it) */
  primary?: HandGunKind;
  /** 65.43: the sidearm in the pistol's place (absent = the pistol; the M1911) */
  sidearm?: 'pistol' | 'm1911';
  /** 65.12: grenades carried in pouches on the belt's front — HE on the support side, smoke on the strong side (two of each at most are drawn) */
  grenades?: { he?: number; smoke?: number };
  /** 65.12: the bandage's pouch on the belt, behind the support hip */
  medkit?: boolean;
  /** 67.56: the vest drawn (absent = none): the plate carrier (armourLooks.ts), light, or heavy (a throat guard, side plates,
   * a groin flap, deltoid pads). The far tier reads it too (heavy wider). */
  armor?: Armor;
  /** 67.56: the helmet drawn — `light` (absent: every figure in a match without armour, and the sim's issued helmet),
   * `heavy` (lane C's heavy helmet, once the sim has one), or `none`: a balaclava (armour in the match, no helmet issued).
   * The host picks it with armourLooks.ts `drawnHelmet`. */
  helmet?: HelmetKind;
  /** 67.56: the armour's look (absent = B, the plate carrier; D, flak, is the owner's kept option, drawn on the sheet). */
  armourLook?: VestLook;
  /** 67.56: the armour's wear (absent = whole): worn below half its life, broken when spent. Not wired to play yet. */
  wear?: ArmourWear;
  /** 68.8 (owner, Oct 10: "we should have squad leader models probably"): the squad leader's look — a role, so any kit
   * can lead: a radio on the pack with a whip antenna on the support side, a data slate on the chest rig, a handset on the
   * strap, a binocular case at the hip (leaderLooks.ts); 67.57: the leader's variant of the helmet worn (a flash band, a
   * boom mic, a rail antenna, rank bars) in place of the old stripe and chevron. The far tier keeps the antenna and adds
   * the band, so a leader reads at a few pixels. */
  leader?: boolean;
  /** 198: the base soldier alone, without the plate carrier every figure wears over it (the sheets show it; play never asks). */
  plain?: boolean;
  /** 201 (8): the medic's or the engineer's kit over the carrier (roleLooks.ts; absent: the line rifleman; PlayerState.role) — on
   * the soldier's body only (the flak look, D, has none); the far tier wears a few boxes of it. */
  role?: UnitRole;
  /** 201 (8): false — the engineer as the owner picked it (A), without the game's kit over it (the sheet's before). */
  roleKit?: false;
}

/** 65.17 (b): the armor tiers drawn (the kit's numbers are 65.17 (a)'s). */
export type Armor = 'none' | 'light' | 'heavy';

/**
 * 66.7 (a): the pose for holding a cover point (types CoverPoint) against a threat — what the cover hides from it and
 * the side the person fires from: over a low cover, `low`; round an edge, `edge` where it hides a crouched body (a
 * kneel behind it) and `corner` where it hides a standing one; a rise or a lip that hides only a body lying down,
 * `crest`. Null where it hides nothing. The host adds `out` (peeking) and, for `low`, the cover's height.
 */
export function coverPoseFor(hides: CoverHides, side: CoverSide): { pose: CoverPose; side?: 1 | -1 } | null {
  if (!hides) return null;
  if (hides === 'prone') return { pose: 'crest' };
  if (side === 'over') return { pose: 'low' };
  return { pose: hides === 'stand' ? 'corner' : 'edge', side: side === 'right' ? 1 : -1 };
}

/** 67.21: make `m` right- or left-handed — the model mirrored across its centreline, or not. Cheap: a flip of the root's x scale. */
export function setPersonHand(m: PersonModel, hand: Hand): void {
  m.hand = hand;
  const s = Math.abs(m.root.scale.x);
  m.root.scale.x = hand === 'right' ? -s : s;
}

export interface PersonAnim {
  state: PersonState;
  time: number;
  /** Ground speed in person units per second (a run is ~4–6); drives the stride. */
  speed: number;
  /** Aim pitch in radians, positive up. */
  aimPitch: number;
  /** true: weapon up at the aim; false: low ready (the idle carry). */
  aiming: boolean;
  /** time of the last shot, for the recoil kick (−Infinity = none) */
  firedAt: number;
  /** time the landing began */
  landedAt: number;
  /** 67.60, the jump's look: the body's speed up along the ground's normal while airborne (person units a second; the view's, from the sim's velocity). */
  airUp?: number;
  /** 67.60: a jump with its own feel (63.15: the sim's take-off height kept) — the push off the ground, the tuck, the reach (JUMP_LOOK); absent, a hop or a blast: the tuck as before. */
  jumpFeel?: boolean;
  /** 67.60: how fast the body came down at its landing (person units a second): the landing's give. */
  landSpeed?: number;
  /**
   * 67.60: the jump under way (presentation state): when it left the ground, how far it has risen (integrated), its take-off
   * speed, the drawn body's lag behind the sim's as the push ends, and the legs' angles then (thigh, knee, thigh, knee).
   */
  air?: { at: number; rise: number; v0: number; lag: number; legs: [number, number, number, number] };
  /** 67.60: how far the body is over the ground it took off from (person units; the view's, airborne). Absent: integrated from `airUp`. */
  airRise?: number;
  /**
   * Crewing (rendering/crewCycle.ts): scene anchors the hands reach for — a handwheel, a round,
   * binoculars. While `hands` is set (even with both empty) the weapon is slung; a hand without an
   * anchor rests at the side.
   */
  hands?: { right?: THREE.Object3D | null; left?: THREE.Object3D | null } | null;
  /**
   * Seated: in a chair (an emplacement's seat, a driver's, a pilot's; absent), astride a saddle (the motorcycle: hips on the
   * seat, boots on the pegs, leaning to the bars), or on the ground (65.22: behind a low gun — the heavy machine gun's tripod —
   * hips a hand off the ground, legs out in front, leaning in to the grips). 67.63: `bench`, a rider's seat (a bay's, a
   * container's: upright, a little forward, the hands on the thighs); `hatch`, standing in a hatch on its floor (its head and
   * shoulders out at the rim, the hands on the grips or the rim); `drive`, low in a cab's seat (the anchor on the cab's floor,
   * the hips a hand and a half over it, the legs reaching forward to the pedals, leaning back a little); `pilot`, a cockpit's seat
   * (the chair's height, the feet flat just ahead of the seat's front, the knees up over them: a short reach where a cockpit's
   * nose is close). A seated hand with no anchor rests on the thigh.
   */
  seat?: 'chair' | 'saddle' | 'ground' | 'bench' | 'hatch' | 'drive' | 'pilot';
  /** 0–1: a stoop with bent knees (lifting from low, leaning into a wheel). */
  bend?: number;
  /** 0–1: flinching from a shot (leaning back, head ducked). */
  flinch?: number;
  /** The gait's phase, in strides, and the speed it is eased to (integrated over time by the walk; presentation state). */
  gaitPhase?: number;
  gaitSpeed?: number;
  /** DECISIONS 401: the clip layer's share (0 … 1, eased in and out with the walk) and the stance it last sampled (presentation state). */
  clipW?: number;
  clipStance?: 'stand' | 'crouch';
  /** DECISIONS 410 (3): the idle clip's share (eased) and the stance it plays */
  idleW?: number;
  idleStance?: 'stand' | 'crouch';
  /** 410 (5): the hips' height eased (the gun's steadying against the clip's bob) */
  hipEased?: number;
  /** Decision 65: crouched (a kneel when still, a low walk when moving) or prone (lying, a crawl). Absent = standing. */
  stance?: Stance;
  /** The ground's slope under the figure: rise per unit along its right (+x) and its front (+z). Absent = flat. The feet follow it; a prone body lies along it. */
  slope?: { x: number; z: number };
  /**
   * 67.60, feet on the ground: the drawn ground under the figure — its height (person units) over the figure's base at
   * (x, z) in the holder's frame (+x as the slope's, +z its front). Given (the view's, at the full tier), each planted foot
   * stands on the ground under it rather than on the slope's plane, and the hips come down to the lower one.
   */
  ground?: (x: number, z: number) => number;
  /** 67.60: the back turned to the cover, 0 … 1 (COVER_TURN.back; eased; presentation state). */
  coverBack?: number;
  /** 67.60: the whole body's slide for its turn about the neck with the back to the cover (the model's frame), last frame's: the feet's ground (presentation state). */
  coverShift?: { x: number; z: number };
  /** 67.60: how far the hips are down for the feet on uneven ground, eased (FEET_LOOK; presentation state). */
  feetDrop?: number;
  /**
   * 67.60: the stance drawn (STANCE_LOOK; presentation state) — what the body is going to (a stance, `cover:<pose>`, `dig`),
   * the step under way to it (from, to, when, how long; `via`: through the kneel, on to the next step early), the joints as
   * they were drawn when it began, and whether last frame was blended (its rotations are cleared before the pose).
   */
  stanceShown?: string;
  stanceStep?: { from: string; to: string; at: number; dur: number; via: boolean };
  stanceSnap?: Float64Array;
  stanceBlended?: boolean;
  /** 67.60: the crawl's muzzle lift, eased in and out (CRAWL_LOOK.muzzle; presentation state). */
  crawlLift?: number;
  /** 65.5: the weapon in hand (absent = rifle). The other waits slung or holstered. */
  weapon?: GroundWeapon;
  /** 65.31: this person carries the 40 mm (slung across the back when not in hand) */
  gl40?: boolean;
  /**
   * 63.8: the gait on foot (PlayerState.gait; absent = the shooting walk): a **move** — the gun carried low across the body,
   * the head up, its fire from the hip — or a **sprint** — the gun across the chest, leaning in, the knees high, the pack
   * bouncing, no fire. Standing only; eased in and out (GAIT_LOOK), so leaving one brings the gun back up to the aim.
   */
  gait?: 'move' | 'sprint';
  /** 63.8: the move's and the sprint's shares, 0 … 1 (eased by the animation; presentation state). */
  gaitMove?: number; gaitSprint?: number;
  /** 66.8: digging in — the dig began at this time (absent: not digging): kneeling, the entrenching tool's cycle (DIG_CYCLE) */
  digAt?: number;
  /** 66.8: the entrenching tool carried folded on the pack (a person who can dig in) */
  tool?: boolean;
  /** S7: a spare LAW strapped to the pack (the sim's reserve); the one in hand is the weapon `rocket` */
  lawSpare?: boolean;
  /** S7: whether the LAW (in hand, or on the pack when it is not) is loaded — false once fired with no throwaway to follow (the last tube: dropped at once, the hands empty). Absent = loaded. */
  lawLoaded?: boolean;
  /** 65.5: a reload began at this time (−Infinity / absent = none); it lasts WEAPON_LOOK[weapon].reload unless `reloadFor` says otherwise */
  reloadAt?: number;
  reloadFor?: number;
  /** 407: the player's own figure with the camera at its eye (drawn only in the viewmodel's pass): the reload lifted to the eye (EYE_RELOAD); 73.9 step 3: the LAW moved aside (EYE_LAW), the throw re-posed (EYE_THROW) */
  eye?: boolean;
  /** 73.9 step 3: written by the pose each frame — what the hands are busy with (absent: the gun): the viewmodel looks at it */
  handsOn?: 'throw' | 'bandage' | 'dig' | 'slate';
  /** 65.7: a bandage being applied — when it began and how long it takes (BANDAGE.seconds when absent). */
  healAt?: number;
  healFor?: number;
  /** 65.5: a switch to `weapon` began at this time: the draw (the previous weapon goes to its sling / holster while this one comes up) */
  drawAt?: number;
  /** 65.12: a dressing on the support forearm (a bandage finished); while a bandage runs it grows there anyway */
  dressed?: boolean;
  /** 65.12: a grenade throw began at this time (THROW_LOOK.seconds long; the weapon slung meanwhile), of this kind (absent = HE) */
  throwAt?: number;
  throwKind?: GrenadeKind;
  /**
   * 66.7 (a): in cover (the host's choice; held while still, a walk leaves it): the pose held behind it, whether the
   * person is `out` — up over it or leaning round it, aiming — or down behind it, the edge leaned round (`side`: +1 the
   * holder's right, −1 their left; 'edge' and 'corner'), and the cover's height in person units ('low': the peek rises
   * until the eyes clear it). The model eases between down and out (COVER_LOOK). Absent or null = not in cover.
   * `gap` (person units): how far ahead of the spot the cover's face is — given, the pose is pressed into it (PRESS).
   * In play the host gives it, and `height`, from the prop the unit stands against (game/takeCover.ts coverFace, toward the
   * prop at the pose's chest; `height` is then the cover's drawn top).
   */
  cover?: { pose: CoverPose; out: boolean; side?: 1 | -1; height?: number; gap?: number } | null;
  /** 66.7 (a): the peek's progress, 0 down … 1 out (eased by the animation; presentation state). */
  coverPeek?: number;
  /** 66.10: a shot's hold on the gun in cover, 0 … 1 (eased by the animation, COVER_FIRE; presentation state). */
  coverFire?: number;
  /** 66.7 (a), pressed: the lean in over a cover whose face slants back from its foot (radians; eased by the press; presentation state). */
  coverLeanIn?: number;
  /**
   * 67.60, the cover aim (the owner: "some the gun is facing the wrong direction, 360 as I frequently do the oblique cover
   * thing"): the way to the cover held, from the body's front (radians, + toward its left; the view's, from the host's face).
   * Given, the cover pose is built facing it, whatever the look (COVER_TURN). Absent: facing the look, as before.
   */
  coverBearing?: number;
  /**
   * 65.28: suppressed — the seconds its mark has left (PlayerState.suppressed; 0 / absent = not). Each near round renews the
   * mark, so a rise is a round cracking past: a beat (SUPPRESS_LOOK). `suppressedWas` / `suppressedAt` are the animation's.
   */
  suppressed?: number;
  suppressedWas?: number;
  suppressedAt?: number;
}

export function createPersonAnim(): PersonAnim {
  return { state: 'idle', time: 0, speed: 0, aimPitch: 0, aiming: false, firedAt: -Infinity, landedAt: -Infinity };
}

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
/** A box `thick` square from `a` to `b` (person units) — a limb or a weapon drawn as one bar (the far tier's poses). */
function bar(parent: THREE.Object3D, a: [number, number, number], b: [number, number, number], thick: number, color: string): THREE.Mesh {
  const va = new THREE.Vector3(...a), vb = new THREE.Vector3(...b), d = vb.clone().sub(va);
  const m = new THREE.Mesh(new THREE.BoxGeometry(thick, d.length() + thick * 0.5, thick), mat(color));
  m.position.copy(va).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  parent.add(m);
  return m;
}
/** 67.16: gloves in a neutral leather, apart from the team's three values, so the hands read as hands. */
const GLOVE = '#5b4d40';
function group(parent: THREE.Object3D, x: number, y: number, z: number): THREE.Group {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  parent.add(g);
  return g;
}

// Proportions (person units)
const HIP_Y = 0.92;
/**
 * Prone: the body pitched a little short of flat (the chest propped on the elbows), the hips' height
 * off the ground, the legs eased flat. Kneel: the hips' height, the planted leg's thigh and shin, the
 * kneeling shin folded a little up behind (the knee the lowest point). Tuned so nothing sinks more than
 * a few hundredths of a person-unit into the ground; the measured sizes are constants STANCE_SIZE.
 */
const PRONE = { pitch: Math.PI / 2 - 0.18, hipY: 0.37, legs: 0.06 };
const KNEEL = { hipY: 0.54, thighL: 1.35, kneeR: 1.8 };
const THIGH = 0.44, SHIN = 0.42;
/**
 * 66.7 (a): the cover poses (person units; a person is 1.8), each down behind the cover and out of it; the peek eases
 * between the two over COVER_LOOK's seconds. Leaning round an edge happens in the model's own frame (+x the strong side).
 * - low: down, sitting back on the strong heel, hunched over the planted knee (the top under the lowest cover kit);
 *   out, the hips rise until the eyes clear the cover by `clear` — the gun's bore sits a quarter of a person-unit
 *   under the eyes, so it clears too (`eyeOverHip`: the eyes over the hips, upright) — kneeling while that is low
 *   enough, up on both feet above it.
 * - edge: kneeling, tucked a little away from the edge; out, the hips slide to it and the torso rolls round it —
 *   further on the weak side, where the gun at the other shoulder has to clear the edge too (a right hand leaning left
 *   shows more of itself than leaning right, as it would).
 * - corner: standing, the knees soft; out, as the edge.
 * - crest: prone, slid back below the crest, the face down; out, up to it, the chest propped higher.
 * Down, the gun is held across the body, its muzzle toward the weak side (pointed ahead it would go through the cover):
 * level behind low cover, up at a rock or a corner (`across`: the turn, and the pivot brought out in front of the chest).
 */
const COVER = {
  low: { downHip: 0.31, downLean: 0.95, upLean: 0.06, clear: COVER_POSE_SIZE.low.clear, eyeOverHip: 0.72, foot: 0.3, head: 0, rest: 0 },
  edge: { hip: 0.52, downLean: 0.22, upLean: 0.08, tuck: -0.08, slide: 0.24, weakSlide: 0.39, roll: 0.46, weakRoll: 0.6, rest: -0.75 },
  corner: { hip: 0.88, downLean: 0.04, upLean: 0.04, tuck: -0.06, slide: 0.24, weakSlide: 0.38, roll: 0.45, weakRoll: 0.6, rest: -0.9 },
  crest: { back: -0.32, downPitch: Math.PI / 2 - 0.18, upPitch: Math.PI / 2 - 0.3, downHip: 0.37, upHip: 0.42, face: 0.5, rest: -0.12 },
  across: { yaw: 1.2, up: -0.08, out: 0.2 },
};
/**
 * 66.7 (a), pressed into the cover (the owner, Oct 11: *"the poses themselves behind the cover are bad, user just stands
 * sort of behind it and doesn't lean into it at all. that could be really hard to get right with procedural cover"*).
 * Given where the cover's face is (PersonAnim.cover `gap`: from the person's spot to the face along their facing) and
 * its height, each pose leans into it, and the body slides along its facing until its front touches the face (`margin`
 * short). The front is measured on the posed rig's key points (`pressFront`), so any cover, a rock, a trunk or a wall,
 * at any size, reads as leaned into:
 * - low: down, kneeling and sitting back, the torso leaning onto the cover until the head is under its top. Over low
 *   sandbags it hunches; at a chest-high wall it is nearly upright, the chest on the wall. The gun is held low along the
 *   cover. Out, the body rises until the eyes clear the top by `clear` and leans over it (more over lower cover): only the
 *   parts under the top are kept behind the face, so the chest and forearms come over it.
 * - edge (kneeling) and corner (standing): down, the body turned toward the edge (`yaw`) so the shoulder and hip away
 *   from it lead into the cover and press on its face, the gun low along it, the muzzle away from the edge. At an edge
 *   lower than the kneel's head, the body sits back and leans until the head is under the top. Out, the peek leans out
 *   from that pressed base: the turn unwinds toward the threat while the torso rolls round the edge (today's slide and
 *   roll), the hips staying on the face.
 * - crest: prone, the head at the lip, the face down; out, the head over it by `over`.
 * Without a `gap` (a host that does not know the face) the poses are as before.
 */
const PRESS = {
  margin: 0.02,
  /** the most the body slides forward to reach a face (beyond that, it stays short of it) */
  reach: 0.9,
  /** round an edge, how far to the side leaned to the cover's edge is from the spot (the body's points past it are beside the cover) */
  edgeAt: 0.42,
  /** over low cover, what is within this of its top rests on it (the forearms out over it) */
  rest: 0.06,
  /** the walk's contact: a prop's foot is at least this far ahead of the spot (a body's walk radius, person units) */
  foot: (ACTION_UNIT_RADIUS / PERSON_BODY_HEIGHT) * 1.8,   // (a person is 1.8 person units)
  /** round an edge or a corner, the lean in over a face that slants back from its foot: how fast it follows (radians a
   *  person unit of room), and the most */
  leanIn: { gain: 1.2, max: 0.6 },
  low: { topMargin: 0.06, topOverHip: 0.95, minLean: 0.2, maxLean: 1.05, foot: 0.24, head: 0.25, overLean: [0.5, 0.28] as const, overLook: 0.85, clear: 0.42 },
  edge: { yaw: 0.5, outYaw: 0.12, lean: 0.18, outLean: 0.1, topMargin: 0.06, sitHip: 0.36, tuck: -0.1 },
  corner: { yaw: 0.55, outYaw: 0.12, lean: 0.24, outLean: 0.05, tuck: -0.12, hip: 0.8, look: 0.8 },
  crest: { over: 0.12 },
  /** down, the gun held low along the cover: its pitch, the pivot dropped and kept in, its turn across the body */
  gun: { lowRest: -0.25, edgeRest: -0.55, crestRest: -0.1, up: -0.12, out: -0.14, yaw: 1.3 },
} as const;
/**
 * 66.7 (a), pressed: each bone's own meshes' corners in its frame (their distinct vertices: a box's eight, the helmet's
 * few), gathered once per model — what a cover's face meets. The weapon is held clear of it by its pose (PRESS.gun), so
 * it is not among them.
 */
const pressBoxes = new WeakMap<PersonModel, Array<{ chain: THREE.Object3D[]; corners: THREE.Vector3[] }>>();
function pressBoxesOf(m: PersonModel): Array<{ chain: THREE.Object3D[]; corners: THREE.Vector3[] }> {
  let out = pressBoxes.get(m);
  if (out) return out;
  out = [];
  const chains: THREE.Object3D[][] = [[m.torso], [m.torso, m.head], [m.thighL], [m.thighL, m.shinL], [m.thighL, m.shinL, m.footL], [m.thighR], [m.thighR, m.shinR], [m.thighR, m.shinR, m.footR],
    [m.torso, m.armL.group], [m.torso, m.armL.group, m.armL.fore], [m.torso, m.armR.group], [m.torso, m.armR.group, m.armR.fore]];
  for (const chain of chains) {
    const bone = chain[chain.length - 1], seen = new Set<string>(), corners: THREE.Vector3[] = [], v = new THREE.Vector3();
    for (const c of bone.children) {
      const me = c as THREE.Mesh; if (!me.isMesh) continue;
      me.updateMatrix();
      const pos = me.geometry.getAttribute('position');
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(me.matrix);
        const key = `${v.x.toFixed(3)},${v.y.toFixed(3)},${v.z.toFixed(3)}`;
        if (!seen.has(key)) { seen.add(key); corners.push(v.clone()); }
      }
    }
    if (corners.length) out.push({ chain, corners });
  }
  pressBoxes.set(m, out);
  return out;
}
const _pm = new THREE.Matrix4(), _pv = new THREE.Vector3();
/**
 * 66.7 (a), pressed: how much room the body has ahead before it meets the cover's face — the least, over the key corners,
 * of the face's distance at the corner's height less the corner's own (+z, the hips' parent frame: person units from the
 * spot) — over all of them, the legs' and the upper body's. The face (`face`) stands at `foot` at the ground and at `gap`
 * from `chest` up (a boulder bulges at its foot: its face slants back between), a plane where they are equal (a wall, a
 * trunk). What is higher than `below` is over the cover (Infinity: none), the head alone counts with `headOnly`, round an
 * edge (`edge`: the model's x of the edge leaned round) what is past it is beside the cover, and the arms count unless `arms`
 * is false.
 */
function pressRoom(m: PersonModel, face: { foot: number; gap: number; chest: number }, below: number, headOnly = false, edge = 0, arms = true): { all: number; legs: number; upper: number } {
  m.hips.updateMatrix();
  const out = { all: Infinity, legs: Infinity, upper: Infinity };
  for (const { chain, corners } of pressBoxesOf(m)) {
    if (headOnly && chain[chain.length - 1] !== m.head) continue;
    if (!arms && (chain.includes(m.armL.group) || chain.includes(m.armR.group))) continue;
    const leg = chain[0] === m.thighL || chain[0] === m.thighR;
    _pm.copy(m.hips.matrix);
    for (const o of chain) { o.updateMatrix(); _pm.multiply(o.matrix); }
    for (const c of corners) {
      _pv.copy(c).applyMatrix4(_pm);
      if (!(_pv.y < below) || (edge > 0 && _pv.x > edge) || (edge < 0 && _pv.x < edge)) continue;
      const room = face.foot + (face.gap - face.foot) * Math.max(0, Math.min(1, _pv.y / face.chest)) - _pv.z;
      if (room < out.all) out.all = room;
      if (leg) { if (room < out.legs) out.legs = room; } else if (room < out.upper) out.upper = room;
    }
  }
  return out;
}
/** The eyes' height over the hips with the torso leaning `lean` forward and the head pitched `head` (both radians). Pure. */
function eyeOverHips(lean: number, head: number): number {
  const ty = 0.62 + 0.11 * Math.cos(head) - 0.15 * Math.sin(head), tz = 0.11 * Math.sin(head) + 0.15 * Math.cos(head);
  return ty * Math.cos(lean) - tz * Math.sin(lean);
}
/**
 * 67.60's second pass: the low gun's turn in cover (the rig's frame) from the hold it was given (`yaw`) and the chest's own turn
 * (`chest`): within COVER_TURN.across of the chest's front it is as given; past it (on one side the bladed chest, turned toward
 * the edge, put the given hold behind it) it is turned forward to that limit, its muzzle lowered `downPitch` a radian turned
 * (`drop`) so it reaches no further ahead. Off (`chestHold` false), as given. Pure.
 */
export function coverHeld(yaw: number, chest: number): { yaw: number; drop: number; lift: number } {
  if (!COVER_TURN.chestHold) return { yaw, drop: 0, lift: 0 };
  const rel = Math.atan2(Math.sin(yaw - chest), Math.cos(yaw - chest)), L = COVER_TURN.across;
  if (Math.abs(rel) <= L) return { yaw, drop: 0, lift: 0 };
  return { yaw: chest + Math.sign(rel) * COVER_TURN.lowReady, drop: -COVER_TURN.downPitch, lift: COVER_TURN.lowLift };
}
/** 66.7 (a): seconds to come out of cover (up over it or round it) and to drop back behind it. */
export const COVER_LOOK = { out: 0.3, back: 0.22 };
/**
 * 67.60 (the owner: "limiting vertical high frequency oscillation in the model when walking on messy terrain"): the hips come
 * down so a planted foot reaches lower ground — taken whole the frame a foot planted, that drop jumped at every footfall on
 * uneven ground (the hips' own shake, the base hardly any). Eased now over `drop` seconds, down and up alike.
 */
export const FEET_LOOK = { drop: 0.09 };
/**
 * 67.60, the cover aim: in cover the body turns to face the cover (PersonAnim.coverBearing) — the press, the lean round its
 * edge and the gun held along it are the cover's whatever the look; with the gun up (out, or a shot's hold: COVER_FIRE) the
 * torso turns back to the look, at most `torso` (radians; prone at a crest none: the whole body turns), past which the base
 * turns toward the look too; the press lets go of the face as the base turns off it (none past `pressOff`: the cosine of the
 * angle off the cover), from `pressOn`. 67.60's second pass (the owner, on play: "when aiming behind body torso rotates off legs
 * … would be cool to transition to back against cover when facing the opposite direction rather than twisting off"): `torso`
 * 0.6 (with the rifle's blade the chest stays within ~65° of the hips); the look more than `back.on` off the cover's way (less
 * than `back.off` to leave; between, as it was) turns the body round over `back.seconds` — its back to the cover, eased back
 * onto it (the face less `back.pack`, the pack's depth), the lean round the held edge mirrored so it goes round the same
 * edge (its size kept: the sim's sizes hold), the gun across the chest toward the look.
 * `backBody` (the owner, on the second pass: "still some hip disconnection from torso at 210 and 240 degrees with tree cover,
 * maybe 150 degrees as well"): with the back to the cover the turn back to the look is the whole body's, about the vertical
 * through the neck (the head where the lean put it) — the hips come round with the chest, which stays within the rifle's
 * blade of them; turned off square to the face (just past the switch), the whole body stood off it, `back.clear` × (the turn's
 * sine − ½) (the near shoulder: it went 0.2 into a trunk). false, the torso's alone off the hips (up to 60° off them, its root slid up to 0.2 off the hips by the turn
 * about the neck), as the second pass first had it (the sheet's before, pass=3).
 * `chestHold`: round an edge or a corner, the low gun kept in front of the chest (`coverHeld`: where the given hold is more than
 * `across` off the chest's front — the strong side, the chest turned toward the edge and bladed — a low ready instead,
 * `lowReady` off the chest's front, the muzzle `downPitch` lower, held `lowLift` higher, between the knee and the face); false,
 * in the rig's frame wherever the chest is, as the first pass had it (the sheet's before).
 */
export const COVER_TURN = { torso: 0.6, pressOn: 0.75, pressOff: 0.35, back: { on: 1.75, off: 1.45, seconds: 0.35, pack: 0.22, clear: 0.45 }, backBody: true, chestHold: true, across: 1.3, lowReady: 0.7, downPitch: -0.6, lowLift: 0.33 };
/**
 * 66.10 (the owner: "when in cover sometimes barrel gets pointed down at ground, bullets come out of it sideways"): a shot in
 * cover brings the gun onto the aim (`rise` seconds: brought up, not a twitch) and holds it there `hold` seconds after the
 * last shot (lane C's GAIT.readySeconds, the view's gun-up after a shot), easing back to the peek's over `fade`. Down or
 * early in the peek the gun was at its rest or across the body, the round leaving it sideways. Pressed to low cover, the
 * gun brought level steps the body back off the face (the press keeps the arms out of it).
 */
export const COVER_FIRE = { rise: 0.08, hold: FOOT_GAIT.readySeconds, fade: 0.25 };
/**
 * 63.8, the gaits' look (lane C's GAIT: a move at 1.5 × the walk, its fire unaimed; a sprint at 2 ×, no fire; the LAW in hand
 * slows both to 0.85). Seconds into and out of each (`in`, `out`: leaving a move is the gun coming back up to the aim). The
 * long gun's carry in each, against the walk's (pitch: radians up from level; `yaw`: the muzzle turned across the body to
 * the support side; `up`, `side`, `fwd`: the hold moved, person units), the pistol's (pitch and drop only), the head's
 * pitch, and the legs': the stride's reach ahead and behind, its duty, its most strides a second, the knee's extra lift.
 * - **move**: the rifle low across the body (the muzzle down to the support side, the hands at the belt), the head up; a
 *   shot brings the muzzle round to the front at the hip, held while the shots keep coming (`hipHold` s after each, then
 *   back over `hipFade`), aimed by eye — the fire is unaimed.
 * - **sprint**: the rifle at port arms across the chest (`port`: the barrel's way in the heading's frame — up, over to the support
 *   shoulder, a little out; the gun's top to the front), the shoulders square,
 *   leaning in, the gun rocking with the stride; the knees high, a long flight; the upper body jolting at each footfall, the
 *   pack's weight with it (`jolt`: the pack as its own part would cost a draw a person — 67.16's budget: triangles, never draws).
 *   The LAW stays on the shoulder in either (the tube is too long to carry across).
 */
export const GAIT_LOOK = {
  in: { move: 0.2, sprint: 0.25 }, out: { move: 0.2, sprint: 0.3 },
  move: {
    pitch: -0.7, yaw: 0.65, up: 0, side: -0.12, fwd: 0.02, head: -0.05, hipHold: 0.3, hipFade: 0.25, hipPitch: -0.12, hipUp: -0.26, hipFwd: -0.14,
    pistol: { pitch: -1.0, up: -0.08 }, front: 0.34, back: 0.56, duty: 0.18, cap: 2.1, lift: 0.04, jolt: 0.02,
  },
  sprint: {
    port: { across: 0.45, rise: 0.85, out: 0.2 }, up: -0.4, side: 0.02, fwd: 0.3, head: -0.12, lean: 0.34, swing: 0.12,
    pistol: { pitch: -1.15, up: -0.02 }, front: 0.36, back: 0.62, duty: 0.16, cap: 2.4, lift: 0.14, jolt: 0.07,
  },
};
/**
 * 66.8, digging in (decision 160): the entrenching tool — a folding shovel, its D-grip at the origin, the shaft down to its
 * hinge, the blade below (along −y). Carried strapped to the back of the pack, grip down, the blade folded back along the
 * shaft in a khaki carrier (`carrier`, shown only folded: the team's dark pack would swallow the bare steel). Its grips:
 * the strong hand on the D-grip, the support hand down the shaft.
 */
export interface EntrenchingTool { group: THREE.Group; blade: THREE.Group; carrier: THREE.Object3D; gripR: THREE.Object3D; gripL: THREE.Object3D }
export const ENTRENCHING_TOOL = { shaft: 0.42, blade: 0.2, width: 0.16 };
export function buildEntrenchingTool(p: UnitPalette): EntrenchingTool {
  const g = new THREE.Group(), T = ENTRENCHING_TOOL;
  // the D-grip: a short bar across a loop
  box(g, 0.1, 0.025, 0.025, p.dark, 0, 0, 0); box(g, 0.022, 0.08, 0.022, p.dark, -0.045, -0.04, 0); box(g, 0.022, 0.08, 0.022, p.dark, 0.045, -0.04, 0);
  box(g, 0.035, T.shaft - 0.08, 0.035, '#5a5a3c', 0, -0.08 - (T.shaft - 0.08) / 2, 0);   // the shaft, olive
  const blade = group(g, 0, -T.shaft, 0);
  box(blade, 0.04, 0.05, 0.04, p.metal, 0, -0.02, 0);                                    // the hinge's collar
  const b = box(blade, T.width, T.blade, 0.012, p.metal, 0, -0.05 - T.blade / 2, 0.01); b.rotation.x = 0.08;   // the blade, a little dished
  const carrier = group(blade, 0, -0.06 - T.blade / 2, 0.01);
  box(carrier, T.width + 0.03, T.blade + 0.05, 0.05, '#8a8458', 0, 0, 0);   // (khaki canvas over the folded blade)
  box(carrier, T.width + 0.04, 0.025, 0.06, '#5e5a3c', 0, 0.04, 0);         // its strap
  const gripR = group(g, 0, 0, 0), gripL = group(g, 0, -T.shaft * 0.62, 0);
  return { group: g, blade, carrier, gripR, gripL };
}
/**
 * 66.8: the dig cycle — kneeling, `seconds` a stroke: the tool raised, driven into the ground in front, levered up with its
 * load, swung forward and tipped so the spoil lands toward the enemy (the lip), and back. Each key is the D-grip's and the
 * blade tip's place in the hips' frame (person units; the hips at the kneel's height, the ground 0.54 below them).
 * `throwAt` is the share of the stroke the spoil leaves the blade (the host throws a clod there).
 */
export const DIG_CYCLE = {
  seconds: 1.4, lean: 0.35, throwAt: 0.68,
  keys: [
    { at: 0, grip: [0.12, 0.36, 0.14], tip: [0.04, -0.06, 0.62] },      // raised
    { at: 0.3, grip: [0.12, -0.08, 0.32], tip: [0.02, -0.74, 0.5] },    // driven in (the hands down at the ground)
    { at: 0.5, grip: [0.16, 0.2, 0.04], tip: [0.04, -0.34, 0.56] },     // levered up, loaded
    { at: 0.72, grip: [0.12, 0.36, 0.32], tip: [0.04, 0.0, 0.96] },     // swung forward and tipped: the spoil thrown
    { at: 1, grip: [0.12, 0.36, 0.14], tip: [0.04, -0.06, 0.62] },
  ] as const,
};
const _dg = new THREE.Vector3(), _dt = new THREE.Vector3(), _dd = new THREE.Vector3();
/** 66.8: the tool's grip and tip `phase` of the way through a stroke (into `grip`, `tip`). Pure. */
export function digKey(phase: number, grip: THREE.Vector3, tip: THREE.Vector3): void {
  const K = DIG_CYCLE.keys, p = phase - Math.floor(phase);
  let i = 0; while (i < K.length - 2 && p >= K[i + 1].at) i++;
  const a = K[i], b = K[i + 1], k = smooth((p - a.at) / (b.at - a.at));
  grip.set(a.grip[0] + (b.grip[0] - a.grip[0]) * k, a.grip[1] + (b.grip[1] - a.grip[1]) * k, a.grip[2] + (b.grip[2] - a.grip[2]) * k);
  tip.set(a.tip[0] + (b.tip[0] - a.tip[0]) * k, a.tip[1] + (b.tip[1] - a.tip[1]) * k, a.tip[2] + (b.tip[2] - a.tip[2]) * k);
}
/**
 * 65.28: how a suppressed person reacts (decision 161; the owner wants the player's impact minimal until he has felt it,
 * so this is the drawing's): each near round a beat `beat` s long, in fast (`attack` of it) and out slow — the head down
 * (`head` rad), the shoulders in (`lean`), the hips dipping at a post (`dip`) or a stoop in the open (`stoop`, the bend's
 * share); while the mark lasts (full at `full` s left) a pose down in cover is held lower (`held*`), the hips no lower than
 * `minHip`. Prone: the face to the ground on the beat (`proneHead`).
 */
export const SUPPRESS_LOOK = { beat: 0.45, attack: 0.15, head: 0.45, lean: 0.16, dip: 0.07, stoop: 0.3, heldHead: 0.18, heldLean: 0.06, heldDip: 0.05, full: 2, minHip: 0.28, proneHead: 0.3 };
/** 66.7 (a): the stance each cover pose is a kind of (the hitbox's family, the slope rule, the far tier). */
export const COVER_STANCE: Record<CoverPose, Stance> = { low: 'crouch', edge: 'crouch', corner: 'stand', crest: 'prone' };
/** A kneeling knee's height off the ground; a rising kneel lifts it onto the foot between `KNEEL_RISE` and that plus 0.12 of hip height. */
const KNEE_DOWN = 0.1, KNEEL_RISE = 0.54;
/** The low walk's hip height. */
const CROUCH_WALK_HIP_Y = 0.74;
/**
 * BUILDLIST 73.3 (DECISIONS 326, 331; ARCHITECTURE §5.37): the crouch's kneel, a little lower — sat back onto its heel (the
 * kneeling leg solved knee-down, as the cover's kneel is: hip 0.54 → 0.38) and hunched over the rifle (lean 0.08 → 0.40),
 * the front foot `foot` ahead. Measured eye 0.99, top 1.245 (were 1.25 / 1.47): STANCE_SIZE.crouch. The low walk went
 * lower with it (LOW_LOOK). (The edge's ducked pose stays: lowered, its rifle meets the ground or comes past the cover's face.)
 */
export const CROUCH_KNEEL = { hipY: 0.38, lean: 0.4, foot: 0.3 };
/**
 * The gait (owner, Oct 10: the moving figures "could be better", then "walking is better … but too frantic, legs
 * move really fast"). A person runs at four and a half heights a second (8 person units): a sprinter's pace. Each
 * foot's stance carries it from `front` ahead of the hip to `back` behind it at the ground's speed, so the cadence is
 * the duty factor × speed ÷ contact length; the duty factor — the share of a stride a foot is down — falls from a
 * walk's (both feet down between steps) to a run's (a flight between them), which is what lets a runner cover the
 * ground in few strides. A real gait at a sprint is four-odd steps a second; this one is capped at `maxCycles`
 * strides (3.5 steps) a second, so at full pace the planted foot slides a little (a fifth) rather than the legs
 * blurring — walks and jogs never slide. Walking, the hips ride highest over the planted foot; running, they
 * compress in each stance and rise in the flight. The swinging foot's lift, the lean and the hip–shoulder twist grow
 * with speed.
 */
const GAIT = {
  walkAt: 1.8, runAt: 4,                      // person units a second (a person is 1.8): a walk at or below, a run at or above, a blend between
  walkDuty: 0.6, runDuty: 0.22,
  walkFront: 0.38, walkBack: 0.42, runFront: 0.32, runBack: 0.53,
  runDip: 0.06, runBounce: 0.05, dropMax: 0.08,   // a run's compression in each stance and rise in each flight; the most the stride may lower the hips
  minCycles: 0.6, maxCycles: 1.75,            // strides a second: the slowest shuffle, and the cap at a sprint (3.5 steps a second)
  lift: 0.08, liftPerSpeed: 0.03, liftMax: 0.26, lean: 0.04, leanPerSpeed: 0.035, leanMax: 0.22, twist: 0.12, roll: 0.035,
};
/** The gait at a speed (person units a second): duty factor, the foot's reach ahead and behind, strides a second. Pure. */
export function gaitAt(speed: number): { duty: number; front: number; back: number; cycles: number; run: number } {
  const g = GAIT, run = Math.max(0, Math.min(1, (speed - g.walkAt) / (g.runAt - g.walkAt)));
  const duty = g.walkDuty + (g.runDuty - g.walkDuty) * run;
  const front = g.walkFront + (g.runFront - g.walkFront) * run, back = g.walkBack + (g.runBack - g.walkBack) * run;
  return { duty, front, back, cycles: Math.max(g.minCycles, Math.min(g.maxCycles, (duty * speed) / (front + back))), run };
}
/** One foot at phase `p` (0…1) of its stride: stance from `front` to `back`, then the swing forward, lifted. */
function footAt(p: number, gt: ReturnType<typeof gaitAt>, liftH: number): { forward: number; lift: number; stance: number } {
  if (p < gt.duty) { const q = p / gt.duty; return { forward: gt.front - (gt.front + gt.back) * q, lift: 0, stance: Math.sin(Math.PI * q) }; }
  const q = (p - gt.duty) / (1 - gt.duty), e = q * q * (3 - 2 * q);
  return { forward: -gt.back + (gt.front + gt.back) * e, lift: liftH * Math.sin(Math.PI * q), stance: 0 };
}
/** The low walk's strides a second at most: its reach is short (a low hip), so above this its feet slide a little rather than blur. */
const LOW_WALK_CYCLES = 1.6;
/**
 * 67.60, the walk in each stance (the coordinator's brief, Oct 19). The low walk (crouched; the game's at 0.55 of the walk,
 * 4.5 person units a second, a crouched run): today's short steps at 1.6 strides a second carried the feet a sixth of the
 * ground's pace (they skated); now the stride is the run's measure with the hips low — each planted foot keeps pace with
 * the ground (the duty `duty` slow … fast, the reach `front` / `back` that far ahead and behind, at most `maxCycles`
 * strides a second), heel to toe, the hips' bob a smooth wave (`bob`), the shoulders turning against the hips (`twist`),
 * leaning into it (`lean`, more with speed). `hip` the hips' height. The crawl (prone; 0.25 of the walk): each knee drawn up
 * by the side in turn (the thigh out along the ground `splay`, turned on its axis `turn` so the shin folds along the
 * ground, the knee `knee`), the opposite elbow reaching (the gun forward `reach`), the hips surging on each push (`surge`),
 * the shoulders rolling to the drawn side (`roll`), the muzzle carried up off the ground (`muzzle`), `cycles` strides a
 * second (a stride: both sides). `on` false: as before.
 */
export const LOW_LOOK = { on: true, hip: 0.51, duty: [0.6, 0.42] as const, front: 0.36, back: 0.42, walkAt: 1, runAt: 3.5, minCycles: 0.6, maxCycles: 2.1, lift: 0.1, liftPerSpeed: 0.02, bob: 0.025, lean: 0.66, leanPerSpeed: 0.02, twist: 0.1 };
export const CRAWL_LOOK = { on: true, cycles: [0.5, 1.2] as const, perSpeed: 0.45, splay: 0.55, turn: 1.45, knee: 1.5, reach: 0.09, surge: 0.05, roll: 0.12, twist: 0.08, muzzle: 0.12 };
/**
 * 67.60, the jump's look (the coordinator's brief: "take-off crouch, tuck at the apex, the landing's knee give, the rifle held
 * through it", now that 63.15's jump feel gives ~0.7 s in the air). The sim leaves the ground the tick the jump is asked for,
 * so the crouch is drawn inside the jump: for `push` seconds the feet stay on the ground it left while the hips dip `crouch`
 * (at `low` of the push) and rise onto the toes (`rise` over standing, the boots pitched `extend.toes`); then the drawn body,
 * that far behind the sim's, catches it up over `catchUp` seconds. In the air the legs go from the push's extension (`extend`:
 * one trailing) to the tuck by the time the body has lost `tuckBy` of its rising speed (`tuck`: the knees up), and from it
 * to the reach for the ground (`reach`: the soles about at the ground) as it falls from `reachFrom` to `reachBy` of the take-off
 * speed. Angles in radians (thigh > 0 forward, knee > 0 folded, toes > 0 down, lean > 0 forward). The landing (`land`, every
 * landing — a hop's, a blast's): the hips give `perSpeed` × the speed it came down at (at most `most`), in over `in` s and up
 * over `out`, the feet planted, leaning in `lean` at the deepest, the gun dipping `gun` with the arms. `on` false: as before
 * (the sheet's before): no push, a fixed tuck the whole way, the landing's 0.18 taken in its first frame.
 */
export const JUMP_LOOK = {
  on: true, push: 0.14, low: 0.4, crouch: 0.26, rise: 0.06, catchUp: 0.12, blend: 0.06, feet: [0.04, -0.06] as const,
  tuckBy: 0.75, reachFrom: 0.15, reachBy: 0.75,
  extend: { thighL: 0.05, kneeL: 0.2, thighR: -0.3, kneeR: 0.45, lean: 0.12, toes: 0.55 },
  tuck: { thighL: 1.05, kneeL: 1.65, thighR: 0.7, kneeR: 1.35, lean: 0.2, toes: 0.25 },
  reach: { thighL: 0.32, kneeL: 0.42, thighR: 0.12, kneeR: 0.3, lean: 0.08, toes: 0.2 },
  land: { perSpeed: 0.036, most: 0.3, in: 0.07, out: 0.32, lean: 0.3, gun: 0.05, feet: [0.1, -0.1] as const },
};
/**
 * 67.60, the walk and the run's look (the owner, on the feet pass: "slightly better" — this one bolder; the coordinator's
 * brief: "stride by speed, arm swing with the rifle held, the hips' bob, the feet planted against the ground's drop"):
 * - heel to toe: a stance foot strikes heel first (its toe up `heel` rad, flat by `flat` of the stance) and rolls onto its toe
 *   from `rollFrom` of the stance (the heel up to `toe` rad at the toe-off), leaving toe down and pulled level by `level` of
 *   the swing; the ankle rises with the roll, so a foot far behind reaches without the hips coming down for it;
 * - the hips' bob, designed: a walk vaults over each planted leg — highest at mid-stance, down `bob` at the change of feet
 *   (fading as the walk becomes a run, whose own dip and bounce stay); the sway, `sway` over the planted foot; the drop, the
 *   swinging leg's side down `drop` rad (the shoulders kept level against it);
 * - the rifle held swinging with the stride: turning `gunYaw` rad with the shoulders' counter-turn and nodding `gunNod` with
 *   each step, bobbing `gunBob`, all with the pace (full by 4 person units a second), `aimed` of it while the gun is up at the
 *   aim (a shot just fired);
 * - the shoulders' counter-turn `twist` × the walk's.
 * `on` false: as before (the sheet's before).
 */
export const STRIDE_LOOK = {
  on: true, heel: 0.3, flat: 0.18, rollFrom: 0.55, toe: 0.55, level: 0.45,
  bob: 0.045, sway: 0.03, drop: 0.07, gunYaw: 0.07, gunNod: 0.05, gunBob: 0.025, aimed: 0.25, twist: 1.5,
};
/** 67.60: a stance or swinging foot's pitch (+ toe down) at phase `p` of its stride (STRIDE_LOOK's heel to toe). Pure. */
export function footRoll(p: number, duty: number): number {
  const S = STRIDE_LOOK;
  if (p < duty) { const q = p / duty; return -S.heel * (1 - smooth(q / S.flat)) + S.toe * smooth((q - S.rollFrom) / (1 - S.rollFrom)); }
  const q = (p - duty) / (1 - duty);
  return S.toe * (1 - smooth(q / S.level)) - S.heel * smooth((q - 0.7) / 0.3);
}
/** 67.60: how far the ankle rises over the ground when the boot pitches `pitch` (+ toe down) about its toe or its heel. Pure. */
export function ankleRise(pitch: number): number {
  return pitch >= 0 ? BOOT.toe * Math.sin(pitch) + SOLE * (Math.cos(pitch) - 1) : BOOT.heel * Math.sin(-pitch) + SOLE * (Math.cos(pitch) - 1);
}
/** The boot's toe and heel from the ankle, along it (the boot: 0.31 long, 0.04 ahead of the ankle). */
const BOOT = { toe: 0.195, heel: 0.115 };
/** A leg's lateral offset from the hips' centre. */
const LEG_X = 0.11;
/** The ground's slope is followed up to this rise per unit (35°); steeper is a cliff the feet cannot follow anyway. */
const SLOPE_MAX = 0.7;
/** The sole sits this far below the shin's end (a standing leg: HIP_Y = THIGH + SHIN + SOLE). */
const SOLE = HIP_Y - THIGH - SHIN;
/**
 * A leg's [thigh, knee] angles (the rig's: thigh > 0 swings forward, knee > 0 folds back) that put the
 * sole `forward` of the hip and `lift` off the ground with the hip `hip` high — a two-link solve in the
 * leg's plane, the knee bending forward.
 */
function legTo(hip: number, forward: number, lift: number): [number, number] {
  const h = hip - SOLE - lift, d = Math.min(THIGH + SHIN - 1e-6, Math.hypot(forward, h));
  const phi = Math.atan2(forward, h);                                                         // hip → ankle, forward of straight down
  const alpha = Math.acos(Math.max(-1, Math.min(1, (THIGH * THIGH + d * d - SHIN * SHIN) / (2 * THIGH * d))));
  const beta = Math.acos(Math.max(-1, Math.min(1, (THIGH * THIGH + SHIN * SHIN - d * d) / (2 * THIGH * SHIN))));
  return [phi + alpha, Math.PI - beta];
}
const SHOULDER_Y = 0.52, SHOULDER_X = 0.32;     // above the hips, in torso space
const UPPER_ARM = 0.32, FOREARM = 0.32;
const AIM_AT = new THREE.Vector3(0.15, 0.47, 0.08);   // the handheld's pivot, at the right shoulder pocket
const POLE_R = new THREE.Vector3(1, -0.7, -0.3);
const POLE_L = new THREE.Vector3(-0.4, -0.6, 1.0);
const POLE_R_LOW = new THREE.Vector3(0.4, -1, 0), POLE_L_LOW = new THREE.Vector3(-0.3, -1, 0.3);   // 67.60: the low ready in cover — both elbows down at the sides    // the support elbow forward and down, under the weapon: the arm reaches the foregrip in front of the chest, never through it
/**
 * 409 (the owner, after playing 407: "left arm is sort of broken so elbow acts like a hanging hinge"): at the eye the support
 * arm reached the handguard at full stretch (0.62–0.64 of its 0.64; locked in the low carry), so it had no bend and its
 * forearm's roll swung with the hand. At the eye the support hand slides back along the gun to within `reach` of the
 * shoulder (up to `slide` further than SUPPORT_REACH lets it), so the arm bends, and its pole hangs the elbow down and out
 * under the gun. Every other figure's hold is as before.
 */
export const EYE_SUPPORT = { reach: 0.52, slide: 0.12, pole: new THREE.Vector3(-0.6, -1, 0) };
const REST_R = new THREE.Vector3(0.36, 0.0, 0.06), REST_L = new THREE.Vector3(-0.36, 0.0, 0.06);   // a free hand at the side
/** 67.63: a free hand seated (a chair, a bench): on the thigh, halfway to the knee (torso space). */
const SEAT_REST_R = new THREE.Vector3(0.15, 0.03, 0.3), SEAT_REST_L = new THREE.Vector3(-0.15, 0.03, 0.3);

/** The handheld (decision 3): today's gun is the rifle (as play draws it, gunLooks GUN_IN_PLAY); kept under its old name for the sheets. */
export function buildHandheld(p: UnitPalette): { group: THREE.Group; muzzle: THREE.Object3D; gripR: THREE.Object3D; gripL: THREE.Object3D } {
  return buildPlayGun('rifle', p);
}

function buildArm(parent: THREE.Group, x: number, p: UnitPalette, band: boolean, bare = false): IkArm {
  const sh = group(parent, x, SHOULDER_Y, 0);
  if (bare) return { group: sh, fore: group(sh, 0, -UPPER_ARM, 0), a: UPPER_ARM, b: FOREARM };   // 198: the soldier's own parts (soldierLooks.ts)
  const out = Math.sign(x), inward = -out;
  box(sh, 0.16, 0.12, 0.18, p.body, 0, -0.02, 0);                   // shoulder cap
  // 67.16: a shoulder pad over the cap, with a dark edge — the shoulder reads as armoured, not as a box's corner
  box(sh, 0.21, 0.06, 0.23, p.body, out * 0.02, 0.055, 0);
  box(sh, 0.22, 0.025, 0.24, p.dark, out * 0.02, 0.015, 0);
  box(sh, 0.15, UPPER_ARM, 0.16, p.body, 0, -UPPER_ARM / 2, 0);
  if (band) box(sh, 0.16, 0.07, 0.17, p.flash, 0, -0.12, 0);         // the armband: the flash on the side
  const el = group(sh, 0, -UPPER_ARM, 0);
  box(el, 0.14, FOREARM - 0.06, 0.15, p.body, 0, -(FOREARM - 0.06) / 2, 0);
  // 67.16: a hand — a cuff at the wrist, a gloved hand past the palm point (it closes round the grip there), a thumb on the inside
  box(el, 0.15, 0.05, 0.16, p.dark, 0, -FOREARM + 0.075, 0);
  box(el, 0.1, 0.14, 0.12, GLOVE, 0, -FOREARM - 0.01, 0.01);
  box(el, 0.045, 0.075, 0.055, GLOVE, inward * 0.06, -FOREARM + 0.02, 0.05);
  return { group: sh, fore: el, a: UPPER_ARM, b: FOREARM };
}

function buildLeg(parent: THREE.Group, x: number, p: UnitPalette, bare = false): { thigh: THREE.Group; shin: THREE.Group; foot: THREE.Group } {
  const thigh = group(parent, x, 0, 0);
  thigh.rotation.order = 'ZXY';   // 66.7 (a): a splay across the body comes after the swing (no other pose turns a thigh about z)
  if (bare) { const shin = group(thigh, 0, -THIGH, 0); return { thigh, shin, foot: group(shin, 0, -SHIN, 0) }; }   // 198: soldierLooks.ts
  box(thigh, 0.2, THIGH, 0.24, p.body, 0, -THIGH / 2, 0);
  const shin = group(thigh, 0, -THIGH, 0);
  box(shin, 0.19, SHIN, 0.22, p.body, 0, -SHIN / 2, 0);
  const foot = group(shin, 0, -SHIN, 0);                           // the ankle: the boot can stay level while the shin tilts
  box(foot, 0.22, 0.12, 0.31, p.dark, 0, 0, 0.04);                 // boot
  return { thigh, shin, foot };
}

/**
 * The far tier (67.16): a soldier's outline in the pose the body is in — standing aiming, a two-frame run (the
 * legs swap with the stride, so a distant runner runs), kneeling, prone — each a few boxes in the three values
 * plus the weapon, baked to one mesh; one pose shows at a time, so it stays one draw. It used to be one standing
 * block whatever the body did: a distant prone soldier stood up. The gap between the legs, the shoulders wider
 * than the hips, the head apart from the body and the weapon held forward are what read as a person at 6–24 px.
 */
type P3 = [number, number, number];
/** 67.56: the far tier's kit — the vest, the helmet and the leader — as the full tier wears it (armourLooks.ts). */
interface FarKit { armor: Armor; leader: boolean; helmet: HelmetKind; look: VestLook; soldier: boolean; plain: boolean; role?: ReturnType<typeof farRole> }
/** 201 (8): a role's far boxes, offset to the pose. */
function farParts(g: THREE.Object3D, parts: ReturnType<typeof farRole>['upper'], dy: number, dz: number): void {
  for (const q of parts) {
    if ('box' in q) { const [w, h, d, x, y, z] = q.box; box(g, w, h, d, q.c, x, y + dy, z + dz); }
    else { const [a, b, t] = q.bar; bar(g, [a[0], a[1] + dy, a[2] + dz], [b[0], b[1] + dy, b[2] + dz], t, q.c); }
  }
}
function silhouetteUpper(g: THREE.Group, p: UnitPalette, dy: number, dz: number, lowReady: boolean, kit: FarKit): void {
  const at = (x: number, y: number, z: number): P3 => [x, y + dy, z + dz];
  const c = armourColours(p, VEST_LOOKS[kit.look].scheme);
  // 68.8: the leader — the radio's antenna above the pack on the support side (67.57: the helmet's flash band below)
  if (kit.leader) bar(g, at(-0.1, 1.44, -0.3), at(-0.17, 2.3, -0.42), 0.06, p.dark);
  // 67.56: the vest bulks the far figure — the carrier a little wider and deeper; heavy wider again, with a collar and padded shoulders
  // 198: the soldier wears the plate carrier over every kit but the plain base (a soft one with no armour), its pack slimmer
  if (kit.soldier && kit.armor === 'none' && !kit.plain) box(g, 0.52, 0.34, 0.36, c.soft, 0, 1.25 + dy, dz);
  if (kit.armor === 'light') box(g, 0.54, 0.38, 0.38, c.plate, 0, 1.24 + dy, dz);
  if (kit.armor === 'heavy') { box(g, 0.6, 0.44, 0.42, c.plate, 0, 1.24 + dy, dz); box(g, 0.36, 0.1, 0.36, c.soft, 0, 1.5 + dy, dz + 0.02); box(g, 0.88, 0.16, 0.32, c.soft, 0, 1.38 + dy, dz); }
  box(g, 0.4, 0.1, 0.26, p.dark, 0, 0.9 + dy, dz);                 // belt
  box(g, 0.48, 0.5, 0.3, p.body, 0, 1.2 + dy, dz);                 // torso
  if (!kit.plain && kit.role?.pack !== false) box(g, kit.soldier ? 0.3 : 0.34, kit.soldier ? 0.34 : 0.38, kit.soldier ? 0.14 : 0.16, p.body, 0, 1.22 + dy, dz - 0.22);   // pack
  if (kit.role) farParts(g, kit.role.upper, dy, dz);   // 201 (8): the medic's or the engineer's marks
  box(g, 0.62, 0.12, 0.28, p.body, 0, 1.42 + dy, dz);              // shoulders
  box(g, 0.2, 0.2, 0.22, p.body, 0, 1.58 + dy, dz + 0.01);         // head
  // 67.56: the helmet (light today's box; heavy bigger, with its mandible guard) and the visor — or the balaclava, smaller and grey, no visor
  if (kit.helmet === 'none') box(g, 0.24, 0.25, 0.25, BALACLAVA.knit, 0, 1.62 + dy, dz + 0.01);
  else {
    const heavy = kit.helmet === 'heavy', hc = kit.look === 'D' ? (heavy ? c.heavyPlate : c.soft) : c.plate;
    box(g, heavy ? 0.35 : 0.3, heavy ? 0.17 : 0.15, heavy ? 0.36 : 0.32, hc, 0, 1.7 + dy, dz - 0.01);
    if (heavy) box(g, 0.22, 0.08, 0.06, hc, 0, 1.53 + dy, dz + 0.13);
    box(g, 0.22, 0.06, 0.04, p.flash, 0, 1.6 + dy, dz + 0.13);       // visor: the flash survives the far tier
    if (kit.leader) box(g, heavy ? 0.365 : 0.315, 0.04, heavy ? 0.375 : 0.335, p.flash, 0, 1.69 + dy, dz - 0.01);   // 67.57: the leader's band
  }
  if (lowReady) {
    bar(g, at(0.24, 1.0, 0.2), at(-0.14, 1.42, 0.34), 0.07, p.metal);           // the rifle across the chest
    bar(g, at(0.3, 1.42, 0), at(0.3, 1.16, 0.08), 0.11, p.body); bar(g, at(0.3, 1.16, 0.08), at(0.2, 1.06, 0.22), 0.1, p.body);
    bar(g, at(-0.3, 1.42, 0), at(-0.24, 1.2, 0.16), 0.11, p.body); bar(g, at(-0.24, 1.2, 0.16), at(-0.08, 1.33, 0.32), 0.1, p.body);
  } else {
    bar(g, at(0.15, 1.38, -0.05), at(0.15, 1.36, 0.85), 0.07, p.metal);           // the rifle at the shoulder, forward
    bar(g, at(0.3, 1.42, 0), at(0.32, 1.22, 0.1), 0.11, p.body); bar(g, at(0.32, 1.22, 0.1), at(0.17, 1.32, 0.13), 0.1, p.body);
    bar(g, at(-0.3, 1.42, 0), at(-0.22, 1.2, 0.3), 0.11, p.body); bar(g, at(-0.22, 1.2, 0.3), at(0.12, 1.33, 0.5), 0.1, p.body);
  }
}
function buildSilhouette(p: UnitPalette, armor: Armor = 'none', leader = false, helmet: HelmetKind = 'light', look: VestLook = 'B', plain = false, role?: RoleDress): { group: THREE.Group; poses: Record<SilhouettePose, THREE.Group> } {
  const g = new THREE.Group();
  const soldier = look !== 'D';   // 198: the soldier's far figure (the flak look keeps the old body's)
  const kit: FarKit = { armor, leader, helmet, look, soldier, plain, ...(role && soldier && !plain ? { role: farRole(role, p) } : {}) }, c = armourColours(p, VEST_LOOKS[look].scheme);
  const legC = soldier ? clothOf(p).pants : p.body;   // the trousers darker than the shirt, as up close
  const pose = () => { const q = new THREE.Group(); q.visible = false; g.add(q); return q; };
  const legs = (q: THREE.Group, l: [P3, P3, P3], r: [P3, P3, P3]) => {
    for (const [hip, knee, foot] of [l, r]) { bar(q, hip, knee, 0.16, legC); bar(q, knee, foot, 0.15, legC); box(q, 0.17, 0.09, 0.26, p.dark, foot[0], Math.max(0.045, foot[1]), foot[2] + 0.04); }
  };
  const stand = pose();
  legs(stand, [[-0.11, 0.9, 0], [-0.11, 0.48, 0], [-0.11, 0.06, 0]], [[0.11, 0.9, 0], [0.11, 0.48, 0], [0.11, 0.06, 0]]);
  silhouetteUpper(stand, p, 0, 0, false, kit);
  const runA = pose(), runB = pose();
  for (const [q, s] of [[runA, 1], [runB, -1]] as const) {
    const fwd = (x: number): [P3, P3, P3] => [[x, 0.88, 0], [x, 0.5, 0.24], [x, 0.07, 0.12]];
    const back = (x: number): [P3, P3, P3] => [[x, 0.88, 0], [x, 0.46, -0.08], [x, 0.16, -0.34]];
    legs(q, s > 0 ? fwd(-0.1) : back(-0.1), s > 0 ? back(0.1) : fwd(0.1));
    silhouetteUpper(q, p, -0.02, 0.07, true, kit);
  }
  const crouch = pose();
  legs(crouch, [[-0.11, 0.34, 0], [-0.11, 0.36, 0.42], [-0.11, 0.06, 0.4]], [[0.11, 0.34, 0], [0.11, 0.08, 0.2], [0.11, 0.06, -0.24]]);   // (73.3: sat back onto the heel)
  silhouetteUpper(crouch, p, -0.56, 0.06, false, kit);
  const prone = pose();
  for (const sx of [-0.12, 0.12]) { bar(prone, [sx, 0.12, -0.05], [sx * 1.4, 0.08, -0.92], 0.15, legC); box(prone, 0.16, 0.12, 0.09, p.dark, sx * 1.4, 0.1, -0.98); }
  box(prone, 0.4, 0.1, 0.24, p.dark, 0, 0.13, -0.05);              // belt
  box(prone, 0.48, 0.24, 0.55, p.body, 0, 0.17, 0.25);             // torso
  if (armor !== 'none') box(prone, armor === 'heavy' ? 0.6 : 0.54, armor === 'heavy' ? 0.32 : 0.28, 0.5, c.plate, 0, 0.19, 0.27);   // 67.56: the vest over it
  else if (soldier && !plain) box(prone, 0.52, 0.27, 0.46, c.soft, 0, 0.19, 0.28);   // 198: the carrier with no armour
  if (armor === 'heavy') box(prone, 0.82, 0.12, 0.18, c.soft, 0, 0.27, 0.46);   // and the heavy's padded shoulders, past the elbows
  if (!plain && kit.role?.pack !== false) box(prone, soldier ? 0.3 : 0.34, 0.12, soldier ? 0.34 : 0.38, p.body, 0, 0.33, 0.2);   // pack
  if (kit.role) farParts(prone, kit.role.prone, 0, 0);   // 201 (8)
  if (leader) bar(prone, [-0.1, 0.4, 0.1], [-0.16, 0.62, -0.7], 0.06, p.dark);   // 68.8: the antenna laid back
  box(prone, 0.2, 0.2, 0.2, p.body, 0, 0.25, 0.65);                // head
  if (helmet === 'none') box(prone, 0.24, 0.22, 0.24, BALACLAVA.knit, 0, 0.3, 0.66);   // 67.56: the balaclava, no visor
  else {
    const heavy = helmet === 'heavy', hc = look === 'D' ? (heavy ? c.heavyPlate : c.soft) : c.plate;
    box(prone, heavy ? 0.34 : 0.3, heavy ? 0.16 : 0.14, heavy ? 0.34 : 0.3, hc, 0, 0.36, 0.64);   // helmet
    box(prone, 0.22, 0.05, 0.04, p.flash, 0, 0.28, 0.76);            // visor
    if (leader) box(prone, heavy ? 0.355 : 0.315, 0.035, heavy ? 0.355 : 0.315, p.flash, 0, 0.33, 0.64);   // 67.57: the leader's band
  }
  bar(prone, [0.14, 0.3, 0.45], [0.14, 0.26, 1.35], 0.07, p.metal);                    // the rifle, forward
  bar(prone, [-0.28, 0.06, 0.6], [0.08, 0.26, 0.95], 0.1, p.body); bar(prone, [0.3, 0.06, 0.45], [0.15, 0.28, 0.55], 0.1, p.body);   // on the elbows
  // 66.7 (a): ducked behind low cover — back on the strong heel, the weak foot planted ahead, hunched over its knee
  // (the full tier's COVER.low, down), the rifle across the chest
  const duck = pose();
  legs(duck, [[-0.11, 0.36, 0], [-0.11, 0.46, 0.43], [-0.11, 0.06, 0.3]], [[0.11, 0.36, 0], [0.11, 0.1, 0.36], [0.11, 0.2, -0.05]]);
  const hunch = group(duck, 0, 0.36, 0); hunch.rotation.x = COVER.low.downLean;
  silhouetteUpper(hunch, p, -0.9, 0, true, kit);
  // 67.63: seated — in a chair or on a bench (the hips at 0.55, the thighs forward, the shins down), and astride (the hips at
  // their standing height, the knees up and forward to the pegs); the rifle stowed across the chest
  const sit = pose();
  legs(sit, [[-0.11, 0.55, 0], [-0.11, 0.55, 0.42], [-0.11, 0.06, 0.44]], [[0.11, 0.55, 0], [0.11, 0.55, 0.42], [0.11, 0.06, 0.44]]);
  silhouetteUpper(sit, p, -0.37, 0, true, kit);
  const ride = pose();
  legs(ride, [[-0.12, 0.88, 0], [-0.15, 0.72, 0.36], [-0.17, 0.46, 0.17]], [[0.12, 0.88, 0], [0.15, 0.72, 0.36], [0.17, 0.46, 0.17]]);
  const lean = group(ride, 0, 0.88, 0); lean.rotation.x = SADDLE.lean;
  silhouetteUpper(lean, p, -0.88, 0, true, kit);
  stand.visible = true;
  return { group: g, poses: { stand, runA, runB, crouch, prone, duck, sit, ride } };
}

/**
 * The old box body's torso and head (67.16): since 198 only the flak look (D) is drawn on it — the owner's kept option, the
 * record of it, to rebuild on the new body if he wants it. The soldier is soldierLooks.ts.
 */
function boxBody(torso: THREE.Group, head: THREE.Group, p: UnitPalette): void {
  box(torso, 0.52, 0.09, 0.32, p.dark, 0, 0.04, 0);              // belt
  box(torso, 0.5, 0.52, 0.3, p.body, 0, 0.33, 0);                // chest
  box(torso, 0.4, 0.2, 0.06, p.dark, 0, 0.3, 0.15);              // chest rig (pouches)
  box(torso, 0.36, 0.4, 0.18, p.body, 0, 0.33, -0.24);           // pack, body-coloured so the team reads from behind
  box(torso, 0.38, 0.12, 0.2, p.dark, 0, 0.5, -0.24);            // pack flap
  // 67.16: the yoke between the chest and the neck, and the rig's straps over the shoulders and down the front
  box(torso, 0.46, 0.07, 0.27, p.body, 0, 0.6, 0);
  for (const sx of [-0.15, 0.15]) {
    box(torso, 0.07, 0.03, 0.33, p.dark, sx, 0.64, 0);
    box(torso, 0.06, 0.3, 0.02, p.dark, sx, 0.46, 0.16);
  }
  const skull = new THREE.Mesh(new THREE.IcosahedronGeometry(0.15, 0), mat(p.body)); skull.position.set(0, 0.12, 0.01); head.add(skull);
  // 67.56: the helmet (or the balaclava) is the kit's — armourLooks.ts, below
  box(head, 0.24, 0.07, 0.06, p.flash, 0, 0.11, 0.15);           // visor
  // 67.16: a neck, a face guard under the visor, the visor wrapping the sides, comms cups at the ears
  box(head, 0.12, 0.1, 0.12, p.dark, 0, 0, 0);
  box(head, 0.18, 0.08, 0.08, p.dark, 0, 0.025, 0.1);
  for (const sx of [-1, 1]) {
    box(head, 0.04, 0.06, 0.08, p.flash, sx * 0.125, 0.11, 0.1);
    box(head, 0.05, 0.09, 0.09, p.dark, sx * 0.165, 0.085, -0.01);
  }
}

/**
 * Build a person in the given palette, `height` world units tall (1.1 classic, 0.055 at
 * k_human = 1/20). Not baked: call bakePersonModel once after adding any team decals.
 */
export function buildPersonModel(p: UnitPalette, height: number, kit: PersonKit = {}): PersonModel {
  const root = new THREE.Group();
  root.scale.setScalar(height / PERSON_UNITS);
  const full = group(root, 0, 0, 0);
  const hips = group(full, 0, HIP_Y, 0);
  // 198: the soldier (soldierLooks.ts) dresses the same groups; the flak look (D, the owner's kept option) stays on the old
  // box body as the record, to rebuild on the new body if he wants it
  const bare = kit.armourLook !== 'D';
  const L = buildLeg(hips, -LEG_X, p, bare), R = buildLeg(hips, LEG_X, p, bare);
  const torso = group(hips, 0, 0, 0);
  const head = group(torso, 0, 0.62, 0);
  if (!bare) boxBody(torso, head, p);
  const armL = buildArm(torso, -SHOULDER_X, p, false, bare);
  const armR = buildArm(torso, SHOULDER_X, p, true, bare);
  const aim = group(torso, AIM_AT.x, AIM_AT.y, AIM_AT.z);
  const primary: HandGunKind = kit.primary ?? kit.longGun ?? 'rifle', sidearm = kit.sidearm ?? 'pistol';
  const rifle = buildPlayGun(primary, p), pistol = buildPlayGun(sidearm, p);   // 65.36: as play draws them (gunLooks GUN_IN_PLAY; 65.43's classics their own)
  aim.add(rifle.group);
  // 65.5: the pistol waits in a holster on the right hip; the slung rifle hangs across the back
  // (owner, Oct 10: grip back, barrel down and a little forward — the holster's axis tilts its foot forward, canted out from the hip)
  const holster = group(hips, 0.24, 0.02, -0.02); holster.rotation.set(-0.15, 0, 0.2);
  box(holster, 0.07, 0.2, 0.09, p.dark, 0, -0.075, 0);   // long enough to sheathe the barrel
  holster.add(pistol.group);
  const sling = group(torso, -0.12, 0.32, -0.36); sling.rotation.set(0, 0, 0.75);
  sling.rotation.x = Math.PI / 2 - 0.2;
  // S7: the LAW's home, strapped across the pack's top (its front to the holder's left), the spare behind it; built only for
  // someone who carries one (at the owner's size, a metre long, it no longer fits upright at the pack's side)
  const lawHome = group(torso, 0, 0.63, -0.22); lawHome.rotation.set(0, Math.PI / 2, 0);
  // 65.31: the 40 mm's, across the back the other way from the rifle's sling (the two cross), built only for someone who carries one
  const gl40Home = group(torso, 0.1, 0.34, -0.39); gl40Home.rotation.set(Math.PI / 2 - 0.2, 0, -0.75);
  const hh = { group: rifle.group, muzzle: rifle.muzzle, gripR: rifle.gripR, gripL: rifle.gripL };
  // 65.12: the pouches the kit carries (the roll, the dressing and a grenade in the hand are built when first shown)
  const pouches = (kind: GrenadeKind, n: number, side: number) => {
    for (let i = 0; i < Math.min(2, n); i++) {
      const pouch = group(torso, side * (0.12 + 0.075 * i), 0.02, 0.17);
      box(pouch, 0.068, 0.075, 0.06, p.dark, 0, 0, 0);
      const g = buildGrenade(kind, p); g.group.position.set(0, 0.045, 0); g.ring.visible = true; pouch.add(g.group);   // the top out of the pouch
    }
  };
  pouches('he', kit.grenades?.he ?? 0, -1);
  pouches('smoke', kit.grenades?.smoke ?? 0, 1);
  if (kit.medkit) { const med = buildMedPouch(p); med.position.set(-0.17, 0.0, -0.18); med.rotation.y = Math.PI; torso.add(med); }
  // The drone controller: a small slate held at the chest, its screen the flash
  const controller = group(torso, 0, 0.26, 0.34);
  box(controller, 0.3, 0.04, 0.18, p.dark, 0, 0, 0);
  box(controller, 0.2, 0.012, 0.11, p.flash, 0, 0.025, 0.01);
  const ctrlR = new THREE.Object3D(); ctrlR.position.set(0.13, -0.03, -0.02); controller.add(ctrlR);
  const ctrlL = new THREE.Object3D(); ctrlL.position.set(-0.13, -0.03, -0.02); controller.add(ctrlL);
  controller.rotation.x = 0.5;
  controller.visible = false;
  // 67.56: the helmet, the vest and the leader's kit (armourLooks.ts, below, once the model's groups are named)
  const armor = kit.armor ?? 'none', helmetKind = kit.helmet ?? 'light', look = kit.armourLook ?? 'B', wear = kit.wear ?? 'whole';
  const role: RoleDress | undefined = kit.role ? { role: kit.role, ...(kit.roleKit === false ? { kit: false } : {}) } : undefined;   // 201 (8)
  const { group: silhouette, poses: silhouettePoses } = buildSilhouette(p, armor, kit.leader === true, helmetKind, look, kit.plain === true, role);
  silhouette.visible = false;
  root.add(silhouette);
  const model: PersonModel = {
    root, full, silhouette, silhouettePoses, silhouettePose: 'stand', hips, torso, head,
    thighL: L.thigh, shinL: L.shin, footL: L.foot, thighR: R.thigh, shinR: R.shin, footR: R.foot,
    armL, armR, aim, handheld: hh.group, muzzle: hh.muzzle, gripR: hh.gripR, gripL: hh.gripL, controller, ctrlR, ctrlL,
    weapons: { rifle, pistol }, sling, holster, lawHome, gl40Home,
    tier: 'full', hand: 'left', longGun: kit.longGun ?? kinOf(primary) as LongGun, gunKinds: { rifle: primary, pistol: sidearm, rocket: 'rocket' }, kit: { palette: p, inHand: {} }, kitKey: [helmetKind !== 'light' ? `helmet:${helmetKind}` : '', armor !== 'none' ? `armor:${armor}` : '', look !== 'B' ? `look:${look}` : '', kit.leader ? 'leader' : '', kit.plain ? 'plain' : '', role ? `role:${role.role}${role.kit === false ? '-' : ''}` : ''].filter(Boolean).join(','),
  };
  // 67.56 / 67.57: on the groups that already move, so it bakes into their draws (no new draws)
  if (bare) buildSoldier(model, p, { plain: kit.plain, vest: armor, helmet: helmetKind, leader: kit.leader === true, wear, ...(role ? { role } : {}) });
  else dressArmour(model, p, { helmet: helmetKind, vest: armor, look, leader: kit.leader === true, wear });
  setPersonHand(model, kit.hand ?? 'right');
  updatePersonAnimation(model, createPersonAnim(), 0);
  return model;
}

/**
 * Fold static parts into one mesh per moving group (≈ 12 draws for the full tier, 1 for the
 * silhouette — one mesh per pose, one pose shown). Crowds will want instancing on top of this (§10.2 rule 4); not yet.
 */
export function bakePersonModel(m: PersonModel): number {
  const moving: THREE.Object3D[] = [m.hips, m.torso, m.head, m.thighL, m.shinL, m.footL, m.thighR, m.shinR, m.footR, m.armL.group, m.armL.fore, m.armR.group, m.armR.fore, m.aim];
  const isUnder = (o: THREE.Object3D, r: THREE.Object3D) => { for (let q = o.parent; q; q = q.parent) if (q === r) return true; return false; };
  let n = 0;
  const apart = [m.controller, m.weapons.rifle.group, m.weapons.pistol.group, m.gl40Home, ...kitParts(m)];   // these toggle or move between anchors, so they stay apart
  for (const g of moving) n += bakeStaticParts(g, { exclude: [...moving.filter(o => o !== g && isUnder(o, g)), ...apart] }).partsBaked;
  for (const w of [m.weapons.rifle, m.weapons.pistol]) n += bakeStaticParts(w.group, { exclude: [w.magazine] }).partsBaked;   // the magazine comes out for the reload
  m.silhouette.visible = true;               // bakeable only while visible
  for (const [key, q] of Object.entries(m.silhouettePoses)) { q.visible = true; n += bakeStaticParts(q).partsBaked; q.visible = key === m.silhouettePose; }
  setPersonTier(m, m.tier);
  return n;
}

/**
 * DECISIONS 384 lever 6: one built and baked person per look — the palette, the height and the kit — kept, and each person
 * after the first a copy of it sharing its geometry (and its materials, shared already). A death built the body from scratch,
 * and so did every view: ~4.5 ms of a frame's JS a death and 440 MB of the fight page's 2.4 GB in 30 s. The copy is the same
 * tree, at rest; what a person builds the first time it shows (a grenade in the hand, the LAW, the tool) is still its own.
 * The kept geometry is marked shared, so disposing a copy (disposeObjectTree) leaves it. Presentation only.
 */
const bakedLooks = new Map<string, PersonModel>();
const lookKey = (v: unknown): string => v !== null && typeof v === 'object'
  ? `{${Object.keys(v).sort().filter(k => (v as Record<string, unknown>)[k] !== undefined).map(k => `${k}:${lookKey((v as Record<string, unknown>)[k])}`).join(',')}}`
  : String(v);
export function bakedPersonModel(p: UnitPalette, height: number, kit: PersonKit = {}): PersonModel {
  return copyPersonModel(bakedLook(p, height, kit));
}

/** 384: the look's one baked figure made now if it isn't yet, without a copy — the load's warm-up (a first bake mid-match was 20–25 ms in one frame). */
export function bakePersonLook(p: UnitPalette, height: number, kit: PersonKit = {}): void {
  bakedLook(p, height, kit);
}

function bakedLook(p: UnitPalette, height: number, kit: PersonKit): PersonModel {
  const key = `${lookKey(p)}|${height}|${lookKey(kit)}`;
  let t = bakedLooks.get(key);
  if (!t) {
    t = buildPersonModel(p, height, kit);
    bakePersonModel(t);
    markTreeShared(t.root);
    bakedLooks.set(key, t);
  }
  return t;
}

/** A copy of `t` (built and baked, never drawn): modelCopy.ts copyModel; the kit's moving parts are built per person, the first time they show. */
function copyPersonModel(t: PersonModel): PersonModel {
  const m = copyModel({ ...t, kit: null } as unknown as PersonModel);
  m.kit = { palette: t.kit.palette, inHand: {} };
  return m;
}

export function setPersonTier(m: PersonModel, tier: PersonTier): void {
  m.tier = tier;
  m.full.visible = tier === 'full';
  m.silhouette.visible = tier === 'silhouette';
}

/** Which tier a person this many pixels tall should draw (the host measures the pixels). */
export function personTierFor(pixelHeight: number): PersonTier {
  return pixelHeight < MARKER_PX ? 'marker' : pixelHeight < SILHOUETTE_BELOW_PX ? 'silhouette' : 'full';
}

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------

const _t = new THREE.Vector3(), _t2 = new THREE.Vector3(), _t3 = new THREE.Vector3();
const _m = new THREE.Matrix4(), _mi = new THREE.Matrix4(), _pos = new THREE.Vector3(), _q = new THREE.Quaternion(), _sc = new THREE.Vector3();
const _cq = new THREE.Quaternion(), _cq2 = new THREE.Quaternion(), _ce = new THREE.Euler(0, 0, 0, 'YXZ');
const _gx = new THREE.Vector3(), _gy = new THREE.Vector3(), _gz = new THREE.Vector3(), _gq = new THREE.Quaternion(), _gm = new THREE.Matrix4();   // (63.8: port arms)
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const smooth = (x: number) => { const k = clamp01(x); return k * k * (3 - 2 * k); };
/** A hump over 0…1: 0 at the ends, 1 in the middle. */
const bell = (x: number) => Math.sin(Math.PI * clamp01(x));
/** `v` moved toward `to` by at most `step`. */
const toward = (v: number, to: number, step: number) => v + Math.max(-step, Math.min(step, to - v));
/** The belt pouch the spare magazine comes from (torso space, left hip). */
const POUCH = new THREE.Vector3(-0.36, -0.02, 0.22);   // outside the hip, in front of the belt: the arm never crosses the chest
const POLE_RELOAD = new THREE.Vector3(-0.8, -0.4, 0.6);   // the working elbow out to the left and forward, never back into the torso
/** Where each weapon is held, in torso space: the rifle's stock at the shoulder pocket; the pistol out in front of the chest, both arms extended (a two-hand stance). */
/**
 * Where the weapon's pivot sits in torso space. The rifle (owner, Oct 10: "seated in the shoulder")
 * pivots at the right shoulder pocket with its butt plate there (RIFLE_BUTT ahead to its origin); the
 * pistol is held out in front.
 */
/**
 * 407 (the coordinator, after the viewmodel's shots: the reload read only as the gun dipping): at the eye the reload is
 * lifted to the face — the gun up (person units), its muzzle raised and rolled so the well faces the eye (radians), eased
 * in over the reload's first `ease` and out over its last; the hands follow the gun (their spots are the gun's). Drawn
 * only in the viewmodel's pass (the figure is hidden from the world at its eye).
 */
export const EYE_RELOAD = { up: 0.14, back: 0.06, pitch: 0.3, roll: 0.45, ease: 0.15 };
/**
 * 73.9 step 3 (DIRECTION §0.14): the LAW at the eye. Shouldered, its tube ran past the eye (HOLD_AT.rocket stands level with
 * the shoulder), so its sight, its tube and the support hand filled half the view. At the eye it is moved aside: down, out to
 * the strong side and forward (torso space), the tube along the lower edge, its muzzle ahead of the crosshair's line. The
 * hands follow it (their spots are the tube's). Drawn only in the viewmodel's pass.
 */
export const EYE_LAW = { offset: new THREE.Vector3(0.1, -0.16, 0.12) };
const HOLD_AT: Record<GroundWeapon, THREE.Vector3> = { rifle: new THREE.Vector3(0.17, 0.46, 0.1), pistol: new THREE.Vector3(0.08, 0.45, 0.44), rocket: new THREE.Vector3(0.25, 0.6, 0.0), gl40: new THREE.Vector3(0.17, 0.46, 0.1) };   // (65.31: the 40 mm shouldered as the rifle is)   // (S7: the LAW on top of the right shoulder, out on the pad, clear of the helmet)
/**
 * S7: the LAW's throwaway (its "reload", WEAPON_LOOK.rocket): the spent tube lowered and dropped by `drop` of the way (the
 * host lays the spent tube there, rendering/groundWeapons.ts buildSpentLaw), the hands empty while the left reaches the pack,
 * the next drawn from it at `draw` (the spare gone from the pack) and extended by the end.
 */
export const LAW_THROW = { drop: 0.22, draw: 0.6 };
/** S7: where the left hand reaches for the next LAW (torso space: back to the pack's left side, by the spare). */
const LAW_PACK_REACH = new THREE.Vector3(-0.3, 0.62, -0.24);
/** The rifle stance: the torso bladed by this much (the right shoulder back) so the support hand reaches the handguard; the rifle and the face turn back to the heading. */
const RIFLE_BLADE = 0.55;
/** The support arm's comfortable reach (under its full length, so it never locks straight). */
const SUPPORT_REACH = 0.62;
/** The cheek on the stock: the head tilts this far toward the right shoulder while aiming the rifle. */
const CHEEK = 0.18;
/** Astride a motorcycle (rendering/vehicleModels.ts BIKE): the torso's lean to the bars, and the pegs from the feet's anchor under the saddle (forward, up). */
const SADDLE = { lean: 0.5, pegForward: 0.17, pegLift: 0.44 };
/** 67.63: a cab's seat: the hips over the floor, the lean (back: negative), how far ahead the feet are on the pedals. */
export const DRIVE_SEAT = { hip: 0.26, lean: -0.08, pedals: 0.62 };
/** 65.7: the bandage — the left forearm held across the body (the left palm, in torso space) and the right hand wrapping it, so many turns a second. */
const WOUND_L = new THREE.Vector3(-0.12, 0.2, 0.3);
const BANDAGE_LOOK = { wrapHz: 1.5, crouch: 0.6 };

/**
 * 65.5: which weapon rides the hand, and the draw between them. The weapon in hand is parented to
 * `aim`; the other to its sling (rifle) or holster (pistol). A switch reparents at once and, for
 * WEAPON_LOOK.draw seconds, eases each weapon from where it was to where it now lives (the pose it
 * had, carried into the new parent's space, blended to the parent's home pose).
 */
function placeWeapons(m: PersonModel, weapon: GroundWeapon, drawAt: number | undefined, t: number, stowed = false): void {
  const held = m.root.userData.held as string | undefined, want = stowed ? 'stowed' : weapon;
  const homes: Record<GroundWeapon, THREE.Object3D> = { rifle: m.sling, pistol: m.holster, rocket: m.lawHome, gl40: m.gl40Home };
  const kinds: GroundWeapon[] = ['rifle', 'pistol', ...(m.weapons.rocket ? ['rocket' as const] : []), ...(m.weapons.gl40 ? ['gl40' as const] : [])];
  if (held !== want) {
    // reparent each, carrying the world pose along so the draw can ease from it
    for (const k of kinds) {
      const w = m.weapons[k]!.group, parent = k === weapon && !stowed ? m.aim : homes[k];
      if (w.parent !== parent) {
        w.updateWorldMatrix(true, false); parent.updateWorldMatrix(true, false);
        _m.copy(parent.matrixWorld).invert().multiply(w.matrixWorld).decompose(_pos, _q, _sc);
        parent.add(w);
        w.position.copy(_pos); w.quaternion.copy(_q);
        w.userData.from = { pos: _pos.clone(), q: _q.clone() };
      }
    }
    m.root.userData.held = want;
    if (drawAt === undefined) for (const k of kinds) m.weapons[k]!.group.userData.from = undefined;
  }
  // each weapon eases from where it was (if a draw is running) to its parent's home pose
  const k = drawAt === undefined ? 1 : smooth((t - drawAt) / WEAPON_LOOK.draw);
  for (const kind of kinds) {
    const w = m.weapons[kind]!.group, from = w.userData.from as { pos: THREE.Vector3; q: THREE.Quaternion } | undefined;
    const home = kind === weapon && !stowed ? handPose(m, kind) : kind === 'pistol' ? HOLSTERED_POSE : SLUNG_POSE;   // (the LAW's home is its own frame, as the sling's)
    if (from && k < 1) { w.position.lerpVectors(from.pos, home.pos, k); w.quaternion.slerpQuaternions(from.q, home.q, k); }
    else { w.position.copy(home.pos); w.quaternion.copy(home.q); w.userData.from = undefined; }
  }
}
/** Each weapon's pose in its parent: in the hand (aim space), on the sling (sling space), in the holster (holster space). */
const HAND_POSE: Record<GroundWeapon, { pos: THREE.Vector3; q: THREE.Quaternion }> = { rifle: { pos: new THREE.Vector3(0, 0, RIFLE_BUTT), q: new THREE.Quaternion() }, pistol: { pos: new THREE.Vector3(0, 0, 0), q: new THREE.Quaternion() }, rocket: { pos: new THREE.Vector3(0, 0.07, 0.12), q: new THREE.Quaternion() }, gl40: { pos: new THREE.Vector3(0, 0, GL40.butt), q: new THREE.Quaternion() } };   // (S7: the tube resting on the shoulder by its radius, a little forward)   // the rifle's butt at the pivot
const SLUNG_POSE = { pos: new THREE.Vector3(0, 0, 0), q: new THREE.Quaternion() };
/** 65.12: the long gun's butt in the shoulder whatever gun it is (each has its own `butt`). */
const LONG_HAND = { pos: new THREE.Vector3(), q: new THREE.Quaternion() };
function handPose(m: PersonModel, kind: GroundWeapon): { pos: THREE.Vector3; q: THREE.Quaternion } {
  if (kind !== 'rifle') return HAND_POSE[kind];
  LONG_HAND.pos.set(0, 0, m.weapons.rifle.butt ?? RIFLE_BUTT);
  return LONG_HAND;
}
/**
 * 65.36: swap the gun in `slot` for `gun` where it is now (in the hand, on the sling or in the holster, its draw's easing
 * carried over), and return the one it replaces. PersonView swaps the player's own figure to a gun whose moving parts are
 * kept apart, and back.
 */
export function swapPersonGun(m: PersonModel, slot: 'rifle' | 'pistol' | 'gl40', gun: WeaponModel): WeaponModel {
  const old = m.weapons[slot]!, parent = old.group.parent;
  gun.group.position.copy(old.group.position); gun.group.quaternion.copy(old.group.quaternion);
  gun.group.userData.from = old.group.userData.from; gun.group.visible = old.group.visible;
  if (parent) { parent.remove(old.group); parent.add(gun.group); }
  if (slot === 'gl40') m.weapons.gl40 = gun as Gl40Model; else m.weapons[slot] = gun;
  if (slot === 'rifle') { m.handheld = gun.group; m.muzzle = gun.muzzle; m.gripR = gun.gripR; m.gripL = gun.gripL; }
  return old;
}

/**
 * 65.36: the gun's moving parts from the pose's clock — the weapon in hand cycles on each shot (`firedAt`) and through the
 * reload (`reloadAt` / `reloadFor`, which the view takes from the sim's reload), the other rests. Only a gun whose parts are
 * kept apart shows it (the player's own: PersonView.setOwn); on a baked gun the parts are folded in and nothing moves.
 */
export function poseGunParts(m: PersonModel, ctx: PersonAnim): void {
  const weapon = ctx.weapon ?? 'rifle';
  for (const slot of ['rifle', 'pistol', 'rocket', 'gl40'] as const) {
    const gk = slot === 'gl40' ? 'gl40' : m.gunKinds[slot], gun = m.weapons[slot], kind: GunKind = gk === 'missile' ? 'rocket' : gk;   // (65.43: a classic by itself — the Springfield's bolt, the Garand's lock; 65.49: the missile's launcher as the tube)
    if (!gun || !('parts' in gun)) continue;
    const parted = gun as WeaponModel & Parted;
    if (slot !== weapon) { setGunParts(parted, GUN_AT_REST); continue; }
    const spec = GROUND_WEAPONS[kind as keyof typeof GROUND_WEAPONS];
    const rk = ctx.reloadAt === undefined ? -1 : (ctx.time - ctx.reloadAt) / (ctx.reloadFor ?? spec.reload);
    const since = ctx.time - ctx.firedAt;
    setGunParts(parted, rk >= 0 && rk < 1 ? gunReloadPose(kind, rk) : kind === 'springfield' ? gunBoltShotPose(since, spec.interval) : gunShotPose(since, Math.min(0.16, 0.9 * spec.interval)));
  }
}

/** S7: the LAW, built the first time someone carries one (its moving parts stay apart: the inner tube, the sights, the covers; 65.36's D: its static parts baked, its trigger bar apart). */
function kitLaw(m: PersonModel): LawModel {
  if (!m.weapons.rocket) { const law = m.gunKinds.rocket === 'missile' ? buildAntiDropshipMissile(m.kit.palette) : buildPlayLaw(m.kit.palette, { kind: m.gunKinds.rocket }); m.lawHome.add(law.group); m.weapons.rocket = law; }   // (65.49 step 2: the anti-dropship missile's launcher, the owner's pick)
  return m.weapons.rocket;
}
/** 65.31: the 40 mm, built the first time someone carries one — its static parts one mesh each by material; the barrel (with its fore-end), the leaf and the round apart (they move). */
function kitGl40(m: PersonModel): Gl40Model {
  if (!m.weapons.gl40) {
    const w = buildPlayGl40(m.kit.palette);
    bakeGl40Kit(w);
    m.gl40Home.add(w.group); m.weapons.gl40 = w;
  }
  return m.weapons.gl40;
}
/** 65.31: the 40 mm's bake — its static parts one mesh each by material; the barrel (with its fore-end), the leaf and the round apart (they move; 65.36's D: the latch and the trigger fold into the body, unless the player's twin keeps them apart first). */
export function bakeGl40Kit(w: Gl40Model): void {
  bakeKitPart(w.leaf); bakeKitPart(w.magazine); bakeKitPart(w.barrel, [w.leaf, w.muzzle]); bakeKitPart(w.group, [w.barrel, w.magazine, w.gripR, w.gripL]);
}
/** S7: the spare LAW strapped behind it on the pack (collapsed; one baked mesh). */
function kitLawSpare(m: PersonModel): THREE.Group {
  if (!m.kit.lawSpare) { const g = buildPlayLaw(m.kit.palette, { bake: false, kind: m.gunKinds.rocket }).group; g.position.set(0.14, 0, 0); bakeKitPart(g); m.lawHome.add(g); m.kit.lawSpare = g; }   // (behind it: the home's +x is the holder's back)
  return m.kit.lawSpare;
}
/** 66.8: the entrenching tool, built the first time it is carried or dug with (folded on the pack's right side). */
function kitTool(m: PersonModel): EntrenchingTool {
  if (!m.kit.tool) {
    // baked as the other kit parts: the shaft and grip one mesh, the blade one, the carrier one (the blade folds, the carrier toggles)
    const tool = buildEntrenchingTool(m.kit.palette);
    bakeKitPart(tool.blade, [tool.carrier]); bakeKitPart(tool.carrier); bakeKitPart(tool.group, [tool.blade, tool.gripR, tool.gripL]);
    m.kit.tool = tool;
  }
  return m.kit.tool;
}
/** 66.8: the tool's place on the pack, folded (torso space): on the pack's back face, a little to the right, grip down. */
const TOOL_PACKED = { pos: new THREE.Vector3(0.07, 0.08, -0.36), fold: Math.PI };
/** 65.12: the kit's parts built so far. */
function kitParts(m: PersonModel): THREE.Object3D[] {
  return [m.kit.roll, m.kit.dressing, m.kit.inHand.he?.group, m.kit.inHand.smoke?.group, m.kit.tool?.group].filter((o): o is THREE.Group => !!o);
}
/** Bake a kit part to one mesh (it is small and shows rarely; `keep` stays apart: the pin's ring comes off). */
function bakeKitPart(g: THREE.Object3D, keep: THREE.Object3D[] = []): void {
  const was = g.visible; g.visible = true;
  bakeStaticParts(g, { exclude: keep });
  g.visible = was;
}
function kitRoll(m: PersonModel): THREE.Group {
  if (!m.kit.roll) { const r = buildBandageRoll(); r.position.set(0, -FOREARM - 0.04, 0.06); bakeKitPart(r); m.armR.fore.add(r); m.kit.roll = r; }
  return m.kit.roll;
}
function kitDressing(m: PersonModel): THREE.Group {
  if (!m.kit.dressing) { const d = buildDressing(); d.position.set(0, -FOREARM * 0.55, 0); bakeKitPart(d); m.armL.fore.add(d); m.kit.dressing = d; }
  return m.kit.dressing;
}
function kitGrenade(m: PersonModel, kind: GrenadeKind): GrenadeModel {
  let g = m.kit.inHand[kind];
  if (!g) { g = buildGrenade(kind, m.kit.palette); g.group.position.set(0, -FOREARM - 0.05, 0.045); bakeKitPart(g.group, [g.ring]); m.armR.fore.add(g.group); m.kit.inHand[kind] = g; }
  return g;
}
/**
 * 65.12: a grenade throw's pacing (seconds) and its beats as fractions: out of the pouch, the pin pulled, the wind-up, the
 * release. 67.60 (the coordinator's brief, Oct 19: "wind-up, release on the sim's tick, follow-through"): the sim lets the
 * grenade go the tick the throw comes in (core/grenades.ts throwGrenade), so the throw is drawn from that tick (`throwAt`,
 * the view's, from the sim's `throwReady`): the gun to the support hand, the strong hand up to the chest and wound back
 * behind the shoulder, the release over it `hold` seconds after the sim's (the drawn grenade held in the hand till then and
 * catching up with the sim's over `catchUp`: rendering/layer0Views.ts), the follow-through down across, the gun back up by
 * `seconds`. The sim's throw is quick — at the human scale ~95 person heights a second under ~380 a second² of felt
 * gravity, 24 heights in ~0.35 s — so the drawn wind-up is short (a held grenade would lag its flight). `on` false: no throw
 * drawn, as in play before (the view fed none). The fractions are the default `hold`'s.
 */
export const THROW_LOOK = { on: true, seconds: 0.75, hold: REAL_TIMING.throwWindSeconds, catchUp: 0.25, draw: 0.04 / 0.75, pin: 0.05 / 0.75, wind: 0.1 / 0.75, release: REAL_TIMING.throwWindSeconds / 0.75 };   // (216: the hold and the release the sim's wind-up, REAL_TIMING — one source)
/** 67.60: the throw's strong hand (torso space): at the chest, wound back behind the shoulder, the release over it, the follow-through down across. */
const THROW_HAND = { chest: new THREE.Vector3(0.14, 0.4, 0.22), back: new THREE.Vector3(0.34, 0.68, -0.24), release: new THREE.Vector3(0.2, 0.84, 0.4), through: new THREE.Vector3(-0.04, 0.3, 0.42) };
/**
 * 73.9 step 3: the throw at the eye (the one new pose). The world's wind-up goes behind the head, out of a view from the eye,
 * and the release sweeps the forearm across the lens. At the eye (it stands at about (0, 0.75, 0) in torso space) the strong
 * hand comes up in view at the lower right with the grenade, is cocked high at the right edge, released ahead over the
 * crosshair and follows through down to the left; the torso winds a `twist` share of the world's (a full wind would carry
 * the cocked hand out of view). The viewmodel follows the hand till `focus` seconds past the release, then the gun (the hand
 * goes back to it under the view). Drawn only in the viewmodel's pass.
 */
export const EYE_THROW = { chest: new THREE.Vector3(0.16, 0.55, 0.34), back: new THREE.Vector3(0.22, 0.82, 0.32), release: new THREE.Vector3(0.12, 0.86, 0.5), through: new THREE.Vector3(-0.06, 0.62, 0.48), twist: 0.2, focus: 0.25 };
/** 67.60: the long gun in the support hand through a throw (the aim's pivot from its hold, torso space; its pitch and turn): low, the muzzle ahead and down. */
export const THROW_GUN = { at: new THREE.Vector3(-0.22, -0.14, -0.1), pitch: 0.5, yaw: -0.55 };
/**
 * 67.60, the stances' transitions (the coordinator's brief, Oct 19: "crouch and prone, with transitions"): the sim changes
 * stance in a tick (its eye and its hit shape at once); the drawn body goes there over `seconds` (by the step), eased from
 * the pose it was drawn in — standing to prone and back through the kneel (the next step begins at `via` of the first),
 * the support hand to the ground ahead on the way down and pushing off it on the way up (`plant`: the hand's ground spot
 * in the body's own frame, and the step's share it is down over). Crouched, the kneel and the low walk are steps too
 * (`crouch:move`). In and out of cover and a dig, `cover`. `on` false: at once, as before.
 */
export const STANCE_LOOK = {
  on: true, via: REAL_TIMING.stanceVia, cover: 0.3,
  // (216: the stances' own steps and the kneel's `via` are the sim's, REAL_TIMING — one source; the low walk's steps are the view's)
  seconds: { ...REAL_TIMING.stanceSeconds, 'crouch>crouch:move': 0.2, 'crouch:move>crouch': 0.25, 'stand>crouch:move': 0.24, 'crouch:move>stand': 0.28 } as Record<string, number>,
  plant: { at: [-0.2, 0.02, 0.62] as V3, down: [0.15, 0.9] as const, up: [0, 0.75] as const },
};
// the barrel down the holster's axis, the slide facing forward, the grip out of the top pointing back (it was turned the other way: barrel up, grip forward)
const HOLSTERED_POSE = { pos: new THREE.Vector3(0, 0.03, 0.02), q: new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0)) };

/** A descendant of `aim`, in torso space (where the arms' shoulders live). */
function inTorso(m: PersonModel, obj: THREE.Object3D, out: THREE.Vector3): THREE.Vector3 {
  out.set(0, 0, 0);
  for (let o: THREE.Object3D | null = obj; o && o !== m.torso; o = o.parent) { o.updateMatrix(); out.applyMatrix4(o.matrix); }
  return out;
}

/** 65.12: the throw's hand positions (torso space) at its beats — each hand eases from one to the next. */
const THROW_BEATS: Array<{ at: number; r: THREE.Vector3; l: THREE.Vector3 }> = [
  { at: 0, r: REST_R.clone(), l: REST_L.clone() },
  { at: THROW_LOOK.draw, r: new THREE.Vector3(0.12, 0.32, 0.3), l: new THREE.Vector3(-0.2, 0.2, 0.2) },     // the grenade out, at the chest
  { at: THROW_LOOK.pin, r: new THREE.Vector3(0.1, 0.36, 0.32), l: new THREE.Vector3(0.0, 0.4, 0.36) },      // the left hand pulls the pin
  { at: THROW_LOOK.wind, r: new THREE.Vector3(0.34, 0.72, -0.26), l: new THREE.Vector3(-0.14, 0.62, 0.52) }, // wound back; the left arm points the way
  { at: THROW_LOOK.release, r: new THREE.Vector3(0.2, 0.82, 0.38), l: new THREE.Vector3(-0.3, 0.38, 0.3) },  // over the shoulder: the release
  { at: 1, r: new THREE.Vector3(-0.04, 0.26, 0.4), l: REST_L.clone() },                                       // the follow-through, down across
];

/**
 * 67.60, the reloads in hand (the coordinator's brief, Oct 19: "the D rifle's mag swap, the LMG's box, the rocket tube's
 * reload, the 40 mm's break-open", standing, crouched and in cover): each gun's reload as beats across the sim's reload
 * time — where each hand is, where the magazine is, how the gun is held for the work — eased from beat to beat. A hand's
 * spot is its own grip, a point of the gun (`g`, the gun's own space), of the seated magazine (`w`, from the well), of the
 * 40 mm's barrel (`b`, it breaks open), of the body (`t`, torso space), the LMG's feed cover's rear (`cover`, lifted with it)
 * or the LAW's rear cap (`rear`, pulled out with its inner tube). The magazine is seated (`well`), in the support hand
 * (`hand`: carried by the palm where the arm reached) or out of sight (`away`: in a pouch, dropped). The hold: the gun
 * rolled (`roll`, its well to the support hand), dipped (`dip`, radians down), lifted (`lift`, torso space: in to the
 * chest), the head down to the work (`head`). A beat leaves out what does not change. The gun's moving parts keep
 * gunReloadPose's beats (the feed cover, the charging handle, the lever, the latch). `on` false: as before (65.5).
 */
type V3 = readonly [number, number, number];
type ReloadSpot = 'grip' | 'cover' | 'rear' | { g: V3 } | { w: V3 } | { b: V3 } | { t: V3 };
type ReloadMag = 'well' | 'hand' | 'away';
interface ReloadKey { at: number; l?: ReloadSpot; r?: ReloadSpot; mag?: ReloadMag; roll?: number; dip?: number; lift?: V3; head?: number }
type ReloadBeat = Required<ReloadKey>;
export type ReloadFamily = 'rifle' | 'smg' | 'box' | 'pistol' | 'lmg' | 'gl40' | 'rocket';
const add3 = (a: V3, b: V3, s = 1): V3 => [a[0] + s * b[0], a[1] + s * b[1], a[2] + s * b[2]];
/** Where the hands fetch from (torso space): the fresh magazine from the chest's pouch, the spent one into the hip's (POUCH); an LMG's box let fall by the hip; the 40 mm's round from the vest, its case flicked away. */
const MAG_POUCH: V3 = [-0.12, 0.16, 0.24], HIP_POUCH: V3 = [POUCH.x, POUCH.y, POUCH.z], BOX_DROP: V3 = [-0.32, -0.12, 0.3], ROUND_POCKET: V3 = [-0.06, 0.24, 0.24], CASE_FLICK: V3 = [-0.34, 0.06, 0.34];
/** A magazine's swap from `from` (the beats' share where the hand reaches it): `grab` the palm on it (from the well), out and in along `out`. */
function magSwap(from: number, grab: V3, out: V3, roll: number, dip: number, lift: V3): ReloadKey[] {
  const s = (k: number) => from + k;
  return [
    { at: s(0), l: { w: grab }, roll, dip, lift, head: 0.38 },        // the hand to it, the gun rolled down to the hand
    { at: s(0.05), mag: 'hand' },                                     // gripped (the release pressed)
    { at: s(0.17), l: { w: add3(grab, out) } },                       // stripped out of the well
    { at: s(0.3), l: { t: HIP_POUCH } },                              // into the pouch at the hip
    { at: s(0.34), mag: 'away' },
    { at: s(0.42), l: { t: MAG_POUCH } },                             // the fresh one from the chest
    { at: s(0.46), mag: 'hand' },
    { at: s(0.58), l: { w: add3(grab, out, 0.6) } },                  // lined up under the well
    { at: s(0.66), l: { w: grab } },                                  // seated
    { at: s(0.68), mag: 'well', l: { w: add3(grab, [0, 0.014, 0]) } },   // the tap home
    { at: s(0.7), l: { w: grab } },
  ];
}
/** The rifle's charging handle's latch, the SMG's lever, the LMG's handle (each gun's own space, D's models). */
const AR_CHARGER: V3 = [-0.02, 0.064, -0.05], UMP_LEVER: V3 = [-0.05, 0.03, 0.2], LMG_CHARGER: V3 = [0.085, 0.03, 0.16];
const RELOAD_KEYS: Record<ReloadFamily, ReloadKey[]> = {
  // the D rifle (2.2 s; its charging handle drawn 0.88–0.93, home by 0.96): the magazine swapped, the support hand up over
  // the top to the handle, pulled and let go
  rifle: [
    { at: 0, l: 'grip', r: 'grip', mag: 'well', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
    ...magSwap(0.07, [-0.03, -0.08, 0.03], [0, -0.15, 0.06], -0.4, 0.3, [-0.02, -0.06, 0.07]),
    { at: 0.84, l: { g: AR_CHARGER }, roll: 0, dip: 0.12, head: 0.2 },
    { at: 0.88 },
    { at: 0.93, l: { g: add3(AR_CHARGER, [0, 0, -0.075]) } },
    { at: 0.96, l: { g: add3(AR_CHARGER, [-0.04, 0.03, -0.03]) } },
    { at: 1, l: 'grip', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
  ],
  // the D SMG (its lever on the support side: pulled and locked at the start, slapped home at 0.88–0.91)
  smg: [
    { at: 0, l: 'grip', r: 'grip', mag: 'well', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
    { at: 0.04, l: { g: UMP_LEVER }, dip: 0.12, head: 0.2 },
    { at: 0.1, l: { g: add3(UMP_LEVER, [0, 0, -0.11]) } },
    ...magSwap(0.15, [-0.03, -0.09, 0.0], [0, -0.16, 0.03], -0.4, 0.3, [-0.02, -0.06, 0.07]),
    { at: 0.84, l: { g: add3(UMP_LEVER, [-0.01, 0.02, -0.11]) }, roll: 0, dip: 0.12, head: 0.2 },
    { at: 0.88 },
    { at: 0.91, l: { g: add3(UMP_LEVER, [-0.02, 0.03, 0.04]) } },
    { at: 1, l: 'grip', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
  ],
  // a classic with a box magazine (the Thompson, the BAR): the swap; its handle works itself (its parts as gunReloadPose has them)
  box: [
    { at: 0, l: 'grip', r: 'grip', mag: 'well', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
    ...magSwap(0.08, [-0.03, -0.08, 0.03], [0, -0.15, 0.05], -0.4, 0.3, [-0.02, -0.06, 0.07]),
    { at: 0.86, l: 'grip', roll: 0, dip: 0.08, head: 0.1 },
    { at: 1, dip: 0, lift: [0, 0, 0], head: 0 },
  ],
  // the pistol (1.5 s; the slide locked back until the strong thumb drops it at 0.9): the magazine out of the grip and the next in from under it
  pistol: [
    { at: 0, l: 'grip', r: 'grip', mag: 'well', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
    ...magSwap(0.05, [0, -0.106, -0.03], [0, -0.12, -0.035], -0.3, 0.3, [0, -0.04, -0.08]),
    { at: 0.86, l: 'grip', roll: 0, dip: 0.1, head: 0.15 },
    { at: 1, dip: 0, lift: [0, 0, 0], head: 0 },
  ],
  // the D LMG (3.4 s; the feed cover up 0.04–0.14 and shut 0.82–0.88, the handle drawn 0.89–0.93 and home by 0.97): the
  // support hand lifts the cover, swaps the box, lays the belt on the tray, shuts the cover; the strong hand works the handle
  lmg: [
    { at: 0, l: 'grip', r: 'grip', mag: 'well', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
    { at: 0.04, l: 'cover', dip: 0.32, lift: [-0.04, -0.06, 0.04], head: 0.45 },
    { at: 0.14 },
    { at: 0.2, l: { w: [-0.085, -0.06, 0] }, roll: -0.25 },
    { at: 0.24, mag: 'hand' },
    { at: 0.32, l: { w: [-0.13, -0.2, 0.02] } },
    { at: 0.4, l: { t: BOX_DROP } },                                  // let fall
    { at: 0.42, mag: 'away' },
    { at: 0.5, l: { t: HIP_POUCH } },                                 // the next from the hip
    { at: 0.53, mag: 'hand' },
    { at: 0.62, l: { w: [-0.125, -0.17, 0.02] } },
    { at: 0.68, l: { w: [-0.085, -0.06, 0] } },                       // hung under the feed
    { at: 0.7, mag: 'well' },
    { at: 0.76, l: { g: [-0.04, 0.085, 0.13] }, roll: 0 },            // the belt laid on the tray
    { at: 0.8, l: 'cover' },
    { at: 0.84, r: 'grip' },
    { at: 0.88 },                                                     // the cover shut under the hand
    { at: 0.9, r: { g: LMG_CHARGER } },
    { at: 0.93, r: { g: add3(LMG_CHARGER, [0, 0, -0.1]) } },
    { at: 0.95, l: 'grip' },
    { at: 0.97, r: { g: LMG_CHARGER } },
    { at: 1, r: 'grip', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
  ],
  // the D 40 mm (2.5 s; broken open by 0.12, shut 0.88–0.98): the muzzle down, the support hand riding the fore-end down,
  // the spent case drawn out and flicked away, a round from the vest pushed in, the gun flicked up shut
  gl40: [
    { at: 0, l: 'grip', r: 'grip', mag: 'away', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
    { at: 0.02, l: { b: [0, -0.1 - GL40.hinge.y, GL40.gripAt - GL40.hinge.z] }, dip: 0.15, head: 0.25 },
    { at: 0.12, dip: 0.55, lift: [-0.04, -0.04, 0.04], head: 0.45 },
    { at: 0.14, mag: 'well' },                                        // the spent case in the chamber
    { at: 0.17, l: { w: gl40Along(-0.055) } },
    { at: 0.19, mag: 'hand' },
    { at: 0.28, l: { w: gl40Along(-0.17) } },                         // drawn out along the bore
    { at: 0.33, l: { t: CASE_FLICK } },
    { at: 0.34, mag: 'away' },                                        // flicked away
    { at: 0.46, l: { t: ROUND_POCKET } },
    { at: 0.5, mag: 'hand' },                                         // a round from the vest
    { at: 0.64, l: { w: gl40Along(-0.15) } },
    { at: 0.74, l: { w: gl40Along(-0.055) } },                        // pushed in
    { at: 0.76, mag: 'well' },
    { at: 0.84, l: { b: [0, -0.1 - GL40.hinge.y, GL40.gripAt - GL40.hinge.z] } },
    { at: 0.88, mag: 'away' },                                        // (in the bore: hidden as it shuts)
    { at: 0.96, dip: 0, lift: [0, 0, 0], head: 0 },                   // flicked up shut
    { at: 1, l: 'grip' },
  ],
  // the LAW's throwaway (3 s; LAW_THROW: the spent tube dropped at 0.22, the next drawn from the pack at 0.6): lowered off the
  // shoulder and let go, the support hand back over the shoulder to the pack, the next brought round in front, pulled open by
  // its rear cap (the inner tube out, RELOAD_LOOK.extend), up onto the shoulder
  rocket: [
    { at: 0, l: 'grip', r: 'grip', mag: 'well', roll: 0, dip: 0, lift: [0, 0, 0], head: 0 },
    { at: 0.16, lift: [0.04, -0.18, 0.14], dip: 0.45, head: 0.2 },    // lowered off the shoulder
    { at: 0.22 },                                                     // let go (the host lays it on the ground)
    { at: 0.28, l: { t: [REST_L.x, REST_L.y, REST_L.z] }, r: { t: [REST_R.x, REST_R.y, REST_R.z] } },
    { at: 0.38, l: { t: [LAW_PACK_REACH.x, LAW_PACK_REACH.y, LAW_PACK_REACH.z] }, lift: [-0.42, 0.02, -0.26], dip: -0.2, head: 0 },   // to the pack (the next tube's place ready behind the shoulder)
    { at: 0.58 },
    { at: 0.6, l: 'grip' },                                           // the next off the pack
    { at: 0.68, r: 'rear', lift: [-0.1, -0.2, 0.18], dip: 0.25, head: 0.3 },   // round in front, the strong hand to the rear cap
    { at: 0.86, r: 'rear' },                                          // pulled open
    { at: 0.92, r: 'grip', lift: [0, 0, 0], dip: 0, head: 0 },        // up onto the shoulder
    { at: 1 },
  ],
};
/** The 40 mm's chamber, `d` along the bore from the round's centre (the well, broken open). */
function gl40Along(d: number): V3 { return [0, -Math.sin(GL40.open) * d, Math.cos(GL40.open) * d]; }
/** The beats with what each leaves out carried from the one before. */
const RELOAD_BEATS = Object.fromEntries(Object.entries(RELOAD_KEYS).map(([k, keys]) => {
  const out: ReloadBeat[] = [];
  for (const key of keys) out.push({ ...(out[out.length - 1] ?? keys[0]), ...key } as ReloadBeat);
  return [k, out];
})) as Record<ReloadFamily, ReloadBeat[]>;
/** Where the support palm holds each family's magazine, from its origin (the first spot it reaches for on it). */
const RELOAD_GRAB = Object.fromEntries(Object.entries(RELOAD_KEYS).map(([k, keys]) => {
  const w = keys.find(b => b.l && typeof b.l === 'object' && 'w' in b.l)?.l as { w: V3 } | undefined;
  return [k, w?.w ?? [0, 0, 0]];
})) as Record<ReloadFamily, V3>;
/** 67.60: the reloads' switch, and the LAW's extension's share (from, to: the inner tube pulled out by its rear cap). */
export const RELOAD_LOOK = { on: true, extend: [0.68, 0.86] as const };
/** The reload a gun has (absent: as before — the clips loaded from the top). */
function reloadFamily(m: PersonModel, weapon: GroundWeapon): ReloadFamily | null {
  if (!RELOAD_LOOK.on) return null;
  if (weapon === 'rocket' || weapon === 'gl40') return weapon;
  const kind = weapon === 'pistol' ? m.gunKinds.pistol : m.gunKinds.rifle;
  return kind === 'rifle' ? 'rifle' : kind === 'smg' ? 'smg' : kind === 'lmg' ? 'lmg' : kind === 'pistol' || kind === 'm1911' ? 'pistol' : kind === 'thompson' || kind === 'bar' ? 'box' : null;
}
/** The beats either side of `rk` and the eased share between them. */
function reloadAt(beats: ReloadBeat[], rk: number): { a: ReloadBeat; b: ReloadBeat; k: number } {
  let i = 0; while (i < beats.length - 2 && rk >= beats[i + 1].at) i++;
  const a = beats[i], b = beats[i + 1];
  return { a, b, k: smooth((rk - a.at) / Math.max(1e-6, b.at - a.at)) };
}
/** `obj`'s frame in torso space (it is under the aim, or the torso itself). */
function torsoFrame(m: PersonModel, obj: THREE.Object3D, out: THREE.Matrix4): THREE.Matrix4 {
  out.identity();
  for (let o: THREE.Object3D | null = obj; o && o !== m.torso; o = o.parent) { o.updateMatrix(); out.premultiply(o.matrix); }
  return out;
}
const _rm = new THREE.Matrix4(), _ra = new THREE.Vector3(), _rb = new THREE.Vector3();
/** A reload spot, in torso space now. */
function reloadSpot(m: PersonModel, gun: WeaponModel, s: ReloadSpot, hand: 'l' | 'r', rk: number, out: THREE.Vector3): THREE.Vector3 {
  if (s === 'grip') return inTorso(m, hand === 'l' ? gun.gripL : gun.gripR, out);
  if (s === 'rear') return inTorso(m, (gun as LawModel).rearCover, out);
  if (s === 'cover') {
    // the LMG's feed cover's rear, lifted with the cover (hinged at its front: its rear 0.22 behind and 0.04 up, as gunReloadPose opens it)
    const open = 1.25 * gunReloadPose('lmg', rk).cover, c = Math.cos(open), sn = Math.sin(open);
    return out.set(-0.02, 0.065 + 0.05 * c + 0.22 * sn, 0.2 + 0.05 * sn - 0.22 * c).applyMatrix4(torsoFrame(m, gun.group, _rm));
  }
  if ('t' in s) return out.set(s.t[0], s.t[1], s.t[2]);
  if ('g' in s) return out.set(s.g[0], s.g[1], s.g[2]).applyMatrix4(torsoFrame(m, gun.group, _rm));
  if ('b' in s) return out.set(s.b[0], s.b[1], s.b[2]).applyMatrix4(torsoFrame(m, (gun as Gl40Model).barrel, _rm));
  return out.set(gun.well.x + s.w[0], gun.well.y + s.w[1], gun.well.z + s.w[2]).applyMatrix4(torsoFrame(m, gun.magazine.parent ?? gun.group, _rm));
}
/** 67.60: the joints a stance's step eases (each position and turn), and the support hand's plant (module scratch; set each pose). */
const _stanceJoints = new WeakMap<PersonModel, THREE.Object3D[]>();
const stanceJoints = (m: PersonModel) => { let j = _stanceJoints.get(m); if (!j) _stanceJoints.set(m, j = [m.full, m.hips, m.torso, m.thighL, m.thighR, m.shinL, m.shinR, m.footL, m.footR, m.head, m.aim]); return j; };
const STANCE_JOINTS = 11;
const _plant = { w: 0, at: new THREE.Vector3() };
const POLE_PLANT = new THREE.Vector3(-1, 0.2, -0.4);   // the planted arm's elbow out and back
const _sq = new THREE.Quaternion(), _sq2 = new THREE.Quaternion(), _sp = new THREE.Vector3();
function snapStance(m: PersonModel, out: Float64Array): void {
  stanceJoints(m).forEach((o, i) => { o.position.toArray(out, i * 7); o.quaternion.toArray(out, i * 7 + 3); });
}
function blendStance(m: PersonModel, from: Float64Array, k: number): void {
  stanceJoints(m).forEach((o, i) => {
    o.position.lerpVectors(_sp.fromArray(from, i * 7), o.position, k);
    _sq2.copy(o.quaternion); o.quaternion.copy(_sq.fromArray(from, i * 7 + 3)).slerp(_sq2, k);
  });
}
/** Where an arm's palm lands reaching for `target` (solveArm's reach, clamped as it clamps). */
function palmAt(arm: IkArm, target: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 {
  const s = arm.group.position, d = out.copy(target).sub(s), len = d.length();
  const reach = Math.min(arm.a + arm.b - 1e-3, Math.max(Math.abs(arm.a - arm.b) + 1e-3, len));
  return d.multiplyScalar(len > 1e-9 ? reach / len : 0).add(s);
}

/** Pose the full tier from the context. Presentation only; deterministic in (ctx, time). */
export function updatePersonAnimation(m: PersonModel, ctx: PersonAnim, dt: number): void {
  _press.on = false;
  ctx.handsOn = undefined;   // (73.9 step 3: set again below where the pose reaches the hands)
  poseFullTier(m, ctx, dt);
  // 66.7 (a), pressed: the body slid along its facing until it first touches the cover's face — over low cover only what is
  // under its top (the chest and forearms come over it), round an edge only what is inside it (the lean goes past), at a
  // crest the head (at the lip, over it out). The face stands at the walk's contact at the ground and at `gap` at the chest
  // (a boulder's foot bulges out under its face), so the feet stop at the foot; round an edge or a corner the upper body
  // then leans in over the bulge until it touches too (`coverLeanIn`, eased over a few frames, read by the next pose).
  if (_press.on) {
    // (over low cover the arms come out over it with the peek: the forearms rest on the top)
    const face = _press.face, withArms = pressRoom(m, face, _press.below, _press.headOnly, _press.edge);
    const room = _press.overTop > 0 ? withArms.all + (pressRoom(m, face, _press.below, false, 0, false).all - withArms.all) * _press.overTop : withArms.all;
    const shift = Math.min(PRESS.reach, room - PRESS.margin + _press.over);
    if (Number.isFinite(shift)) {
      const z = _press.slideZ + shift * _press.k;
      m.hips.position.z = z; m.hips.position.y = _press.base + z * _press.sz; m.silhouette.position.z = z;
    }
    if (_press.leanIn && _press.square > 0.5 && (ctx.coverPeek ?? 0) < 0.02 && Number.isFinite(withArms.upper) && Number.isFinite(withArms.legs)) {   // (found while down, square to it; kept through the peek)
      // more lean while the upper body has room left after the slide and the feet have none, less the other way round
      const upper = withArms.upper - withArms.all, legs = withArms.legs - withArms.all;
      ctx.coverLeanIn = Math.max(0, Math.min(PRESS.leanIn.max, (ctx.coverLeanIn ?? 0) + (upper - legs) * PRESS.leanIn.gain));
    }
  }
  if (!_press.leanIn) ctx.coverLeanIn = undefined;
}
/** 66.7 (a), pressed: what the pose leaves for the press (module scratch; set each call). */
const _press = { on: false, below: Infinity, headOnly: false, edge: 0, face: { foot: 0, gap: 0, chest: 1 }, over: 0, base: 0, sz: 0, slideZ: 0, overTop: 0, leanIn: false, k: 1, square: 1 };

/** DECISIONS 401: the clip layer's ease in and out (seconds), and its sampled pose (module scratch). */
const CLIP_EASE = 0.15;
const _clip = createClipPose(), _idle = createClipPose();
/**
 * DECISIONS 410 (3, 5): the idle under the clip layer. `aimed`: the share of the idle's sway (the hips' and the chest's turn
 * from the loop's mean) kept while aiming — the gun steadies; `soles`: the contact band — a boot this high over its ground
 * (person units) or higher in the clip is in the air, not planted; below, it is eased down onto the ground and turned along
 * the slope; `breath`: the idle chest's turn from its
 * mean × this (the library breathes in its upper chest, `spine.003`; our one-piece torso takes its lower spine's, a quarter
 * the size). Starting dials, the owner's to tune.
 */
export const CLIP_IDLE = { aimed: 0.3, soles: 0.08, breath: 2.5 };
/**
 * DECISIONS 410 (5): the share of the clip chest's swing (its turn from the loop's mean, and the hips' bob) the arms take out
 * of the gun — aimed, walking with it low, at the move's low ready; `bobEase` the hips' eased height (seconds). Starting dials.
 */
export const CLIP_GUN = { aimed: 0.8, walk: 0.35, move: 0.5, bobEase: 0.4 };
const _jq = new THREE.Quaternion(), _jq2 = new THREE.Quaternion(), _sole = new THREE.Vector3(), _ank = new THREE.Vector3(), _xAxis = new THREE.Vector3(1, 0, 0), _zAxis = new THREE.Vector3(0, 0, 1);
const J_HIPS = PERSON_CLIP_JOINTS.indexOf('hips'), J_TORSO = PERSON_CLIP_JOINTS.indexOf('torso');
/** 410 (2): the boot's bottom at its heel, under the ankle and at its toe (buildLeg's boot: 0.31 long, 0.04 ahead of the ankle). */
const BOOT_Z = [-0.115, 0, 0.195];
/**
 * 410 (2): a boot's lowest point (its heel, middle or toe, SOLE under the ankle's level) in the hips' parent's frame — lowest
 * over the plane rising `sx` across and `sz` ahead (a heel strike sets the heel down, a push-off the toe) — from the hips'
 * turn and place, the thigh's place on them (its x), the thigh's, shin's and foot's turns.
 */
function soleAt(hq: THREE.Quaternion, hp: THREE.Vector3, x: number, tq: THREE.Quaternion, sq: THREE.Quaternion, fq: THREE.Quaternion, out: THREE.Vector3, sx = 0, sz = 0): THREE.Vector3 {
  const ankle = _sa.set(0, -SHIN, 0).applyQuaternion(sq); ankle.y -= THIGH;
  ankle.applyQuaternion(tq); ankle.x += x; ankle.applyQuaternion(hq).add(hp);
  const turn = _bootQ.copy(hq).multiply(tq).multiply(sq).multiply(fq);
  let best = Infinity;
  for (const z of BOOT_Z) {
    const p = _sb.set(0, -SOLE, z).applyQuaternion(turn).add(ankle), over = p.y - sx * p.x - sz * p.z;
    if (over < best) { best = over; out.copy(p); }
  }
  return out;
}
const _sa = new THREE.Vector3(), _sb = new THREE.Vector3(), _bootQ = new THREE.Quaternion();
const clipGrounds = new Map<PersonClipData, number>();
/**
 * 410 (2): a clip's ground on our legs — the lower boot's height a tenth of the way up its frames (the mannequin's legs and
 * feet are not ours: its soles sit a little in or over its ground; the lowest ever is one deep toe, and a run's flight frames
 * are fewer than its contact's).
 */
function clipGround(c: PersonClipData): number {
  let g = clipGrounds.get(c);
  if (g === undefined) {
    const n = PERSON_CLIP_JOINTS.length, q = [0, 1, 2, 3].map(() => new THREE.Quaternion()), hp = new THREE.Vector3(), lows: number[] = [];
    for (let f = 0; f < c.frames; f++) {
      const at = (j: number, o: THREE.Quaternion) => o.set(c.q[(f * n + j) * 4], c.q[(f * n + j) * 4 + 1], c.q[(f * n + j) * 4 + 2], c.q[(f * n + j) * 4 + 3]);
      at(J_HIPS, q[0]); hp.set(c.hip[f * 3], c.hip[f * 3 + 1], c.hip[f * 3 + 2]);
      let low = Infinity;
      for (const [x, j] of [[-LEG_X, 2], [LEG_X, 5]] as const) low = Math.min(low, soleAt(q[0], hp, x, at(j, q[1]), at(j + 1, q[2]), at(j + 2, q[3]), _sole).y);
      lows.push(low);
    }
    lows.sort((a, b) => a - b);
    g = lows[Math.floor(lows.length * 0.1)];
    clipGrounds.set(c, g);
  }
  return g;
}
/** 410 (2): each boot's lowest point over the clip's ground in a sampled pose (as baked, before widenStride), into `out` [left, right]. */
function clipSoles(pose: PersonClipPose, out: [number, number]): void {
  const g = clipGround(pose.a) + (pose.b ? (clipGround(pose.b) - clipGround(pose.a)) * pose.k : 0);
  out[0] = soleAt(pose.q[J_HIPS], pose.hip, -LEG_X, pose.q[2], pose.q[3], pose.q[4], _sole).y - g;
  out[1] = soleAt(pose.q[J_HIPS], pose.hip, LEG_X, pose.q[5], pose.q[6], pose.q[7], _sole).y - g;
}
/** 410 (2): the hip-to-ankle reach (from the hip) of a thigh and shin's turns, into `out` (the thigh's parent's frame). */
function reachOf(tq: THREE.Quaternion, sq: THREE.Quaternion, out: THREE.Vector3): THREE.Vector3 {
  out.set(0, -SHIN, 0).applyQuaternion(sq); out.y -= THIGH;
  return out.applyQuaternion(tq);
}
/** The knee's inner angle for a hip-to-ankle reach `d`. */
const kneeInner = (d: number) => Math.acos(Math.max(-1, Math.min(1, (THIGH * THIGH + SHIN * SHIN - d * d) / (2 * THIGH * SHIN))));
/**
 * 410 (2): a leg's ankle raised `up` (straight up in the hips' parent's frame) — the knee folded to the new reach, then the
 * thigh swung from the folded ankle onto it; the boot keeps its turn in the hips' frame. Exact for any splay or turn of the leg.
 */
function raiseAnkle(hq: THREE.Quaternion, tq: THREE.Quaternion, sq: THREE.Quaternion, fq: THREE.Quaternion, up: number): void {
  const foot = _rq.copy(tq).multiply(sq).multiply(fq);   // (the boot's turn in the hips' frame, kept)
  const was = reachOf(tq, sq, _ank), to = _rv.set(0, up, 0).applyQuaternion(_rq2.copy(hq).invert()).add(was);
  const d0 = was.length(), d1 = Math.min(THIGH + SHIN - 1e-6, Math.max(0.05, to.length()));
  sq.premultiply(_jq.setFromAxisAngle(_xAxis, kneeInner(d0) - kneeInner(d1)));
  tq.premultiply(_jq.setFromUnitVectors(reachOf(tq, sq, _ank).normalize(), to.normalize()));
  fq.copy(_rq2.copy(tq).multiply(sq).invert().multiply(foot));
}
const _rq = new THREE.Quaternion(), _rq2 = new THREE.Quaternion(), _rv = new THREE.Vector3();
const _soles: [number, number] = [0, 0], _idleSoles: [number, number] = [0, 0];

function poseFullTier(m: PersonModel, ctx: PersonAnim, dt: number): void {
  ctx.time += dt;
  const t = ctx.time;
  // thigh > 0 swings the leg forward; knee > 0 folds the shin back; lean > 0 tips the torso forward
  let hipY = HIP_Y, lean = 0, thighL = 0, thighR = 0, kneeL = 0, kneeR = 0, bob = 0;
  // the whole body's pitch: prone lies forward along the ground
  let body = 0;
  // feet to be placed by the two-link solve (sole `forward` of the hip, `lift` off the ground): the
  // walks, and any standing pose on a slope; null = fixed leg angles
  let footL: { forward: number; lift: number } | null = null, footR: { forward: number; lift: number } | null = null;
  // the hips' and the shoulders' counter-twist in the walk, and the shoulders' roll from step to step
  let twist = 0, roll = 0;
  // 67.60: the boots' pitch past level (+ toe down: the heel to toe, the jump's toes), the hips' drop to the swinging side, the
  // landing's give (the gun dips with it), and the jump's own placed feet (solved in the air, not by the walk's solve)
  let pitchL = 0, pitchR = 0, hipRoll = 0, landGive = 0, airFeet = false;
  // 67.60: the crawl's knees drawn up by the side (0 … 1, each) and its reach (the gun forward with the support elbow)
  let crawlL = 0, crawlR = 0, crawlReach = 0, crawling = false;
  if (ctx.state !== 'airborne') ctx.air = undefined;
  else if (!ctx.air) ctx.air = { at: t, rise: ctx.airRise ?? 0, v0: Math.max(1e-3, ctx.airUp ?? 0), lag: 0, legs: [0, 0, 0, 0] };
  else ctx.air.rise = ctx.airRise ?? ctx.air.rise + (ctx.airUp ?? 0) * dt;
  // 66.7 (a): in cover — held while still (a walk leaves it); the peek eases between down (0) and out (1)
  const cov = ctx.cover && (ctx.state === 'idle' || ctx.state === 'landing') ? ctx.cover : null;
  let peek = 0;
  if (cov) {
    const was = ctx.coverPeek ?? 0, step = dt / (cov.out ? COVER_LOOK.out : COVER_LOOK.back);
    ctx.coverPeek = was + Math.max(-step, Math.min(step, (cov.out ? 1 : 0) - was));
    peek = smooth(ctx.coverPeek);
    // 66.10: a shot's hold on the gun, up over COVER_FIRE.rise, back over its fade once the hold has passed
    const f = ctx.coverFire ?? 0, on = t - ctx.firedAt < COVER_FIRE.hold;
    ctx.coverFire = on ? Math.min(1, f + dt / COVER_FIRE.rise) : Math.max(0, f - dt / COVER_FIRE.fade);
  } else ctx.coverPeek = ctx.coverFire = undefined;
  // 67.60: the cover's way, in the model's own frame (mirrored for a right hand): the whole rig turned to face it (`coverBase`),
  // the torso turned back to the look by the gun's rise (`coverTurn`), the base turning on toward the look past COVER_TURN.torso;
  // the look well away from the cover, the back to it (`coverFlip` −1: the lean mirrored; `coverBackZ` eased back onto the face)
  // (`coverRig`, the back to it: the turn back to the look the whole body's, the hips round with the chest)
  // (`coverPush`: the body stood off the face as it turns off square to it with the back to it; `coverAt`, the cover's bearing)
  let coverBase = 0, coverTurn = 0, coverRig = 0, coverOff = 1, coverFlip = 1, coverBackZ = 0, coverPush = 0, coverAt = 0;
  if (cov && ctx.coverBearing !== undefined) {
    const b = (m.hand === 'right' ? -1 : 1) * ctx.coverBearing, up = Math.max(peek, smooth(ctx.coverFire ?? 0)), T = cov.pose === 'crest' ? 0 : COVER_TURN.torso;   // (prone, the whole body turns)
    const B = COVER_TURN.back, wasBack = ctx.coverBack ?? 0, wantBack = Math.abs(b) > B.on ? 1 : Math.abs(b) < B.off ? 0 : wasBack >= 0.5 ? 1 : 0;
    ctx.coverBack = wasBack + Math.max(-dt / B.seconds, Math.min(dt / B.seconds, wantBack - wasBack));
    const w = smooth(ctx.coverBack), wrap = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
    const toLook = (a: number) => a + (Math.max(-T, Math.min(T, a)) - a) * up;   // a base, turned to within T of the look as the gun rises
    const front = toLook(b), back = toLook(wrap(b + Math.PI));
    coverBase = front + wrap(back - front) * w;
    coverTurn = -Math.max(-T, Math.min(T, coverBase)) * up;
    coverRig = COVER_TURN.backBody ? coverTurn * w : 0; coverTurn -= coverRig;
    coverOff = Math.cos(b - coverBase) * (1 - w) - Math.cos(b - coverBase) * w;   // (how square the pose stands to the cover — its front, or its back)
    coverFlip = 1 - 2 * w;
    coverBackZ = -w * Math.max(0, (cov.gap ?? 0) - B.pack);
    coverPush = w * B.clear * Math.max(0, Math.abs(Math.sin(coverBase + coverRig - b - Math.PI)) - 0.5); coverAt = b;
  } else ctx.coverBack = undefined;
  // 66.8: digging in — kneeling at the dig, still (a walk or a seat ends it)
  const digging = ctx.digAt !== undefined && !cov && (ctx.state === 'idle' || ctx.state === 'landing');
  const simStance: Stance = cov ? COVER_STANCE[cov.pose] : digging ? 'crouch' : ctx.state === 'seated' || ctx.state === 'airborne' ? 'stand' : (ctx.stance ?? 'stand');
  // 67.60: the stance drawn — a step toward the sim's, through the kneel between standing and prone (STANCE_LOOK)
  const stanceKey = cov ? `cover:${cov.pose}` : digging ? 'dig' : simStance === 'crouch' && ctx.state === 'run' ? 'crouch:move' : simStance;   // (the kneel and the low walk differ as much as stances do)
  let stance: Stance = simStance;
  if (STANCE_LOOK.on) {
    const S = STANCE_LOOK, shown = ctx.stanceShown ?? stanceKey, step = ctx.stanceStep;
    if (stanceKey !== shown) {
      const k = step ? (t - step.at) / step.dur : 1;
      if (!step || k >= 1 || stanceKey === step.from || (step.via && k >= S.via)) {
        const to = ((shown === 'stand' || shown === 'crouch:move') && stanceKey === 'prone') || (shown === 'prone' && stanceKey === 'stand') ? 'crouch' : stanceKey;
        snapStance(m, ctx.stanceSnap ??= new Float64Array(STANCE_JOINTS * 7));
        ctx.stanceStep = { from: shown, to, at: t, dur: S.seconds[`${shown}>${to}`] ?? S.cover, via: to !== stanceKey };
        ctx.stanceShown = to;
      } else ctx.stanceShown = shown;
    } else ctx.stanceShown = shown;
    if (ctx.stanceStep && t - ctx.stanceStep.at >= ctx.stanceStep.dur) ctx.stanceStep = undefined;
    const drawn = ctx.stanceShown === 'crouch:move' ? 'crouch' : ctx.stanceShown;
    if (!cov && !digging && (drawn === 'stand' || drawn === 'crouch' || drawn === 'prone')) stance = drawn;
  } else { ctx.stanceShown = undefined; ctx.stanceStep = undefined; }
  if (ctx.stanceBlended) { for (const o of stanceJoints(m)) o.rotation.set(0, 0, 0); ctx.stanceBlended = false; }   // (a blended quaternion leaves angles the pose never sets; after the snapshot above, which wants them as drawn)
  // 63.8: the gaits' shares — standing on foot, out of cover — eased in and out (GAIT_LOOK); `wm` / `ws` their weights
  const GL = GAIT_LOOK, onFoot = !cov && !digging && stance === 'stand' && ctx.state !== 'seated' && ctx.state !== 'operate';
  const wantMove = onFoot && ctx.gait === 'move' ? 1 : 0, wantSprint = onFoot && ctx.gait === 'sprint' ? 1 : 0;
  ctx.gaitMove = toward(ctx.gaitMove ?? 0, wantMove, dt / (wantMove ? GL.in.move : GL.out.move));
  ctx.gaitSprint = toward(ctx.gaitSprint ?? 0, wantSprint, dt / (wantSprint ? GL.in.sprint : GL.out.sprint));
  const gaitSum = Math.max(1, smooth(ctx.gaitMove) + smooth(ctx.gaitSprint)), wm = smooth(ctx.gaitMove) / gaitSum, ws = smooth(ctx.gaitSprint) / gaitSum, w0 = 1 - wm - ws;
  // the cover's lean in the model's own frame (+x the strong side): the hips' slide, the torso's roll, the legs splayed
  // so the feet stay where they stood; the head's pitch against the hunch; a strong knee down that rises onto its foot
  let slideX = 0, slideZ = 0, coverRoll = 0, headDown = 0, kneel = false;
  // 66.7 (a), pressed: the body turned toward the edge (about the hips), the head kept level over low cover
  let coverYaw = 0, lookLevel = 0;
  const pressed = !!cov && cov.gap !== undefined;

  if (cov) {
    const own = (cov.side ?? 1) * (m.hand === 'right' ? 1 : -1);   // the side leaned to, in the model's own frame
    if (cov.pose === 'low' && pressed) {
      const c = COVER.low, P = PRESS.low, H = cov.height ?? 1;
      // down: leaning on the cover until the head is under its top; out: over it, the eyes clearing it by `clear`
      const downLean = Math.max(P.minLean, Math.min(P.maxLean, Math.acos(Math.max(-1, Math.min(1, (H - P.topMargin - c.downHip) / P.topOverHip)))));
      const k = clamp01((H - 0.95) / 0.35), overLean = P.overLean[0] + (P.overLean[1] - P.overLean[0]) * k;
      const up = Math.max(c.downHip, Math.min(HIP_Y - 0.04, H + P.clear - eyeOverHips(overLean, -P.overLook * overLean)));   // (the gun and the forearms resting on the top)
      hipY = c.downHip + (up - c.downHip) * peek; lean = downLean + (overLean - downLean) * peek;
      headDown = P.head * (1 - peek); lookLevel = P.overLook * overLean * peek;
      footL = { forward: P.foot, lift: 0 }; kneel = true;
    } else if (cov.pose === 'low') {
      const c = COVER.low;
      const up = Math.max(c.downHip, Math.min(HIP_Y - 0.04, (cov.height ?? 1) + c.clear - c.eyeOverHip));
      hipY = c.downHip + (up - c.downHip) * peek; lean = c.downLean + (c.upLean - c.downLean) * peek;
      headDown = c.head * (1 - peek);
      footL = { forward: c.foot, lift: 0 }; kneel = true;
    } else if (cov.pose === 'edge' || cov.pose === 'corner') {
      const c = COVER[cov.pose], out = own > 0 ? c.slide : c.weakSlide, tuck = pressed ? PRESS[cov.pose].tuck : c.tuck;
      slideX = own * coverFlip * (tuck + (out - tuck) * peek);   // (67.60: its size the side's, mirrored with the back to the cover)
      coverRoll = -own * coverFlip * (own > 0 ? c.roll : c.weakRoll) * peek;
      hipY = c.hip; lean = c.downLean + (c.upLean - c.downLean) * peek;
      if (pressed) {
        const P = PRESS[cov.pose];
        coverYaw = own * coverFlip * (P.yaw + (P.outYaw - P.yaw) * peek);
        let downLean: number = P.lean;
        if (cov.pose === 'edge' && cov.height !== undefined) {
          // an edge lower than the kneel's head: sitting back and leaning until the head is under its top
          const E = PRESS.edge, sit = clamp01((c.hip + PRESS.low.topOverHip + E.topMargin - cov.height) / 0.3);   // (the kneel's head top over its hips: topOverHip)
          const hip = c.hip + (E.sitHip - c.hip) * sit;
          downLean = Math.max(downLean, Math.acos(Math.max(-1, Math.min(1, (cov.height - E.topMargin - hip) / PRESS.low.topOverHip))));
          hipY = hip + (c.hip - hip) * peek;
        }
        const leanIn = (ctx.coverLeanIn ?? 0) * (1 - peek);   // the lean in over a bulging face, down; the peek stands up off it (today's eyes)
        lean = downLean + (P.outLean - downLean) * peek + leanIn;
        if (cov.pose === 'edge') lookLevel = 0.85 * leanIn;
        if (cov.pose === 'corner') {
          // down, the knees bent and the weight forward onto the cover, the eyes kept level; out, up to today's peek
          const C = PRESS.corner;
          hipY = C.hip + (c.hip - C.hip) * peek; lookLevel = C.look * lean * (1 - peek);
        }
      }
      if (cov.pose === 'edge') { footL = { forward: COVER.low.foot, lift: 0 }; kneel = true; }
      else { footL = { forward: 0.12, lift: 0 }; footR = { forward: -0.14, lift: 0 }; }
    } else {
      const c = COVER.crest;
      body = c.downPitch + (c.upPitch - c.downPitch) * peek; hipY = c.downHip + (c.upHip - c.downHip) * peek;
      slideZ = c.back * (1 - peek); headDown = c.face * (1 - peek);
      kneeL = kneeR = 0.08; thighL = thighR = PRONE.legs;
    }
    slideZ += coverBackZ;   // (67.60: the back to the cover, eased back onto its face)
  } else if (stance === 'crouch') {
    if (ctx.state === 'run' && LOW_LOOK.on) {
      // 67.60: the low walk — a stride that keeps pace with the ground, heel to toe, the hips low and bobbing on a smooth wave
      const L = LOW_LOOK;
      ctx.gaitSpeed = ctx.gaitSpeed === undefined ? ctx.speed : ctx.gaitSpeed + (ctx.speed - ctx.gaitSpeed) * Math.min(1, dt / 0.15);
      const sp = ctx.gaitSpeed, run = clamp01((sp - L.walkAt) / (L.runAt - L.walkAt)), duty = L.duty[0] + (L.duty[1] - L.duty[0]) * run;
      const short = Math.min(1, 0.4 + sp / 3);   // (short steps when slow)
      const gt = { duty, front: L.front * short, back: L.back * short, cycles: 0, run };
      gt.cycles = Math.max(L.minCycles, Math.min(L.maxCycles, (duty * sp) / (gt.front + gt.back)));
      ctx.gaitPhase = (ctx.gaitPhase ?? 0) + dt * gt.cycles;
      const pL = ctx.gaitPhase - Math.floor(ctx.gaitPhase), pR = (pL + 0.5) % 1, liftH = L.lift + L.liftPerSpeed * sp;
      const l = footAt(pL, gt, liftH), r = footAt(pR, gt, liftH);
      footL = { forward: l.forward, lift: l.lift }; footR = { forward: r.forward, lift: r.lift };
      pitchL = footRoll(pL, duty); pitchR = footRoll(pR, duty);
      footL.lift = Math.max(footL.lift, ankleRise(pitchL)); footR.lift = Math.max(footR.lift, ankleRise(pitchR));
      hipY = L.hip - L.bob * (0.5 - 0.5 * Math.cos(4 * Math.PI * (pL - duty / 2)));
      lean = L.lean + L.leanPerSpeed * sp;
      const swing = Math.sin(pL * Math.PI * 2);
      twist = L.twist * Math.min(1, sp / 3) * swing;
      slideX = -STRIDE_LOOK.sway * 0.6 * (l.stance - r.stance) * Math.min(1, sp / 2);
    } else if (ctx.state === 'run') {
      // the low walk: hips down, leaning into it; each foot placed on the ground by a two-link solve
      // (fixed angles cannot keep a foot planted at a lowered hip), the swinging one lifted
      const stride = Math.min(1, ctx.speed / 3);
      ctx.gaitPhase = (ctx.gaitPhase ?? 0) + dt * Math.min(LOW_WALK_CYCLES, 0.8 + ctx.speed * 0.25);
      const ph = ctx.gaitPhase * Math.PI * 2, s = Math.sin(ph), c = Math.cos(ph);
      hipY = CROUCH_WALK_HIP_Y; lean = 0.35;
      footL = { forward: 0.22 * stride * s, lift: 0.1 * stride * Math.max(0, c) };     // short steps: a steeply tilted shin would dig its boot in
      footR = { forward: -0.22 * stride * s, lift: 0.1 * stride * Math.max(0, -c) };
    } else {
      // the kneel: right knee on the ground, left foot planted ahead (66.8: leaning in over the dig)
      hipY = KNEEL.hipY; lean = 0.08 + (digging ? DIG_CYCLE.lean : 0); thighL = KNEEL.thighL; kneeL = KNEEL.thighL; thighR = 0; kneeR = KNEEL.kneeR;
      bob = 0.006 * Math.sin(t * 1.7);
      if (!digging) {
        // 73.3: sat back onto the heel and hunched over the rifle — the kneeling leg solved knee-down at the lower hip
        const K = CROUCH_KNEEL;
        hipY = K.hipY; lean = K.lean; footL = { forward: K.foot, lift: 0 }; kneel = true;
      }
    }
  } else if (stance === 'prone') {
    // lying along the ground, chest down, legs straight back; the crawl flexes the lower legs in turn
    body = PRONE.pitch; hipY = PRONE.hipY;
    if (ctx.state === 'run' && CRAWL_LOOK.on) {
      // 67.60: the crawl — a knee drawn up by the side and the other elbow reaching, then the push (the hips surge), side by side
      const C = CRAWL_LOOK;
      ctx.gaitPhase = (ctx.gaitPhase ?? 0) + dt * Math.min(C.cycles[1], C.cycles[0] + C.perSpeed * ctx.speed);
      const w = Math.sin(2 * Math.PI * ctx.gaitPhase), dl = Math.max(0, w), dr = Math.max(0, -w);
      crawling = true; crawlL = dl; crawlR = dr; kneeL = C.knee * dl; kneeR = C.knee * dr;
      slideZ = C.surge * Math.cos(2 * Math.PI * ctx.gaitPhase * 2);   // (forward on each push)
      roll = C.roll * w; twist = C.twist * w; crawlReach = C.reach * w;
    } else if (ctx.state === 'run') { const ph = t * (3 + ctx.speed * 1.5); kneeL = 0.55 * Math.max(0, Math.sin(ph)); kneeR = 0.55 * Math.max(0, -Math.sin(ph)); bob = 0.01 * Math.abs(Math.sin(ph)); }
    else kneeL = kneeR = 0.08;
    thighL += PRONE.legs; thighR += PRONE.legs;   // legs flat along the ground under the propped chest
  } else if (ctx.state === 'run') {
    // the walk and the run: the planted foot keeps pace with the ground, the hips dip so it still
    // reaches at the stride's ends (the walk's real bob), the swinging foot lifts with its knee
    // folding, the body leans into the speed and the shoulders turn against the hips
    // the gait's speed eases (0.15 s): starting to sprint changes the stride over a few frames, never in one
    ctx.gaitSpeed = ctx.gaitSpeed === undefined ? ctx.speed : ctx.gaitSpeed + (ctx.speed - ctx.gaitSpeed) * Math.min(1, dt / 0.15);
    const g = GAIT, gt = gaitAt(ctx.gaitSpeed);
    if (w0 < 1) {
      // 63.8: a move's and a sprint's stride — a longer flight, quicker, the knees higher (GAIT_LOOK)
      const Mv = GL.move, Sp = GL.sprint;
      gt.front = gt.front * w0 + Mv.front * wm + Sp.front * ws; gt.back = gt.back * w0 + Mv.back * wm + Sp.back * ws; gt.duty = gt.duty * w0 + Mv.duty * wm + Sp.duty * ws;
      gt.cycles = Math.max(g.minCycles, Math.min(g.maxCycles * w0 + Mv.cap * wm + Sp.cap * ws, (gt.duty * ctx.gaitSpeed) / (gt.front + gt.back)));
    }
    ctx.gaitPhase = (ctx.gaitPhase ?? 0) + dt * gt.cycles;   // integrated, so a change of speed changes the pace, never jumps the legs
    const pL = ctx.gaitPhase - Math.floor(ctx.gaitPhase), pR = (pL + 0.5) % 1;
    const liftH = Math.min(g.liftMax, g.lift + g.liftPerSpeed * ctx.gaitSpeed) + GL.move.lift * wm + GL.sprint.lift * ws;
    const l = footAt(pL, gt, liftH), r = footAt(pR, gt, liftH);
    footL = { forward: l.forward, lift: l.lift };
    footR = { forward: r.forward, lift: r.lift };
    // a run compresses in each stance and rises in the flight between (the flight runs from one foot's toe-off to the other's touchdown)
    const half = pL % 0.5, flight = l.stance === 0 && r.stance === 0 && half >= gt.duty ? Math.sin(Math.PI * Math.min(1, (half - gt.duty) / Math.max(1e-6, 0.5 - gt.duty))) : 0;
    hipY = HIP_Y - g.runDip * gt.run * Math.max(l.stance, r.stance) + g.runBounce * gt.run * flight;
    const swing = Math.sin(pL * Math.PI * 2);
    twist = g.twist * Math.min(1, ctx.gaitSpeed / 4) * swing;
    roll = g.roll * Math.min(1, ctx.gaitSpeed / 6) * swing;
    if (STRIDE_LOOK.on) {
      // 67.60: heel to toe (the ankle rising with the roll: a foot far behind reaches on its toe), the walk's vault over the
      // planted leg, the hips over it and the swinging side down, the shoulders turning further against the hips
      const SL = STRIDE_LOOK, walk = 1 - gt.run, lead = l.stance - r.stance;
      pitchL = footRoll(pL, gt.duty); pitchR = footRoll(pR, gt.duty);
      footL.lift = Math.max(footL.lift, ankleRise(pitchL)); footR.lift = Math.max(footR.lift, ankleRise(pitchR));
      // (smooth waves: a walk highest at either foot's mid-stance; a run lowest there and highest in the flight, its dip and
      // bounce as before without the corners where the stance's met the flight's)
      const wave = Math.cos(4 * Math.PI * (pL - gt.duty / 2));
      hipY = HIP_Y - SL.bob * walk * (0.5 - 0.5 * wave) + gt.run * ((g.runBounce - g.runDip) / 2 - ((g.runBounce + g.runDip) / 2) * wave);
      slideX = -SL.sway * lead * Math.min(1, ctx.gaitSpeed / 2);
      hipRoll = -SL.drop * lead * Math.min(1, ctx.gaitSpeed / 2);
      twist *= SL.twist;
    }
    lean = Math.min(g.leanMax, g.lean + g.leanPerSpeed * ctx.gaitSpeed);
    lean += (GL.sprint.lean - lean) * ws; twist *= 1 + 0.3 * ws;   // (63.8: leaning into a sprint)
    lean += (GL.move.jolt * wm + GL.sprint.jolt * ws) * Math.max(l.stance, r.stance);   // (and jolting forward on each footfall, the pack's weight)
  } else if (ctx.state === 'airborne') {
    const J = JUMP_LOOK, A = ctx.air;
    if (J.on && ctx.jumpFeel && A) {
      // 67.60: the jump — the push off the ground it left (the crouch, then up onto the toes), the drawn body catching the
      // sim's up; the legs from the push's extension to the tuck by the apex, and reaching for the ground coming down
      const tau = t - A.at, u = Math.max(-1, Math.min(1, (ctx.airUp ?? 0) / A.v0));
      const a = smooth((1 - u) / J.tuckBy), b = smooth((-u - J.reachFrom) / (J.reachBy - J.reachFrom)), E = J.extend, T = J.tuck, R = J.reach;
      const mix = (k: keyof typeof E) => { const et = E[k] + (T[k] - E[k]) * a; return et + (R[k] - et) * b; };
      thighL = mix('thighL'); kneeL = mix('kneeL'); thighR = mix('thighR'); kneeR = mix('kneeR'); lean = mix('lean');
      pitchL = pitchR = mix('toes');
      if (tau < J.push) {
        const k = tau / J.push, over = k < J.low ? HIP_Y - J.crouch * smooth(k / J.low) : HIP_Y - J.crouch + (J.crouch + J.rise) * smooth((k - J.low) / (1 - J.low));
        const toes = E.toes * smooth((k - J.low) / (1 - J.low)), lift = -A.rise + ankleRise(toes);
        hipY = over - A.rise;
        [thighL, kneeL] = legTo(hipY, J.feet[0], lift); [thighR, kneeR] = legTo(hipY, J.feet[1], lift);
        pitchL = pitchR = toes; lean = E.lean * smooth(k / J.low); airFeet = true;
        A.lag = A.rise - J.rise; A.legs = [thighL, kneeL, thighR, kneeR];
      } else {
        hipY = HIP_Y - A.lag * (1 - smooth((tau - J.push) / J.catchUp));
        const w = smooth((tau - J.push) / J.blend);   // (from the push's last legs into the air's)
        thighL = A.legs[0] + (thighL - A.legs[0]) * w; kneeL = A.legs[1] + (kneeL - A.legs[1]) * w;
        thighR = A.legs[2] + (thighR - A.legs[2]) * w; kneeR = A.legs[3] + (kneeR - A.legs[3]) * w;
        airFeet = true;
      }
    } else { thighL = 0.6; thighR = 0.25; kneeL = 1.1; kneeR = 0.7; lean = 0.05; }   // tucked
  } else if (ctx.state === 'landing') {
    if (JUMP_LOOK.on) {
      // 67.60: the landing's give — by how fast it came down, eased in and out, the feet planted where they came down
      const L = JUMP_LOOK.land, tau = t - ctx.landedAt, depth = Math.min(L.most, L.perSpeed * (ctx.landSpeed ?? 0));
      const k = tau < L.in ? smooth(tau / L.in) : 1 - smooth((tau - L.in) / L.out);
      hipY -= depth * k; lean = L.lean * k * (depth / L.most); landGive = k * depth / L.most;
      footL = { forward: L.feet[0], lift: 0 }; footR = { forward: L.feet[1], lift: 0 };
      if (tau >= L.in + L.out) ctx.state = 'idle';
    } else {
      const k = Math.max(0, 1 - (t - ctx.landedAt) / 0.25);
      hipY -= 0.18 * k; thighL = thighR = 0.6 * k; kneeL = kneeR = 1.1 * k; lean = 0.2 * k;
      if (k === 0) ctx.state = 'idle';
    }
  } else if (ctx.state === 'seated') {
    if (ctx.seat === 'saddle') {
      // astride (the motorcycle): the hips where a standing person's are, so the anchor under the saddle seats them on
      // it; the boots up on the pegs, the knees folded forward; leaning to the bars
      hipY = HIP_Y; lean = SADDLE.lean;
      footL = { forward: SADDLE.pegForward, lift: SADDLE.pegLift }; footR = { forward: SADDLE.pegForward, lift: SADDLE.pegLift };
    } else if (ctx.seat === 'ground') {
      // 65.22: sitting on the ground behind the heavy machine gun, the anchor on the ground: the hips a hand off it,
      // the legs out in front (either side of the tripod's rear leg), the knees a little up, leaning in to the grips
      hipY = 0.17; thighL = thighR = 1.42; kneeL = kneeR = 0.55; lean = 0.38;
    } else if (ctx.seat === 'hatch') {
      // 67.63: standing on the hatch's floor (the anchor), its head and shoulders out at the rim: upright, the knees soft
      hipY = HIP_Y - 0.02; thighL = thighR = 0.06; kneeL = kneeR = 0.12; lean = 0.04;
    } else if (ctx.seat === 'drive') {
      // 67.63: low in a cab's seat: the hips DRIVE_SEAT.hip over the floor (the anchor), the feet forward on the pedals
      hipY = DRIVE_SEAT.hip; lean = DRIVE_SEAT.lean;
      footL = { forward: DRIVE_SEAT.pedals, lift: 0 }; footR = { forward: DRIVE_SEAT.pedals, lift: 0 };
    } else if (ctx.seat === 'pilot') {
      // 67.63: a cockpit's seat — the feet flat on the floor just ahead of the seat's front, the knees bent up over them (about
      // 0.36 ahead of the hips): a short reach where a cockpit's nose is close
      hipY = 0.55; lean = -0.1;
      footL = { forward: 0.12, lift: 0 }; footR = { forward: 0.12, lift: 0 };
    } else if (ctx.seat === 'bench') {
      // 67.63: a rider on a bench (a bay's, a container's): the chair's sit, a little forward over the knees
      hipY = 0.55; thighL = thighR = 1.45; kneeL = kneeR = 1.5; lean = 0.12;
    } else { hipY = 0.55; thighL = thighR = 1.45; kneeL = kneeR = 1.5; }
  } else {
    bob = 0.008 * Math.sin(t * 1.7);   // breathing
  }
  // 65.7: a bandage being applied — down over it (a standing figure crouches), the weapon hanging, the hands at the wound
  const hk = ctx.healAt === undefined ? -1 : (t - ctx.healAt) / (ctx.healFor ?? BANDAGE.seconds);
  const bandaging = hk >= 0 && hk < 1;
  const bandageK = bandaging ? smooth(hk / 0.15) * smooth((1 - hk) / 0.15) : 0;   // eases in and out
  const bend = Math.max(ctx.bend ?? 0, (stance === 'stand' ? BANDAGE_LOOK.crouch : 0) * bandageK), flinch = ctx.flinch ?? 0;
  if (bend > 0) {
    if (ctx.state === 'seated') lean += 0.35 * bend;
    else { hipY -= 0.2 * bend; thighL += 0.55 * bend; thighR += 0.55 * bend; kneeL += 1.0 * bend; kneeR += 1.0 * bend; lean += 0.6 * bend; }
  }
  lean -= 0.22 * flinch;
  // 65.28: suppressed — each near round a beat (the head down, the shoulders in, a dip at a post or a stoop in the open), and
  // while the mark lasts a pose in cover held lower; prone stays prone, its face to the ground on the beat
  const supp = ctx.suppressed ?? 0;
  if (supp > (ctx.suppressedWas ?? 0) + 1e-6) ctx.suppressedAt = t;
  ctx.suppressedWas = supp;
  if (supp <= 0 && ctx.suppressedAt !== undefined && t - ctx.suppressedAt > SUPPRESS_LOOK.beat) ctx.suppressedAt = undefined;
  const sk = ctx.suppressedAt === undefined ? -1 : (t - ctx.suppressedAt) / SUPPRESS_LOOK.beat;
  const beat = sk < 0 || sk >= 1 ? 0 : sk < SUPPRESS_LOOK.attack ? smooth(sk / SUPPRESS_LOOK.attack) : (1 - smooth((sk - SUPPRESS_LOOK.attack) / (1 - SUPPRESS_LOOK.attack)));
  const held = cov && !cov.out ? Math.min(1, supp / SUPPRESS_LOOK.full) * (1 - peek) : 0;
  if (beat > 0 || held > 0) {
    const S = SUPPRESS_LOOK;
    if (stance === 'prone') headDown += S.proneHead * beat;
    else {
      lean += S.lean * beat + S.heldLean * held; headDown += S.head * beat + S.heldHead * held;
      if (cov) hipY = Math.max(S.minHip, hipY - S.dip * beat - S.heldDip * held);   // (the legs are solved from the hips in cover)
      else if (stance === 'stand' && ctx.state === 'idle') {
        // a stoop in the open: the feet planted where they stand and the hips dipping over them (the legs solved below)
        const b = S.stoop * beat; hipY -= 0.2 * b; lean += 0.6 * b;
        footL = footL ?? { forward: 0, lift: 0 }; footR = footR ?? { forward: 0, lift: 0 };
      }
    }
  }

  // the ground under the feet (the slope, clamped): a standing figure's feet follow it, the hips
  // dropping so the lower foot still reaches; a prone body lies along it
  // 67.21: the slope comes in the holder's frame (+x = up × front); a mirrored (right-handed) model's own +x is the holder's −x
  const sx = Math.max(-SLOPE_MAX, Math.min(SLOPE_MAX, (m.hand === 'right' ? -1 : 1) * (ctx.slope?.x ?? 0))), sz = Math.max(-SLOPE_MAX, Math.min(SLOPE_MAX, ctx.slope?.z ?? 0));
  const sloped = sx !== 0 || sz !== 0;
  if ((sloped || ctx.ground) && !footL && stance === 'stand' && ctx.state === 'idle') { footL = { forward: 0, lift: 0 }; footR = { forward: 0, lift: 0 }; }
  // 67.60: the ground under a sole at (x, z) in the hips' frame — the drawn ground's own (the view's sampler; the model's x is
  // mirrored for a right hand, and turned with the rig in cover), else the slope's plane
  const cb = Math.cos(coverBase + coverRig), sb = Math.sin(coverBase + coverRig), mx = m.hand === 'right' ? -1 : 1;
  const shift = coverRig !== 0 || coverPush !== 0 ? ctx.coverShift : undefined;   // (the body's slide for its turn about the neck: last frame's)
  const groundUnder = (x: number, z: number): number => {
    if (!ctx.ground) return sx * x + sz * z;
    const zh = z + slideZ;   // (from the hips, slid back at a crest)
    return ctx.ground(mx * (x * cb + zh * sb + (shift?.x ?? 0)), -x * sb + zh * cb + (shift?.z ?? 0));
  };
  let level = false, levelL = false;
  // 66.7 (a): leaning, the hips slide sideways over feet that stay where they stood — each placed leg solved in its own
  // plane, tilted across the body by its thigh's turn about the hips' forward axis (`splay`), so the in-plane reach is
  // the drop and the slide together
  let splayL = 0, splayR = 0;
  const legHip = (lift: number) => slideX === 0 ? hipY : Math.hypot(hipY - SOLE - lift, slideX) + SOLE + lift;
  const splayFor = (drop: number) => slideX === 0 ? 0 : -Math.atan2(slideX, drop);
  if (kneel && footL) {
    // 66.7 (a): the weak foot placed, the strong knee on the ground — rising onto its foot (planted behind) as the hips
    // come up past the kneel
    [thighL, kneeL] = legTo(legHip(footL.lift), footL.forward, footL.lift); levelL = true;
    splayL = splayFor(hipY - SOLE - footL.lift); splayR = splayFor(hipY - KNEE_DOWN);   // the kneeling knee stays about where it knelt
    const down = Math.acos(Math.min(1, (hipY - KNEE_DOWN) / THIGH)), rise = smooth((hipY - KNEEL_RISE) / 0.12);
    const [tu, ku] = legTo(hipY, -0.3, 0);
    thighR = down + (tu - down) * rise; kneeR = down + KNEEL.kneeR + (ku - down - KNEEL.kneeR) * rise;
    if (rise > 0.5) level = true;
  } else if (footL && footR) {
    const L = THIGH + SHIN;
    const groundL = groundUnder(-LEG_X, footL.forward), groundR = groundUnder(LEG_X, footR.forward);   // the ground's height under each sole
    const liftL = footL.lift + groundL, liftR = footR.lift + groundR;
    // a planted foot must reach the ground: the hips drop to the lowest reach — standing, by at most the stride's
    // allowance below the ground under the feet (a runner's toe-off rolls the heel up rather than sinking the hips);
    // 66.7 (a): in cover the reach is across the hips' slide too, and the hips come down as far as that needs
    let want = hipY;
    if (footL.lift <= 1e-6) want = Math.min(want, SOLE + liftL + Math.sqrt(Math.max(0, L * L - footL.forward * footL.forward - slideX * slideX)));
    if (footR.lift <= 1e-6) want = Math.min(want, SOLE + liftR + Math.sqrt(Math.max(0, L * L - footR.forward * footR.forward - slideX * slideX)));
    const gaitHip = hipY;
    hipY = stance === 'stand' && !cov ? Math.max(want, Math.min(hipY, HIP_Y - GAIT.dropMax + Math.min(0, groundL, groundR))) : want;
    if (!cov) {
      // 67.60: the drop for the feet eased (FEET_LOOK), not taken in the frame a foot plants; the legs reach for what is left
      const drop = gaitHip - hipY, was = ctx.feetDrop ?? drop;
      ctx.feetDrop = drop + (was - drop) * Math.exp(-dt / FEET_LOOK.drop);
      hipY = gaitHip - ctx.feetDrop;
    }
    [thighL, kneeL] = legTo(legHip(liftL), footL.forward, liftL);
    [thighR, kneeR] = legTo(legHip(liftR), footR.forward, liftR);
    splayL = splayFor(hipY - SOLE - liftL); splayR = splayFor(hipY - SOLE - liftR);
    level = true;
  }
  if (stance === 'prone' && sloped) body -= Math.atan(sz);

  m.hips.position.y = hipY + bob + slideZ * sz;   // (66.7 (a): slid back down a crest's slope, the hips follow the ground)
  m.hips.rotation.order = 'ZYX';   // the pitch first, then the twist, then the ground's roll
  m.hips.rotation.x = body;
  m.hips.rotation.y = twist + coverYaw;   // (66.7 (a), pressed: turned toward the edge)
  m.hips.rotation.z = stance === 'prone' && sloped ? Math.atan(sx) : hipRoll;   // (67.60: the swinging leg's side down)
  // (67.16: the far tier's crouch and prone are poses of their own, chosen at the top — no longer the standing block squashed and tipped)
  m.thighL.rotation.x = -thighL; m.thighR.rotation.x = -thighR;
  m.shinL.rotation.x = kneeL; m.shinR.rotation.x = kneeR;
  // a placed foot keeps its boot flat on the ground (level, and pitched and rolled with the slope); no other pose moves the ankle
  // (67.60: pitched past level — heel to toe, the jump's toes; in the air level with the hips, no ground under it)
  m.footL.rotation.x = (airFeet ? thighL - kneeL : level || levelL ? thighL - kneeL - Math.atan(sz) : 0) + pitchL;
  m.footR.rotation.x = (airFeet ? thighR - kneeR : level ? thighR - kneeR - Math.atan(sz) : 0) + pitchR;
  m.footL.rotation.z = level || levelL ? Math.atan(sx) - splayL : 0; m.footR.rotation.z = level ? Math.atan(sx) - splayR : 0;   // (66.7 (a): the splay undone, flat)
  // 66.7 (a): the hips slide toward the edge (or back below the crest), the legs splayed against it so the feet stay put
  m.hips.position.x = slideX; m.hips.position.z = slideZ;
  m.thighL.rotation.z = splayL + CRAWL_LOOK.splay * crawlL; m.thighR.rotation.z = splayR - CRAWL_LOOK.splay * crawlR;   // (ZXY: about the hips' forward axis, after the swing; 67.60: prone, along the ground)
  m.thighL.rotation.y = CRAWL_LOOK.turn * crawlL; m.thighR.rotation.y = -CRAWL_LOOK.turn * crawlR;   // (67.60: the crawl's thigh turned on its axis, the shin folding along the ground)
  m.torso.rotation.x = lean;
  m.torso.rotation.y = -twist;
  m.torso.rotation.z = roll + coverRoll - 0.8 * hipRoll;   // (67.60: the shoulders kept level against the hips' drop)
  m.full.rotation.y = coverBase + coverRig; m.silhouette.rotation.y = coverBase + coverRig;   // 67.60: the cover pose faces the cover
  m.torso.rotation.order = cov ? 'ZXY' : 'XYZ';   // 66.7 (a): in cover the roll comes last, square to the heading (not to the bladed chest)
  // DECISIONS 401: the clip layer — walking or running, standing or crouched, the CC0 gait clips' hips, torso and legs over
  // today's (personClips.ts: at the stride's phase, blended by the gait's pace — 410 (1): each clip at a pace of play's, its
  // stride widened), eased in and out over CLIP_EASE s; the head's look, the gun and the arms' IK below follow the clip's chest
  // as they follow today's
  const clipsOn = personClipsOn();
  const clipOn = clipsOn && ctx.state === 'run' && (stance === 'stand' || stance === 'crouch');
  if (clipOn) ctx.clipStance = stance === 'crouch' ? 'crouch' : 'stand';
  ctx.clipW = Math.max(0, Math.min(1, (ctx.clipW ?? 0) + (clipOn ? 1 : -1) * dt / CLIP_EASE));
  let fitW = 0;
  if (ctx.clipW > 0 && ctx.clipStance) {
    const w = smooth(ctx.clipW), pose = _clip;
    sampleGaitClips(ctx.clipStance, ctx.gaitSpeed ?? ctx.speed, (ctx.gaitPhase ?? 0) % 1, pose);
    clipSoles(pose, _soles); widenStride(pose);
    const joints = [m.hips, m.torso, m.thighL, m.shinL, m.footL, m.thighR, m.shinR, m.footR];
    for (let j = 0; j < joints.length; j++) joints[j].quaternion.slerp(pose.q[j], w);
    m.hips.position.x += pose.hip.x * w; m.hips.position.z += pose.hip.z * w;
    m.hips.position.y += (pose.hip.y - m.hips.position.y) * w;
    fitW = w;
  } else ctx.clipW = undefined;
  // 410 (3): standing still, out of cover, the library's idle — its legs and hips' height (the weight shifting from foot to
  // foot) for today's, the hips' and the chest's sway (the turn from the loop's mean) over today's turns, so the look, the
  // blade and the aim stand as they were; crouched, the 73.3 kneel kept and the crouched idle's chest sway over it. Aiming,
  // a share of the sway (CLIP_IDLE.aimed: the gun steadies)
  const idleOn = clipsOn && ctx.state === 'idle' && !cov && !digging && (stance === 'stand' || stance === 'crouch');
  if (idleOn) ctx.idleStance = kneel || stance === 'crouch' ? 'crouch' : 'stand';
  ctx.idleW = Math.max(0, Math.min(1, (ctx.idleW ?? 0) + (idleOn ? 1 : -1) * dt / CLIP_EASE));
  if (ctx.idleW > 0 && ctx.idleStance) {
    const w = smooth(ctx.idleW), pose = _idle, sway = w * (ctx.aiming ? CLIP_IDLE.aimed : 1);
    sampleIdleClip(ctx.idleStance, t, pose);
    const mean = (j: number) => _jq2.copy(clipMean(pose.a, j)).invert().multiply(pose.q[j]);
    m.torso.quaternion.multiply(_jq.identity().slerp(mean(J_TORSO), sway * CLIP_IDLE.breath));
    if (ctx.idleStance === 'stand') {
      clipSoles(pose, _idleSoles);
      m.hips.quaternion.multiply(_jq.identity().slerp(mean(J_HIPS), sway));
      const legs = [m.thighL, m.shinL, m.footL, m.thighR, m.shinR, m.footR];
      for (let j = 0; j < legs.length; j++) legs[j].quaternion.slerp(pose.q[j + 2], w);
      m.hips.position.x += pose.hip.x * w; m.hips.position.z += pose.hip.z * w;
      m.hips.position.y += (pose.hip.y - m.hips.position.y) * w;
      if (fitW > 0) { _soles[0] = (_soles[0] * fitW + _idleSoles[0] * w) / (fitW + w); _soles[1] = (_soles[1] * fitW + _idleSoles[1] * w) / (fitW + w); }
      else { _soles[0] = _idleSoles[0]; _soles[1] = _idleSoles[1]; }
      fitW = Math.max(fitW, w);
    }
  } else ctx.idleW = undefined;
  // 410 (2): the clip's feet on the ground under them — each boot's lowest point (heel, middle or toe) kept as high over the
  // ground under it as it stands over the clip's own (planted: on it; swinging, or in a run's flight: as far over it); the hips lowered (or raised) to the leg
  // that needs it most, the other's knee folding to lift its foot the rest (the legs only ever shorten); a planted boot along
  // the slope. Two ground samples a figure a frame while the clips draw it
  if (fitW > 0) {
    const legs = [[m.thighL, m.shinL, m.footL], [m.thighR, m.shinR, m.footR]] as const, need = [0, 0];
    for (let i = 0; i < 2; i++) {
      // a planted boot along the slope first (it turns about the ankle: the heel and toe it is measured by move)
      const planted = fitW * (1 - smooth(Math.min(1, Math.max(0, _soles[i]) / CLIP_IDLE.soles)));
      if (planted > 0 && sloped) legs[i][2].quaternion.multiply(_jq.setFromAxisAngle(_xAxis, -Math.atan(sz) * planted)).multiply(_jq.setFromAxisAngle(_zAxis, Math.atan(sx) * planted));
      const [th, sh, ft] = legs[i], p = soleAt(m.hips.quaternion, m.hips.position, th.position.x, th.quaternion, sh.quaternion, ft.quaternion, _sole, sx, sz);
      // (never under it: on our longer shins a clip's swinging toe would scrape through; within the contact band eased down
      // onto it: on our boots the clip's stance hovers a few centimetres)
      const over = Math.max(0, _soles[i]);
      need[i] = groundUnder(p.x, p.z - slideZ) + over * smooth(Math.min(1, over / CLIP_IDLE.soles)) - p.y;
    }
    const dy = Math.min(need[0], need[1]);
    m.hips.position.y += dy * fitW;
    for (let i = 0; i < 2; i++) {
      const [th, sh, ft] = legs[i], up = (need[i] - dy) * fitW;
      if (up > 1e-4) raiseAnkle(m.hips.quaternion, th.quaternion, sh.quaternion, ft.quaternion, up);
    }
  }
  // 66.8: the entrenching tool — in the hands at the dig (the stroke's keys, in the hips' frame), else folded on the pack
  if (digging || ctx.tool) {
    const tool = kitTool(m);
    if (digging) {
      if (tool.group.parent !== m.hips) m.hips.add(tool.group);
      digKey((t - ctx.digAt!) / DIG_CYCLE.seconds, _dg, _dt);
      tool.group.position.copy(_dg);
      tool.group.quaternion.setFromUnitVectors(_dd.set(0, -1, 0), _dt.sub(_dg).normalize());
      tool.blade.rotation.x = 0; tool.carrier.visible = false;
    } else {
      if (tool.group.parent !== m.torso) m.torso.add(tool.group);
      tool.group.position.copy(TOOL_PACKED.pos); tool.group.rotation.set(0, 0, Math.PI); tool.blade.rotation.x = TOOL_PACKED.fold; tool.carrier.visible = true;
    }
    tool.group.visible = true;
  } else if (m.kit.tool) m.kit.tool.group.visible = false;
  // 67.16: the far tier's pose follows the body — the run's two frames swap with the stride; 66.7 (a): in cover, ducked
  // behind low cover, kneeling or standing to peek, prone at a crest, the whole outline leaning as the body does
  const far: SilhouettePose = cov?.pose === 'low' ? (peek < 0.5 ? 'duck' : hipY > 0.72 ? 'stand' : 'crouch')   // (stand once the body is nearer its height than the kneel's)
    : stance === 'prone' ? 'prone' : stance === 'crouch' ? 'crouch'
    : ctx.state === 'run' || ctx.state === 'airborne' ? ((ctx.gaitPhase ?? 0) % 1 < 0.5 ? 'runA' : 'runB')   // the walk's own stride phase
    : ctx.state === 'seated' ? (ctx.seat === 'saddle' ? 'ride' : ctx.seat === 'hatch' ? 'stand' : 'sit') : 'stand';   // 67.63: seated far off stays seated
  if (far !== m.silhouettePose) { m.silhouettePoses[m.silhouettePose].visible = false; m.silhouettePoses[far].visible = true; m.silhouettePose = far; }
  m.silhouette.position.set(slideX, 0, slideZ); m.silhouette.rotation.z = 0.6 * coverRoll;

  // 65.5: the weapon in hand (the other slung / holstered), the draw between them, and the reload
  const weapon: GroundWeapon = ctx.weapon ?? 'rifle';
  if (weapon === 'rocket' || ctx.lawLoaded || ctx.lawSpare) kitLaw(m);           // (S7: built the first time it is carried)
  if (ctx.lawSpare) kitLawSpare(m);
  if (weapon === 'gl40' || ctx.gl40) kitGl40(m);                                   // (65.31: built the first time it is carried)
  const inHand = m.weapons[weapon]!;
  const reloadFor = ctx.reloadFor ?? (weapon === 'rifle' ? WEAPON_LOOK[m.longGun] : weapon === 'rocket' ? WEAPON_LOOK.rocket : weapon === 'gl40' ? WEAPON_LOOK.gl40 : WEAPON_LOOK.pistol).reload;
  const rk = ctx.reloadAt === undefined ? -1 : (t - ctx.reloadAt) / reloadFor;   // 0…1 across the reload, outside it otherwise
  const reloading = rk >= 0 && rk < 1;
  // S7: the LAW's throwaway — the hands empty from the drop until the next is drawn from the pack
  const lawEmpty = weapon === 'rocket' && ((reloading && rk >= LAW_THROW.drop && rk < LAW_THROW.draw) || (!reloading && ctx.lawLoaded === false));   // (or the last one fired: thrown away)
  // the magazine: out of the well and down toward the pouch, gone, back up from the pouch, seated (the LAW has none: its inner tube is the extension)
  const mag = inHand.magazine;
  // 67.60: a gun with its own reload (RELOAD_LOOK) — the beats either side of now, and the hold between them
  const family = reloading ? reloadFamily(m, weapon) : null, rb = family ? reloadAt(RELOAD_BEATS[family], rk) : null;
  const rHold = (f: 'roll' | 'dip' | 'head') => (rb ? rb.a[f] + (rb.b[f] - rb.a[f]) * rb.k : 0);
  if (weapon === 'rocket') { /* the LAW: see below, after it is placed */ }
  else if (rb) { mag.visible = rb.a.mag !== 'away'; mag.position.copy(inHand.well); }   // (in the hand: placed with the hand, below)
  else if (reloading) {
    const out = smooth((rk - 0.12) / 0.2), back = smooth((rk - 0.62) / 0.25);
    const drop = rk < 0.5 ? out : 1 - back;
    mag.visible = !(rk >= 0.34 && rk < 0.6);                                       // in the pouch, out of sight
    if (inHand.clipFromTop) mag.position.copy(inHand.well).add(_t.set(0.04 * drop, 0.2 * drop, -0.02 * drop));   // 65.43: a clip from above (the Garand's pings up and out, the next pressed down)
    else mag.position.copy(inHand.well).add(_t.set(0, -0.22 * drop, 0.1 * drop));   // out: down and forward, away from the chest
  } else { mag.visible = weapon !== 'gl40'; mag.position.copy(inHand.well); }   // (65.31: the 40 mm's round shows only on its way in)
  const dip = reloading && !rb ? bell(rk) : 0, rDip = rHold('dip');
  const eyeLift = ctx.eye && reloading && weapon !== 'rocket' ? smooth(rk / EYE_RELOAD.ease) * (1 - smooth((rk - 1 + EYE_RELOAD.ease) / EYE_RELOAD.ease)) : 0;   // 407: at the eye, lifted to the face

  // The weapon: up at the aim pitch, or the low-ready carry, or slung while operating; a short recoil kick after a shot
  const crewing = !!ctx.hands || digging;                                         // at a crew post (66.8: or digging): the weapon slung
  const operating = ctx.state === 'operate';
  const recoil = Math.max(0, 1 - (t - ctx.firedAt) / 0.12);
  // 66.10: in cover the gun follows the aim by the peek, or by a shot's hold (COVER_FIRE) — whichever is the more
  const gunUp = cov ? Math.max(peek, smooth(ctx.coverFire ?? 0)) : 0;
  const rest = cov ? (pressed ? (cov.pose === 'low' ? PRESS.gun.lowRest : cov.pose === 'crest' ? PRESS.gun.crestRest : PRESS.gun.edgeRest) : COVER[cov.pose].rest) : -0.55, aimK = cov ? gunUp : ctx.aiming ? 1 : 0;   // 66.7 (a): out of cover is aiming
  const across = cov && (cov.pose !== 'crest' || pressed) ? 1 - gunUp : 0;   // 66.7 (a): down, the gun across the body (pressed at a crest too: never through the lip)
  const pitch = (operating || crewing || bandaging ? -1.3 : cov ? rest + (ctx.aimPitch - rest) * gunUp : ctx.aiming ? ctx.aimPitch : -0.55) + lean + body - 0.35 * dip - rDip + EYE_RELOAD.pitch * eyeLift;   // in torso space: undo the lean and the body's pitch; dipped to reload (67.60: as its beats hold it)
  // the rifle seats in the shoulder: the torso blades (eased over a draw, from the other weapon's stance), the
  // rifle and the face turn back to the heading, the cheek goes to the stock while aiming
  const drawK = ctx.drawAt === undefined ? 1 : smooth((t - ctx.drawAt) / WEAPON_LOOK.draw);
  const bladeTo = (weapon === 'rifle' || weapon === 'rocket' || weapon === 'gl40') && !operating && !crewing ? RIFLE_BLADE : 0, bladeFrom = weapon === 'pistol' ? RIFLE_BLADE : 0;   // (S7: the LAW shouldered as the rifle is)
  // 65.12: a grenade throw — the torso winding back (the right shoulder away) and through (67.60: from the sim's release,
  // THROW_LOOK; the long gun to the support hand, the LAW slung)
  const TL = THROW_LOOK, tau = ctx.throwAt === undefined ? -1 : t - ctx.throwAt, tk = tau < 0 ? -1 : tau / TL.seconds;
  const throwing = tk >= 0 && tk < 1, thWind = 0.84 * TL.hold, thBack = TL.hold + 0.3;
  const throwK = throwing ? smooth(tau / 0.06) * smooth((1 - tk) / 0.25) : 0;
  const throwTwist = (throwing ? (0.7 * smooth(tau / Math.max(1e-3, thWind)) - 1.0 * smooth((tau - thWind) / 0.2)) * (1 - smooth((tau - thBack) / (TL.seconds - thBack))) : 0) * (ctx.eye ? EYE_THROW.twist : 1);   // (73.9 step 3: less at the eye)
  ctx.handsOn = throwing && tau < TL.hold + EYE_THROW.focus ? 'throw' : bandaging ? 'bandage' : digging ? 'dig' : operating ? 'slate' : undefined;
  const throwOneHand = throwing && weapon !== 'rocket';   // (the gun kept, in the support hand)
  // 63.8: the gaits' carry (not the LAW, kept on the shoulder; not while crewing, operating, bandaging, throwing or in cover)
  const carry = !crewing && !operating && !bandaging && !throwing && !cov && weapon !== 'rocket' ? 1 : 0, cm = carry * wm, cs = carry * ws;
  const blade = (ctx.drawAt === undefined ? bladeTo : bladeFrom + (bladeTo - bladeFrom) * drawK) * (1 - bandageK) * (1 - throwK) * (1 - 0.5 * cm - cs);   // square to the wound while bandaging (63.8: and to the run)
  m.torso.rotation.y += blade + throwTwist;
  if (throwing) m.torso.rotation.x += 0.3 * bell((tau - thWind) / (TL.seconds - thWind)) * (ctx.eye ? EYE_THROW.twist : 1);   // over the front foot at the release
  // 67.60: the torso turned back to the look about the vertical through the neck (the rig's up, in the hips' frame), last: the
  // head stays where the lean put it — round the edge, at the height the sim reads (COVER_POSE_SIZE) — and the chest swings
  // under it (about its own leaned and rolled axis the head would swing up and down; about the hips, back off the edge)
  m.torso.position.set(0, 0, 0);
  if (coverTurn !== 0) {
    _cq.setFromAxisAngle(_t.set(0, 1, 0).applyQuaternion(_cq2.copy(m.hips.quaternion).invert()), coverTurn);
    _t2.copy(m.head.position).applyQuaternion(m.torso.quaternion);   // the neck, from the torso's root in the hips' frame
    m.torso.quaternion.premultiply(_cq);
    m.torso.position.copy(_t2).sub(_t3.copy(_t2).applyQuaternion(_cq));
  }
  // 67.60 (the owner on the second pass: "still some hip disconnection from torso at 210 and 240 degrees with tree cover"): with
  // the back to the cover the turn back to the look is the whole body's (`coverRig`), not the torso's off the hips — about the
  // vertical through the neck, so the head stays where the lean put it and the body swings under it; turned off square to the
  // face (near the switch, the look up to ~80° off the back), the whole body stood off it (`back.clear`: the near shoulder)
  m.full.position.set(0, 0, 0);
  if (coverRig !== 0 || coverPush !== 0) {
    _t2.copy(m.head.position).applyQuaternion(m.torso.quaternion).add(m.torso.position).applyQuaternion(m.hips.quaternion).add(m.hips.position).setY(0);   // the neck, in the rig's frame
    _t.set(0, 1, 0);
    m.full.position.copy(_t2).sub(_t3.copy(_t2).applyAxisAngle(_t, coverRig)).applyAxisAngle(_t, coverBase);   // R(base)·(n − R(rig)·n): the neck where it was
    m.full.position.x -= Math.sin(coverAt) * coverPush; m.full.position.z -= Math.cos(coverAt) * coverPush;
    const s = ctx.coverShift ??= { x: 0, z: 0 };
    s.x = m.full.position.x; s.z = m.full.position.z;
  } else ctx.coverShift = undefined;
  const thrownKind: GrenadeKind = ctx.throwKind ?? 'he';
  const inHandNow = throwing && tau < TL.hold;
  if (inHandNow) kitGrenade(m, thrownKind);
  for (const kind of ['he', 'smoke'] as GrenadeKind[]) {
    const g = m.kit.inHand[kind];
    if (!g) continue;
    g.group.visible = inHandNow && kind === thrownKind;
    g.ring.visible = tau < 0.4 * TL.hold;
  }
  m.aim.rotation.order = 'YXZ';   // yaw back to the heading, then pitch about the rifle's own axis
  m.aim.rotation.y = -blade;
  m.aim.rotation.x = -(pitch + 0.12 * recoil);
  m.aim.rotation.z = rHold('roll') + EYE_RELOAD.roll * eyeLift;   // (67.60: rolled to the reload's work)
  // 63.8: a move carries the gun low across the body (a shot brings it round to the front at the hip), a sprint at port arms
  // across the chest rocking with the stride; the pistol low in both. Blended from the walk's by the gaits' weights.
  const hipFire = cm > 0 ? smooth(1 - (t - ctx.firedAt - GL.move.hipHold) / GL.move.hipFade) : 0, long = weapon !== 'pistol';
  if (cm + cs > 0) {
    const M = GL.move, S = GL.sprint, free = ctx.aiming ? ctx.aimPitch : -0.55;
    const moveP = long ? M.pitch + (0.5 * ctx.aimPitch + M.hipPitch - M.pitch) * hipFire : M.pistol.pitch;
    m.aim.rotation.x -= cm * (moveP - free) + (long ? 0 : cs * (S.pistol.pitch - free));
    if (long) m.aim.rotation.y -= cm * M.yaw * (1 - hipFire);
    if (long && cs > 0) {
      // port arms: the barrel up across the chest to the support shoulder (−x), the gun's top to the front, rocking with the
      // stride — set in the heading's frame (the hips' and the chest's turns and the lean taken out, as in cover)
      _gz.set(-S.port.across, S.port.rise, S.port.out).normalize(); _gy.set(0, 0, 1); _gx.crossVectors(_gy, _gz).normalize(); _gy.crossVectors(_gz, _gx);
      _gq.setFromRotationMatrix(_gm.makeBasis(_gx, _gy, _gz)).premultiply(_cq.setFromAxisAngle(_gy.set(0, 1, 0), S.swing * Math.sin(2 * Math.PI * (ctx.gaitPhase ?? 0))));
      m.aim.quaternion.slerp(_cq2.multiplyQuaternions(m.hips.quaternion, m.torso.quaternion).invert().multiply(_gq), cs);
    }
  }
  // 67.60: the rifle swinging with the stride — turning with the shoulders, nodding and bobbing with each step; less up at the aim
  // (410 (5): under the clip layer the clip's chest swings the gun instead, below)
  const strideGun = STRIDE_LOOK.on && ctx.state === 'run' && stance === 'stand' && !cov ? Math.min(1, (ctx.gaitSpeed ?? ctx.speed) / 4) * (ctx.aiming ? STRIDE_LOOK.aimed : 1) * (1 - cs) * (1 - smooth(ctx.clipW ?? 0)) : 0;
  const stridePh = 2 * Math.PI * (ctx.gaitPhase ?? 0);
  if (strideGun > 0) { m.aim.rotation.y += STRIDE_LOOK.gunYaw * strideGun * Math.sin(stridePh); m.aim.rotation.x += STRIDE_LOOK.gunNod * strideGun * Math.cos(2 * stridePh); }
  let coverLift = 0;   // (67.60: the low ready held higher, coverHeld)
  if (cov) {
    // 66.7 (a): in cover the gun's turn is set in the heading's frame — across the body while down, round to the aim as the
    // peek comes out, upright in a lean — with the hips' and the chest's own rotations taken out (a hunched chest would tip it)
    // 67.60's second pass (the owner: "that low position is out of sync sometimes and looks like gun is held behind back"): down,
    // across the chest — the chest's own turn (toward the edge, bladed, back to the look) and `chest` across it, so it stays in
    // front of the chest whatever the side, the look or the reload; rising, from there round onto the look (set in the rig's frame)
    const away = pressed ? (cov.pose === 'low' || cov.pose === 'crest' ? -1 : -Math.sign((cov.side ?? 1) * coverFlip * (m.hand === 'right' ? 1 : -1)) || -1) : 1;   // (pressed: the muzzle away from the edge)
    const given = pressed ? away * PRESS.gun.yaw : -COVER.across.yaw;
    const held = cov.pose === 'edge' || cov.pose === 'corner' ? coverHeld(given, coverYaw + blade + throwTwist + coverTurn) : { yaw: given, drop: 0, lift: 0 };   // (over low cover and at a crest the chest faces the cover: the hold as given)
    coverLift = held.lift;
    _ce.set(-(pitch - lean - body + 0.12 * recoil) + held.drop * across, held.yaw * across - (coverBase + coverRig) * gunUp, rHold('roll'), 'YXZ');   // (67.60: up, the look's)
    m.aim.quaternion.copy(_cq2.multiplyQuaternions(m.hips.quaternion, m.torso.quaternion).invert().multiply(_cq.setFromEuler(_ce)));
  }
  const hold = HOLD_AT[weapon];
  // prone, the weapon is pulled in and lifted (held forward it would dig into the ground on the crawl)
  const proneIn = stance === 'prone' ? 1 : 0;
  // recoil drives the butt back into the shoulder, along the rifle
  const acrossUp = pressed ? PRESS.gun.up : COVER.across.up, acrossOut = pressed ? PRESS.gun.out : COVER.across.out;
  m.aim.position.set(hold.x + 0.05 * recoil * Math.sin(blade), hold.y - 0.1 * dip + 0.06 * proneIn + (acrossUp + coverLift) * across, hold.z + 0.12 * dip - 0.05 * recoil * Math.cos(blade) - 0.12 * proneIn + acrossOut * across);   // reloading: down and a little forward
  if (eyeLift > 0) m.aim.position.add(_t.set(0, EYE_RELOAD.up * eyeLift, -EYE_RELOAD.back * eyeLift));   // 407: up to the face, a little back
  if (ctx.eye && weapon === 'rocket') m.aim.position.add(EYE_LAW.offset);   // 73.9 step 3: the LAW aside, off the eye
  if (cm + cs > 0) {
    const M = GL.move, S = GL.sprint;
    if (long) m.aim.position.add(_t.set(M.side * cm + S.side * cs, (M.up + M.hipUp * hipFire) * cm + S.up * cs, (M.fwd + M.hipFwd * hipFire) * cm + S.fwd * cs));
    else m.aim.position.y += M.pistol.up * cm + S.pistol.up * cs;
  }
  ctx.crawlLift = toward(ctx.crawlLift ?? 0, crawling ? 1 : 0, dt / 0.25);
  if (ctx.crawlLift > 0) { m.aim.position.y += crawlReach; m.aim.rotation.x -= CRAWL_LOOK.muzzle * smooth(ctx.crawlLift); }   // (67.60: the crawl's elbows reaching in turn — along the spine, ahead on the ground — the muzzle carried clear of it)
  if (rb) { const a = rb.a.lift, b = rb.b.lift, k = rb.k; m.aim.position.add(_t.set(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k)); }   // (67.60: in to the work)
  if (strideGun > 0) m.aim.position.y += STRIDE_LOOK.gunBob * strideGun * Math.cos(2 * stridePh + 0.6);   // (lagging the step a little)
  m.aim.position.y -= JUMP_LOOK.land.gun * landGive;   // (67.60: the arms giving with the landing)
  if (ctx.clipW !== undefined && !cov) {
    // 410 (5): the gun with the clip layer's stride — it rides the clip's chest (its bob, its turn), less a share (CLIP_GUN)
    // of the chest's turn from the loop's mean and of the hips' bob from their eased height: the arms holding it steadier
    // than the body, most of all at the aim (the sprint's port arms are held in the heading's frame already)
    const G = CLIP_GUN, k = smooth(ctx.clipW) * (1 - cs) * (ctx.aiming ? G.aimed : cm > 0 ? G.move : G.walk), pose = _clip;
    const mean = _jq2.copy(clipMean(pose.a, J_HIPS)).multiply(clipMean(pose.a, J_TORSO));
    if (pose.b) mean.slerp(_jq.copy(clipMean(pose.b, J_HIPS)).multiply(clipMean(pose.b, J_TORSO)), pose.k);
    const off = mean.invert().multiply(_jq.copy(pose.q[J_HIPS]).multiply(pose.q[J_TORSO])).invert();   // (the chest's turn back to its mean)
    m.aim.quaternion.premultiply(_jq.identity().slerp(off, k));
    ctx.hipEased = ctx.hipEased === undefined ? m.hips.position.y : ctx.hipEased + (m.hips.position.y - ctx.hipEased) * Math.min(1, dt / G.bobEase);
    m.aim.position.y -= k * (m.hips.position.y - ctx.hipEased);
  } else ctx.hipEased = undefined;
  if (throwOneHand) {
    // 67.60: the gun to the support hand for the throw — low, the muzzle ahead and down — and back to the shoulder after
    m.aim.position.lerp(_t.copy(m.aim.position).add(THROW_GUN.at), throwK);
    m.aim.rotation.x += THROW_GUN.pitch * throwK; m.aim.rotation.y += THROW_GUN.yaw * throwK;
  }
  const throwStow = throwing && !throwOneHand;
  const throwEnd = ctx.throwAt === undefined || !throwStow ? undefined : ctx.throwAt + TL.seconds;
  const easeFrom = throwStow ? ctx.throwAt : throwEnd !== undefined && t >= throwEnd && t - throwEnd < WEAPON_LOOK.draw ? throwEnd : ctx.drawAt;
  placeWeapons(m, weapon, easeFrom, t, crewing || throwStow);
  // S7: the LAW — extended as it comes up to the shoulder (or after the throwaway's draw), collapsed on the pack; gone from the
  // hands while the spent one is dropped and the next fetched; the spare on the pack until it is drawn
  if (m.weapons.rocket) {
    const law = m.weapons.rocket, held = weapon === 'rocket' && !crewing && !throwing;
    const k = !held ? 0 : reloading ? (rb ? smooth((rk - RELOAD_LOOK.extend[0]) / (RELOAD_LOOK.extend[1] - RELOAD_LOOK.extend[0])) : smooth((rk - LAW_THROW.draw) / (1 - LAW_THROW.draw))) : ctx.drawAt === undefined ? 1 : smooth((t - ctx.drawAt) / WEAPON_LOOK.draw);
    setLawExtended(law, k);
    law.group.visible = !lawEmpty && (held || ctx.lawLoaded !== false);   // (on the pack only while there is a loaded one)
  }
  if (m.kit.lawSpare) m.kit.lawSpare.visible = !!ctx.lawSpare && !(weapon === 'rocket' && reloading && rk >= LAW_THROW.draw);
  // 65.31: the 40 mm — broken open across its reload (the case out, a round in, closed at the end), the leaf up while aimed; slung, closed and folded
  if (m.weapons.gl40) {
    const w = m.weapons.gl40, held = weapon === 'gl40' && !crewing && !throwing;
    setGl40Open(w, held && reloading ? smooth(rk / 0.12) * (1 - smooth((rk - 0.88) / 0.1)) : 0);
    setGl40Leaf(w, held && ctx.aiming && !reloading ? 1 : 0);
    w.group.visible = held || !!ctx.gl40;
  }
  // 65.12: the LMG stands on its bipod when its carrier lies prone with it in hand
  if (m.weapons.rifle.bipod) setBipod(m.weapons.rifle, stance === 'prone' && weapon === 'rifle' && !crewing && !throwing ? 1 : 0);   // hands on the bars or a gun's anchors: both weapons stowed   // after the pivot is placed, so a draw carries the pose from where the hand now is
  const astride = ctx.state === 'seated' && ctx.seat === 'saddle';
  m.head.rotation.x = (astride ? -SADDLE.lean * 0.9 : operating || bandaging ? 0.45 : cov ? 0.1 + (-0.6 * ctx.aimPitch - 0.1) * peek : ctx.aiming ? -0.6 * ctx.aimPitch : crewing ? 0.3 : 0.1) + 0.35 * flinch - 0.85 * body + 0.4 * dip + rHold('head') + headDown;   // prone: the head comes up to look ahead; reloading: a glance down
  m.head.rotation.x -= lookLevel;   // (66.7 (a), pressed: leaning over low cover, the eyes kept level)
  if (wm + ws > 0) {
    // 63.8: the head up on a move, up against the lean at a sprint — the eyes on the way ahead
    const to = (wm * (GL.move.head - 0.9 * lean) + ws * (GL.sprint.head - 0.9 * lean)) / (wm + ws);
    m.head.rotation.x += (to - m.head.rotation.x) * (wm + ws);
  }
  m.head.rotation.y = -blade - throwTwist - 0.85 * coverYaw;   // (67.60: with the chest — a glance toward the look would move the eyes the sim reads, and the press)   // the eyes stay on the heading (the throw's target; 66.7 (a): back from the body's turn)
  m.head.rotation.z = -CHEEK * (blade / RIFLE_BLADE) * (reloading ? 0 : aimK) - 0.6 * coverRoll;   // 66.7 (a): the eyes kept nearer level in a lean
  // 66.7 (a), pressed: what the press needs once the arms are placed (updatePersonAnimation slides the body after the pose)
  _press.on = pressed && !!cov;
  if (_press.on) {
    const own = (cov!.side ?? 1) * (m.hand === 'right' ? 1 : -1);
    _press.below = cov!.pose === 'low' ? (cov!.height ?? 1) - PRESS.rest : Infinity; _press.headOnly = cov!.pose === 'crest';   // (what rests on the top is on it)
    _press.edge = cov!.pose === 'edge' || cov!.pose === 'corner' ? own * coverFlip * PRESS.edgeAt : 0;
    // the face: the walk's contact at the ground (never nearer than the gap), the gap at the chest the host measured it at
    // (67.60: the base turned off the cover — the gun up past COVER_TURN.torso — meets its face further off, and lets go of it)
    const gap = cov!.gap! / Math.max(coverOff, 0.25);
    _press.k = clamp01((coverOff - COVER_TURN.pressOff) / (COVER_TURN.pressOn - COVER_TURN.pressOff)) * (coverFlip > 0 ? 1 : 0);   // (no slide forward with the back to it: coverBackZ)
    _press.square = coverOff;
    _press.face.foot = Math.min(gap, PRESS.foot); _press.face.gap = gap; _press.face.chest = Math.max(0.1, COVER_POSE_SIZE[cov!.pose].down.eye - 0.3);
    _press.over = cov!.pose === 'crest' ? PRESS.crest.over * peek : 0;
    _press.base = hipY + bob; _press.sz = sz; _press.slideZ = slideZ; _press.overTop = cov!.pose === 'low' ? peek : 0;
    _press.leanIn = cov!.pose === 'edge' || cov!.pose === 'corner';
  }
  m.controller.visible = operating;

  // Both hands on the weapon at every pitch and in every state (on the controller while operating,
  // on the anchors while crewing)
  // 67.60: a stance's step under way — the joints eased from how they were drawn when it began (STANCE_LOOK), the support
  // hand to the ground ahead going down (and pushing off it coming up)
  _plant.w = 0;
  if (ctx.stanceStep && ctx.stanceSnap) {
    const st = ctx.stanceStep, k = (t - st.at) / st.dur;
    blendStance(m, ctx.stanceSnap, smooth(k)); ctx.stanceBlended = true;
    const down = st.from === 'crouch' && st.to === 'prone', up = st.from === 'prone' && st.to === 'crouch';
    if (down || up) {
      const [a, b] = down ? STANCE_LOOK.plant.down : STANCE_LOOK.plant.up;
      _plant.w = bell((k - a) / (b - a));
      const P = STANCE_LOOK.plant.at;
      m.hips.updateMatrix(); m.torso.updateMatrix();
      _plant.at.set(P[0], P[1], P[2]).applyMatrix4(_rm.multiplyMatrices(m.hips.matrix, m.torso.matrix).invert());   // (the body's own frame, into the torso's)
    }
  }
  m.torso.updateMatrix();
  if (digging && m.kit.tool) {
    // 66.8: both hands on the entrenching tool — the strong on the D-grip, the support down the shaft (the tool is in the
    // model's own frame, so no side swap)
    m.torso.updateWorldMatrix(true, false);
    solveArm(m.armR, m.torso.worldToLocal(m.kit.tool.gripR.getWorldPosition(_t)), POLE_R);
    solveArm(m.armL, m.torso.worldToLocal(m.kit.tool.gripL.getWorldPosition(_t2)), POLE_L);
    return;
  }
  if (crewing) {
    m.torso.updateWorldMatrix(true, false);
    const reach = (anchor: THREE.Object3D | null | undefined, rest: THREE.Vector3) =>
      anchor ? m.torso.worldToLocal(anchor.getWorldPosition(_t)) : _t.copy(rest);
    // 67.21: the anchors are named on the model's sides; mirrored, the strong arm takes the other one (no reach across the body)
    const strong = m.hand === 'right' ? ctx.hands!.left : ctx.hands!.right, support = m.hand === 'right' ? ctx.hands!.right : ctx.hands!.left;
    const sat = ctx.state === 'seated' && (ctx.seat === undefined || ctx.seat === 'chair' || ctx.seat === 'bench' || ctx.seat === 'drive' || ctx.seat === 'pilot');   // 67.63: a free hand on the thigh
    solveArm(m.armR, reach(strong, sat ? SEAT_REST_R : REST_R), POLE_R);
    solveArm(m.armL, reach(support, sat ? SEAT_REST_L : REST_L), POLE_L);
    return;
  }
  // 65.12: the roll in the strong hand while the bandage runs, unwinding; the dressing growing over the support forearm, then staying
  if (bandaging) { const r = kitRoll(m); r.visible = true; setRollLeft(r, 1 - 0.55 * hk); } else if (m.kit.roll) m.kit.roll.visible = false;
  const dressK = bandaging ? Math.max(ctx.dressed ? 1 : 0, smooth((hk - 0.15) / 0.65)) : ctx.dressed ? 1 : 0;
  if (dressK > 0.02) { const d = kitDressing(m); d.visible = true; d.scale.set(1, dressK, 1); } else if (m.kit.dressing) m.kit.dressing.visible = false;
  if (throwing) {
    // the strong hand: from the grip to the chest (the grenade), wound back behind the shoulder, the release over it, the
    // follow-through down across, back to the grip; the support hand keeps the gun (67.60), or points ahead (the LAW slung)
    const H = ctx.eye ? EYE_THROW : THROW_HAND, grip = inTorso(m, inHand.gripR, _t3);   // (73.9 step 3: re-posed at the eye)
    const keys: Array<[number, THREE.Vector3]> = [[0, grip], [0.32 * TL.hold, H.chest], [thWind, H.back], [TL.hold, H.release], [TL.hold + 0.25, H.through], [TL.seconds, grip]];
    let i = 0; while (i < keys.length - 2 && tau >= keys[i + 1][0]) i++;
    const [a0, a] = keys[i], [b0, b] = keys[i + 1];
    solveArm(m.armR, _t.copy(a).lerp(b, smooth((tau - a0) / Math.max(1e-3, b0 - a0))), POLE_R);
    if (throwOneHand) { if (weapon !== 'pistol') slideSupportHand(m, inHand); solveArm(m.armL, inTorso(m, inHand.gripL, _t2), POLE_L); }
    else {
      const beats = THROW_BEATS;
      let j = 0; while (j < beats.length - 2 && tk >= beats[j + 1].at) j++;
      solveArm(m.armL, _t2.copy(beats[j].l).lerp(beats[j + 1].l, smooth((tk - beats[j].at) / (beats[j + 1].at - beats[j].at))), POLE_L);
    }
    return;
  }
  if (bandaging) {
    // the left forearm held across the body, the right hand wrapping it round and round
    const wrap = t * BANDAGE_LOOK.wrapHz * Math.PI * 2;
    solveArm(m.armL, _t.copy(WOUND_L), POLE_L);
    solveArm(m.armR, _t2.copy(WOUND_L).add(_t3.set(0.05 + 0.05 * Math.cos(wrap), 0.04 + 0.05 * Math.sin(wrap), -0.04)), POLE_R);
    return;
  }
  if (rb) {
    // 67.60: the reload's beats — each hand eased from its spot to the next's, the magazine carried by the support palm
    const spot = (h: 'l' | 'r', out: THREE.Vector3) => reloadSpot(m, inHand, rb.a[h], h, rk, out).lerp(reloadSpot(m, inHand, rb.b[h], h, rk, _rb), rb.k);
    const working = rb.a.l !== 'grip' || rb.b.l !== 'grip';
    solveArm(m.armR, spot('r', _t), POLE_R);
    solveArm(m.armL, spot('l', _t2), working ? POLE_RELOAD : POLE_L);
    if (rb.a.mag === 'hand' && weapon !== 'rocket') {
      // the magazine where the palm is, held where it was gripped
      const grab = RELOAD_GRAB[family!];
      palmAt(m.armL, _t2, _ra).applyMatrix4(torsoFrame(m, mag.parent ?? inHand.group, _rm).invert());
      mag.position.set(_ra.x - grab[0], _ra.y - grab[1], _ra.z - grab[2]);
    }
    return;
  }
  if (lawEmpty) {
    // S7: the throwaway — the right hand free at the side, the left reaching back to the pack for the next tube
    solveArm(m.armR, _t.copy(REST_R), POLE_R);
    const reach = smooth((rk - LAW_THROW.drop) / 0.15) * (1 - smooth((rk - (LAW_THROW.draw - 0.1)) / 0.1));
    solveArm(m.armL, _t2.copy(REST_L).lerp(_t3.copy(LAW_PACK_REACH), reach), POLE_RELOAD);
    return;
  }
  solveArm(m.armR, inTorso(m, operating ? m.ctrlR : inHand.gripR, _t), coverLift > 0 ? POLE_R_LOW : POLE_R);   // (67.60: at the low ready the elbows tucked down, never out past the cover's edge)
  if (operating) { solveArm(m.armL, inTorso(m, m.ctrlL, _t), POLE_L); return; }
  if (reloading && weapon !== 'rocket') {
    // the left hand: to the magazine, down to the pouch with it, back up with the fresh one, then to its grip
    const toMag = smooth(rk / 0.12), toPouch = smooth((rk - 0.34) / 0.14), toWell = smooth((rk - 0.6) / 0.27), toGrip = smooth((rk - 0.9) / 0.1);
    const magT = inTorso(m, mag, new THREE.Vector3());
    const gripT = inTorso(m, inHand.gripL, new THREE.Vector3());
    const p = gripT.clone().lerp(magT, toMag).lerp(POUCH, toPouch).lerp(magT, toWell).lerp(gripT, toGrip);
    solveArm(m.armL, p, POLE_RELOAD);
    return;
  }
  if (weapon === 'rifle' || weapon === 'gl40') slideSupportHand(m, inHand, ctx.eye);
  inTorso(m, inHand.gripL, _t);
  if (_plant.w > 0) _t.lerp(_plant.at, _plant.w);   // (67.60: the hand to the ground through a stance's step)
  solveArm(m.armL, _t, _plant.w > 0.3 ? POLE_PLANT : coverLift > 0 ? POLE_L_LOW : ctx.eye ? EYE_SUPPORT.pole : POLE_L);
}

/**
 * The support hand holds the handguard where it reaches: when the foregrip is further than
 * SUPPORT_REACH from the left shoulder (a low aim, mostly), the grip slides back along the rifle —
 * as far as the magazine well — so the arm never locks straight.
 */
function slideSupportHand(m: PersonModel, w: WeaponModel, eye = false): void {
  const at = w.gripAt ?? RIFLE_GRIP_L, min = (w.gripMin ?? RIFLE_GRIP_MIN) - (eye ? EYE_SUPPORT.slide : 0), reach = eye ? EYE_SUPPORT.reach : SUPPORT_REACH;   // (409: at the eye, a bent arm)
  w.gripL.position.z = at;
  const g = inTorso(m, w.gripL, _t2), s = m.armL.group.position;
  const wv = _t3.copy(g).sub(s);                                  // shoulder → grip
  if (wv.lengthSq() <= reach * reach) return;
  const dir = inTorso(m, w.muzzle, _t).sub(inTorso(m, w.group, _t2)).normalize();   // along the rifle, in torso space
  const along = wv.dot(dir), disc = along * along - (wv.lengthSq() - reach * reach);
  const back = disc >= 0 ? along - Math.sqrt(disc) : along;      // the nearest point along the rifle within reach (or the closest)
  w.gripL.position.z = at - Math.max(0, Math.min(at - min, back));
}
